import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { formatDT, getFileTimestamp, getDateFormat } from '../../utils/timezone';
import { DateRangePresetFilter } from '../../components/shared/DateRangePresetFilter';
import { MobileTabPicker } from '../../components/shared/MobileTabPicker';
import { formatUserDateTime, formatUserDateTimeWithUTC, formatUTCStamp } from '../../utils/dateFormat';
import { AUDIT_ACTION_LABELS } from '../../utils/auditActions';
import { newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, drawPdfTable, exportTableHtml } from '../../utils/tripExport';

const usageBadgeClass = (count, max) => {
  if (max === -1) return 'text-muted-foreground border-border';
  if (max === 0) return count > 0
    ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800'
    : 'text-muted-foreground border-border';
  if (count >= max) return 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
  if (count >= max * 0.8) return 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800';
  return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
};
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { UserDeactivationWizard, UserReactivationWizard } from '../../components/wizards/DeactivationWizards';
import { useEmailAvailability, EmailAvailabilityHint } from '../../components/shared/EmailAvailability';
import { DeviceDeactivationWizard, DeviceReactivationWizard } from '../../components/wizards/DeactivationWizards';
import UserImportWizard, { downloadUserImportTemplate, downloadUserImportTemplateXlsx, readUserImportFile } from '../../components/wizards/UserImportWizard';
import * as XLSX from 'xlsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/ui/number-input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { CheckboxOption } from '../../components/shared/CheckboxOption';
import { PaginationFooter, usePersistedPageSize } from '../../components/shared/PaginationFooter';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';
import { useUrlPage, useUrlPageSize, useSkipFirstEffect, useDebouncedValue, useSessionSearch } from '../../components/shared/useUrlPagination';
import { BannerBroadcastDialog } from '../../components/admin/BannerBroadcastDialog';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Plus,
  Search,
  Sparkles,
  FileText,
  FileSpreadsheet,
  Pencil,
  Trash2,
  Users,
  Shield,
  Loader2,
  RefreshCw,
  Mail,
  Phone,
  Building,
  Download,
  Upload,
  User,
  Lock,
  Unlock,
  HardDrive,
  History,
  Undo2,
  ChevronRight,
  ChevronDown,
  GripVertical,
  X,
  Megaphone,
  CheckCircle,
  XCircle,
  Key,
  Eye,
  EyeOff,
  Wifi,
  WifiOff,
  Clock,
  AlertTriangle,
  UserX,
  UserCheck,
  ExternalLink,
  LogIn,
  LogOut,
  Link2,
  Share2,
  Copy,
  Trash,
  Timer,
  CalendarIcon,
  Radio,
  Play,
  Activity,
  Circle,
  Video,
  Power,
  PowerOff,
  MapPin,
  Settings as SettingsIcon,
} from 'lucide-react';
import { PasswordRequirements } from '../../components/PasswordRequirements';
import { isPasswordValid, PASSWORD_POLICY_TEXT } from '../../lib/passwordPolicy';
import { COUNTRIES } from '../../lib/countries';
import { SortableTableHead } from '../../components/SortableTableHead';
import { SyncedTableScroll } from '../../components/admin/SyncedTableScroll';
import { ShareQuickLoginModal } from '../../components/admin/ShareQuickLoginModal';
import { getAllowedLandingPageOptions, getFallbackLandingPageKey } from '../../config/landingPages';

const userRoles = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-green-600 text-white' },
  { value: 'admin', label: 'Administrator', color: 'bg-blue-600 text-white' },
  { value: 'manager', label: 'Manager', color: 'bg-blue-600/50 text-white' },
  { value: 'end_user', label: 'End User', color: 'bg-slate-500 text-white' },
  { value: 'sub_account', label: 'Sub Account', color: 'bg-slate-400 text-white' },
];

// Tree view component for privileges
// Call tier implication graph: checking a tier auto-checks everything it implies;
// unchecking a tier auto-unchecks everything that implies it (video implies voice).
const CALL_TIER_IMPLIES = {
  chat_video_calls: ['chat_voice_calls'],
  chat_group_voice_4: ['chat_voice_calls'],
  chat_group_voice_6: ['chat_group_voice_4'],
  chat_group_voice_8: ['chat_group_voice_6'],
  chat_group_video_4: ['chat_video_calls', 'chat_group_voice_4'],
  chat_group_video_6: ['chat_group_video_4', 'chat_group_voice_6'],
  chat_group_video_8: ['chat_group_video_6', 'chat_group_voice_8'],
};

const getImpliedCallPrivileges = (id) => {
  const out = new Set();
  const stack = [...(CALL_TIER_IMPLIES[id] || [])];
  while (stack.length) {
    const k = stack.pop();
    if (!out.has(k)) {
      out.add(k);
      stack.push(...(CALL_TIER_IMPLIES[k] || []));
    }
  }
  return out;
};

const getDependentCallPrivileges = (id) =>
  new Set(Object.keys(CALL_TIER_IMPLIES).filter(k => getImpliedCallPrivileges(k).has(id)));

const PrivilegeTreeNode = ({ node, selected, onToggle, level = 0 }) => {
  const [expanded, setExpanded] = useState(false); // Changed to false - collapsed by default
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selected.includes(node.id);
  const isPartiallySelected = hasChildren && node.children.some(child => selected.includes(child.id));

  return (
    <div className={`${level > 0 ? 'ml-6' : ''}`} data-testid={`user-privilege-tree-node-${node.id}`}>
      <div className="flex items-center gap-2 py-2 hover:bg-muted/50 px-2 rounded">
        {hasChildren && (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5" data-testid={`user-privilege-tree-expand-${node.id}`}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
        {!hasChildren && <div className="w-5" />}
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(node.id)}
          className={isPartiallySelected && !isSelected ? 'opacity-50' : ''}
          data-testid={`user-privilege-tree-checkbox-${node.id}`}
        />
        <label className="text-sm cursor-pointer flex-1" onClick={() => onToggle(node.id)} data-testid={`user-privilege-tree-label-${node.id}`}>
          {node.label}
        </label>
        {node.badge && <Badge variant="outline" className="text-xs">{node.badge}</Badge>}
      </div>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <PrivilegeTreeNode
              key={child.id}
              node={child}
              selected={selected}
              onToggle={onToggle}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Drag and drop device assignment component
const DeviceAssignment = ({ availableDevices, assignedDevices, onAssign, onUnassign }) => {
  const [draggedDevice, setDraggedDevice] = useState(null);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Available Devices */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Available Devices</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {availableDevices.map((device) => (
                <div
                  key={device.id}
                  draggable
                  onDragStart={() => setDraggedDevice(device)}
                  onDragEnd={() => setDraggedDevice(null)}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-white dark:bg-slate-900 cursor-move hover:border-[#2563eb] transition-colors"
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <HardDrive className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{device.name}</p>
                    <p className="text-xs text-muted-foreground">{device.imei}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onAssign(device)}
                  >
                    →
                  </Button>
                </div>
              ))}
              {availableDevices.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">No available devices</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Assigned Devices */}
      <Card
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => {
          if (draggedDevice) {
            onAssign(draggedDevice);
            setDraggedDevice(null);
          }
        }}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            Assigned Devices
            <Badge variant="outline">{assignedDevices.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {assignedDevices.map((device) => (
                <div
                  key={device.id}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200"
                >
                  <HardDrive className="w-4 h-4 text-green-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{device.name}</p>
                    <p className="text-xs text-muted-foreground">{device.imei}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onUnassign(device)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {assignedDevices.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <p className="text-sm text-muted-foreground">Drag devices here to assign</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper function to parse expiry date - ensures UTC timezone is applied
// Backend stores dates without 'Z' suffix, so we need to add it for correct parsing
const parseExpiryDate = (dateString) => {
  if (!dateString) return null;
  // If the date doesn't have a timezone indicator, treat it as UTC
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    return new Date(dateString + 'Z');
  }
  return new Date(dateString);
};

// Helper function to check if an account is expired (frontend-side prediction)
const isAccountExpired = (user) => {
  if (!user?.expiry_enabled || !user?.expiry_date) return false;
  const expiryDate = parseExpiryDate(user.expiry_date);
  return expiryDate && expiryDate < new Date();
};

// Helper function to extract error message from API responses
const extractErrorMessage = (error, defaultMessage = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  
  if (typeof detail === 'string') {
    return detail;
  }
  
  if (Array.isArray(detail) && detail.length > 0) {
    // Extract message from first validation error (Pydantic format)
    return detail[0]?.msg || detail[0]?.message || defaultMessage;
  }
  
  return defaultMessage;
};

export const UserManagement = () => {
  const { api, user: currentUser } = useAuth();
  const { refetch: refetchFeatureFlags, isNotificationChannelActive, isSubpageEnabled } = useFeatureFlags();
  // Import/Export Log visibility — Super Admins & Admins with 'View / Manage All Devices'
  const canViewIoLog = isSubpageEnabled('admin_device_management_view_manage_all_devices');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  const [lastImportRun, setLastImportRun] = useState(null);
  const [undoingImport, setUndoingImport] = useState(false);
  // User Import/Export Log (mirrors Device Management's log)
  const [userIoLogOpen, setUserIoLogOpen] = useState(false);
  const [userIoLogs, setUserIoLogs] = useState([]);
  const [userIoLogsLoading, setUserIoLogsLoading] = useState(false);
  const [userIoLogTypeFilter, setUserIoLogTypeFilter] = useState('all');
  const [userIoLogPersonFilter, setUserIoLogPersonFilter] = useState('all');
  const [subAccountsViewer, setSubAccountsViewer] = useState(null); // parent user for read-only sub list
  const [userIoLogDateRange, setUserIoLogDateRange] = useState({ from: undefined, to: undefined });
  const [userIoLogDateResetKey, setUserIoLogDateResetKey] = useState(0);
  const [userIoLogPage, setUserIoLogPage] = useState(1);
  const [userIoLogPageSize, setUserIoLogPageSize] = usePersistedPageSize('user-io-log', 25);
  const [userIoLogTotal, setUserIoLogTotal] = useState(0);
  const [userIoLogGrandTotal, setUserIoLogGrandTotal] = useState(0);
  const [userIoLogPersons, setUserIoLogPersons] = useState([]);
  const [quickImporting, setQuickImporting] = useState(false);
  const [staleInvites, setStaleInvites] = useState([]);
  const [staleInviteDays, setStaleInviteDays] = useState(3);
  const [staleInvitesDismissed, setStaleInvitesDismissed] = useState(false);
  const [resendingInvites, setResendingInvites] = useState(false);
  // Sticks to `true` after the first successful fetch. Prevents the initial
  // full-page spinner from re-appearing on subsequent search refetches — which
  // would unmount the search input and lose focus, especially when the current
  // query filters the users list down to 0 rows.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [searchQuery, setSearchQuery] = useSessionSearch('user-mgmt');
  const [selectAllAcrossLoading, setSelectAllAcrossLoading] = useState(false);
  // Server-side pagination — future-proof for tenants with 3 000+ users. Filters (search /
  // role / invite-pending) are still applied client-side WITHIN the current page for now;
  // page resets to 0 whenever the page size changes.
  const [pageIndex, setPageIndex] = useUrlPage('page', { zeroIndexed: true });
  const [pageSize, setPageSize] = useUrlPageSize('user-mgmt', 25);
  const [totalCount, setTotalCount] = useState(0);
  // Cross-page counters — computed via a lightweight (limit=1) fetch that reads
  // X-Total-Count so the pending-invite badge and role summary don't disappear
  // when the target row is on another page.
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [totalRoleCounts, setTotalRoleCounts] = useState({});
  const { dateFormat: userDateFmt, timeFormat: userTimeFmt, timezone: userTimezone } = useGeneralSettings();

  const [roleFilter, setRoleFilter] = useState('all');  // Role filter state
  const [statusFilter, setStatusFilter] = useState('all'); // Active / Blocked filter
  const [invitePendingOnly, setInvitePendingOnly] = useState(false);  // Filter to pending/expired invites
  const [userSortField, setUserSortField] = useState('full_name');
  const [userSortDir, setUserSortDir] = useState('asc');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeEditTab, setActiveEditTab] = useState('account');
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);  // For bulk export
  const isMobileOrWebview = useIsMobileOrWebview();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteText, setBulkDeleteText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [quickLoginLoading, setQuickLoginLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  // Resend-invitation delivery channel selection (Edit dialog → Account Activation)
  const [resendInviteEmail, setResendInviteEmail] = useState(true);
  const [resendInviteSms, setResendInviteSms] = useState(false);
  // "Send Login Details" (activated accounts) delivery channels
  const [loginDetailsEmail, setLoginDetailsEmail] = useState(true);
  const [loginDetailsSms, setLoginDetailsSms] = useState(false);
  const [sendingLoginDetails, setSendingLoginDetails] = useState(false);
  // Broadcast banner dialog
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [quickLoginToken, setQuickLoginToken] = useState(null);  // Current quick login token
  const [shareQuickLoginOpen, setShareQuickLoginOpen] = useState(false); // Share quick login modal
  const [pendingUserAction, setPendingUserAction] = useState(null); // { title, message, action, requireTyped }
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [pendingInputChange, setPendingInputChange] = useState(null); // { title, message, field, value }
  const [savedUserSnapshot, setSavedUserSnapshot] = useState(null);
  const [showUserUnsavedWarning, setShowUserUnsavedWarning] = useState(false);
  const [showAddUserUnsavedWarning, setShowAddUserUnsavedWarning] = useState(false);
  // Snapshot of the Add User form taken when the dialog opens — the unsaved
  // warning compares actual form data instead of trusting DOM input events
  // (browser autofill fired spurious input events on untouched forms).
  const addUserSnapshotRef = useRef('');
  const [deactivateWizardUser, setDeactivateWizardUser] = useState(null);
  const [reactivateWizardUser, setReactivateWizardUser] = useState(null);
  const [statusTogglePrompt, setStatusTogglePrompt] = useState(null); // { next: bool } — wizard-or-toggle prompt from the Account Status card switch
  const [deactivateWizardDevice, setDeactivateWizardDevice] = useState(null);
  const [reactivateWizardDevice, setReactivateWizardDevice] = useState(null);
  const [allDevicesForWizard, setAllDevicesForWizard] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    // Account Details
    account_reference: '',
    username: '',
    full_name: '',
    email: '',
    password: '',
    role: 'end_user',
    account_status: 'active',
    expiry_date: '',
    expiry_enabled: false,
    send_credentials: false,
    send_invite: true,
    invite_email: true,
    invite_sms: false,
    
    // Contact Information
    company: '',
    display_name_preference: 'personal',
    address_street: '',
    address_city: '',
    address_postcode: '',
    address_state: '',
    address_country: 'Australia',
    phone: '',
    mobile: '',
    notes: '',
    
    // Privileges
    enabled_pages: [],
    landing_page: 'dashboard_page',
    enabled_modules: [],
    map_providers: [],
    map_features: {
      map_satellite_view: false,
      map_street_view: false,
      map_traffic_layer: false,
      map_terrain_view: false,
      map_heatmap: false,
      map_weather: false,
      map_nearest_device: false,
      map_dark_mode: false,
      map_geofence_overlay: false,
      live_tracking_edit_asset: true,
      live_tracking_edit_icon: true,
      share_position: false,
      generate_link: false,
      trip_direction_trail: false,
      animated_movement: false,
      live_stop_timer: false,
      device_card_customization: false,
      speed_alert_config: false,
      snap_to_road: false,
      google_reverse_geocoding: false,
      osrm_route_eta: false,
      notification_email: true,
      notification_sms: false,
      notification_push: false,
      google_directions_eta: false,
    },
    sms_provider: 'mobilemessage',
    history_retention_days: 0, // Will be set from global settings when editing
    ai_enabled: false,
    
    // Resource Limits
    max_devices: 0, max_beacons: 0,
    max_drivers: 0,
    max_staff: 0,
    max_passengers: 0,
    // User Management Limits
    max_end_users: 0,
    max_sub_accounts: 0,
    // Allocated Users (for Managers)
    allocated_user_ids: [],
    // Live Streaming Limits
    max_stream_duration_minutes: 3,
    stream_inactivity_timeout_minutes: 1,
    
    // Devices
    assigned_devices: [],
    
    is_active: true,
  });

  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Device search
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [assignedDeviceSearchQuery, setAssignedDeviceSearchQuery] = useState('');

  // Available devices for assignment (derived via useMemo below from allDevices/assignedDevices)
  const [allDevices, setAllDevices] = useState([]);
  const [assignedDevices, setAssignedDevices] = useState([]);
  // True once the full device/beacon list has loaded — lets us tell a transient "Loading…"
  // placeholder (list not loaded yet) apart from a device that is genuinely outside this
  // admin's allocation pool (their Device Management List) -> "Managed by another admin".
  const [devicesLoaded, setDevicesLoaded] = useState(false);

  // Available beacons for assignment (derived via useMemo below from allBeacons/assignedBeacons)
  const [allBeacons, setAllBeacons] = useState([]);
  const [assignedBeacons, setAssignedBeacons] = useState([]);
  const [beaconSearchQuery, setBeaconSearchQuery] = useState('');
  // Server-side paginated "Available" panels (Edit User > Devices / Beacons tabs)
  const [availDevicesPageSize, setAvailDevicesPageSize] = usePersistedPageSize('um-available-devices', 10);
  const [availBeaconsPageSize, setAvailBeaconsPageSize] = usePersistedPageSize('um-available-beacons', 10);
  const [availDeviceRows, setAvailDeviceRows] = useState([]);
  const [availDevicesTotal, setAvailDevicesTotal] = useState(0);
  const [availDevicesPage, setAvailDevicesPage] = useState(1);
  const [availDevicesLoading, setAvailDevicesLoading] = useState(false);
  const [availBeaconRows, setAvailBeaconRows] = useState([]);
  const [availBeaconsTotal, setAvailBeaconsTotal] = useState(0);
  const [availBeaconsPage, setAvailBeaconsPage] = useState(1);
  const [availBeaconsLoading, setAvailBeaconsLoading] = useState(false);
  // Client-side pagination for the Assigned panels (state is the save payload, so we page in-memory)
  const [assignedDevicesPageSize, setAssignedDevicesPageSize] = usePersistedPageSize('um-assigned-devices', 10);
  const [assignedBeaconsPageSize, setAssignedBeaconsPageSize] = usePersistedPageSize('um-assigned-beacons', 10);
  const [assignedDevicesPage, setAssignedDevicesPage] = useState(1);
  const [assignedBeaconsPage, setAssignedBeaconsPage] = useState(1);
  // Persisted (saved) assignment ids at dialog-open time — used to compute unsaved deltas
  const persistedAssignedIdsRef = useRef({ devices: [], beacons: [] });
  const [assignedBeaconSearchQuery, setAssignedBeaconSearchQuery] = useState('');

  // Allocated Users (for Managers) - derived via useMemo below
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Privileges tree structure
  const privilegesTree = [
    {
      id: 'dashboard_page',
      label: 'Dashboard',
      children: []
    },
    {
      id: 'live_tracking_page',
      label: 'Live Tracking',
      children: [
        { id: 'live_tracking_driver', label: 'Driver' },
        { id: 'live_tracking_staff', label: 'Staff' },
        { id: 'live_tracking_passengers', label: 'Passengers' },
        { id: 'live_tracking_assets', label: 'Trailer / Equipment' },
        { id: 'live_tracking_telemetry_data', label: 'Telemetry Data' },
        { id: 'live_tracking_commands', label: 'Commands' },
        { id: 'live_tracking_events', label: 'Events' },
      ]
    },
    {
      id: 'media_page',
      label: 'Media',
      children: [
        { id: 'media_event_videos', label: 'Event Videos' },
        { id: 'media_live_videos', label: 'Live Videos' },
        { id: 'media_remote_downloads', label: 'Remote Media' },
        { id: 'media_shared_evidence', label: 'Shared Evidence' },
      ]
    },
    {
      id: 'analytics_page',
      label: 'Analytics',
      children: [
        { id: 'analytics_reports', label: 'Reports' },
        { id: 'analytics_events', label: 'Events' },
        { id: 'analytics_alerts', label: 'Alerts' },
        { id: 'analytics_history', label: 'History' },
        { id: 'analytics_history_forensic_panel', label: 'History Forensic Panel' },
        { id: 'analytics_investigations', label: 'Investigations' },
      ]
    },
    {
      id: 'geofencing_page',
      label: 'Geofencing',
      children: [
        { id: 'geofencing_zones', label: 'Zones' },
        { id: 'geofencing_places', label: 'Places' },
        { id: 'geofencing_routes', label: 'Routes' },
      ]
    },
    {
      id: 'people_page',
      label: 'People',
      children: [
        { id: 'people_drivers', label: 'Drivers' },
        { id: 'people_staff', label: 'Staff' },
        { id: 'people_passengers', label: 'Passengers' },
        { id: 'people_rfid_ibutton_cards', label: 'RFID / iButton Cards' },
        { id: 'people_driver_corrections', label: 'Driver Corrections' },
      ]
    },
    {
      id: 'fleet_page',
      label: 'Fleet',
      children: [
        { id: 'fleet_vehicles', label: 'Vehicles' },
        { id: 'fleet_trailers', label: 'Trailers' },
        { id: 'fleet_maintenance', label: 'Maintenance' },
        { id: 'fleet_fuel', label: 'Fuel' },
        { id: 'fleet_equipment', label: 'Equipment' },
        { id: 'fleet_green_driving', label: 'Green Driving' },
      ]
    },
    {
      id: 'operations_page',
      label: 'Operations',
      children: [
        { id: 'operations_asset_booking', label: 'Asset Booking' },
        { id: 'operations_pre_checklist', label: 'Pre-Checklist' },
        { id: 'operations_reporting', label: 'Reporting & Compliance' },
      ]
    },
    {
      id: 'chat_page',
      label: 'Chat',
      children: [
        { id: 'chat_voice_calls', label: 'Voice Calls 1:1' },
        { id: 'chat_video_calls', label: 'Voice & Video Calls 1:1' },
        { id: 'chat_group_voice_4', label: 'Group Voice Calls — up to 4' },
        { id: 'chat_group_voice_6', label: 'Group Voice Calls — up to 6' },
        { id: 'chat_group_voice_8', label: 'Group Voice Calls — up to 8' },
        { id: 'chat_group_video_4', label: 'Group Voice & Video Calls — up to 4' },
        { id: 'chat_group_video_6', label: 'Group Voice & Video Calls — up to 6' },
        { id: 'chat_group_video_8', label: 'Group Voice & Video Calls — up to 8' },
      ]
    },
    {
      id: 'installation_page',
      label: 'Installation',
      children: []
    },
    {
      id: 'settings_page',
      label: 'Settings',
      children: [
        { id: 'settings_general', label: 'General' },
        { id: 'settings_notifications', label: 'Notifications' },
        { id: 'settings_integrations', label: 'Integrations' },
        { id: 'settings_sub_accounts', label: 'Sub Accounts' },
        { id: 'settings_activity_trail', label: 'Activity Trail' },
      ]
    },
  ];

  // Admin Panel subpages - shown based on role being edited
  const adminPanelPrivilegesNode = {
    id: 'admin_panel_page',
    label: 'Admin Panel',
    children: [
      {
        id: 'admin_user_management',
        label: 'User Management',
        children: [
          { id: 'admin_user_management_view_all_non_admin_users', label: 'View Only' },
          { id: 'admin_user_management_manage_all_non_admin_users', label: 'Edit / Manage' },
        ],
      },
      {
        id: 'admin_device_management',
        label: 'Device Management',
        children: [
          { id: 'admin_device_management_add_devices', label: 'Add Devices' },
          { id: 'admin_device_management_view_manage_all_devices', label: 'View / Manage All Devices' },
        ],
      },
      { id: 'admin_tenant_isolation_audit', label: 'Tenant Isolation Audit' },
      { id: 'admin_impersonation_audit', label: 'Impersonation Audit' },
      { id: 'admin_global_audit_log', label: 'Audit Log' },
      { id: 'admin_audit_archives', label: 'Audit Archives' },
      { id: 'admin_audit_alerts', label: 'Audit Alerts' },
      { id: 'admin_beacon_management', label: 'Beacon Management' },
      { id: 'admin_commands', label: 'Commands' },
      { id: 'admin_feature_management', label: 'Feature Management' },
      { id: 'admin_system_settings', label: 'System Settings' },
    ]
  };

  // Manager gets a limited Admin Panel tree (only User Management & Device Management)
  const managerAdminPanelNode = {
    id: 'admin_panel_page',
    label: 'Admin Panel',
    children: [
      { id: 'admin_user_management', label: 'User Management' },
      { id: 'admin_device_management', label: 'Device Management' },
    ]
  };

  // Build the full privileges tree based on the user being edited
  const getPrivilegesTreeForUser = (user) => {
    if (user?.role === 'admin') {
      return [...privilegesTree, adminPanelPrivilegesNode];
    }
    if (user?.role === 'manager') {
      return [...privilegesTree, managerAdminPanelNode];
    }
    return privilegesTree;
  };

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('all');
  const [auditDateRange, setAuditDateRange] = useState({ from: undefined, to: undefined });
  const [auditDateResetKey, setAuditDateResetKey] = useState(0);

  // ============ ROLE-BASED ACCESS CONTROL HELPERS ============
  
  // Check if current user is editing themselves
  const isEditingSelf = selectedUser?.id === currentUser?.id;

  // Self-management (iter317): non-super Admins & Managers may manage their OWN device/beacon
  // allocations + view their own audit from User Management, but NEVER their own privileges.
  // - Privileges tab: Super Admin (any user) or Admin editing ANOTHER user.
  // - Devices/Beacons/Audit tabs: Admins & Super Admins always; Managers only when editing self.
  const canSeePrivilegesTab = currentUser?.role === 'super_admin' || (currentUser?.role === 'admin' && !isEditingSelf);
  const canSeeAllocationTabs = currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || (currentUser?.role === 'manager' && isEditingSelf);

  // Add User now reuses the multi-tab Edit dialog in "create mode" (single-step, no reopen).
  const isCreatingUser = isAddDialogOpen;
  // Live email-availability hint (same helper as the People dialogs) — checks the
  // email against all user accounts AND all drivers/staff/passengers.
  const userEmailAvail = useEmailAvailability(api, formData.email, {
    excludeUserId: isCreatingUser ? undefined : selectedUser?.id,
    enabled: isAddDialogOpen || isEditDialogOpen,
  });
  // Effective user for role-dependent tab UI: real user when editing, the form's chosen role when creating.
  const effectiveUser = selectedUser || { role: formData?.role };
  
  // Check if role field should be locked
  const isRoleLocked = () => {
    if (!selectedUser || !currentUser) return false;
    
    // Everyone's own role is locked (can't change your own role)
    if (isEditingSelf) return true;
    
    // Super Admin role can never be changed
    if (selectedUser.role === 'super_admin') return true;
    
    // Managers cannot change any roles
    if (currentUser.role === 'manager') return true;
    
    // End Users cannot change any roles
    if (currentUser.role === 'end_user') return true;
    
    return false;
  };

  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    if (!currentUser) return userRoles;
    
    // Define which roles each user can create/assign based on their role
    // Sub Accounts can ONLY be created by End Users from Settings page
    const roleMap = {
      'super_admin': ['super_admin', 'admin', 'manager', 'end_user'],
      'admin': ['manager', 'end_user'],
      'manager': ['end_user'],
      'end_user': [], // End Users create Sub Accounts from Settings, not Admin Panel
    };
    
    const allowedRoleValues = roleMap[currentUser.role] || [];
    return userRoles.filter(r => allowedRoleValues.includes(r.value));
  };

  // Get reason why role is locked (for display)
  const getRoleLockReason = () => {
    if (!selectedUser || !currentUser) return '';
    
    if (isEditingSelf) return 'You cannot change your own role';
    if (selectedUser.role === 'super_admin') return 'Super Administrator role cannot be changed';
    if (currentUser.role === 'manager') return 'Managers cannot modify user roles';
    if (currentUser.role === 'end_user') return 'You cannot modify user roles';
    
    return '';
  };

  // Check if current user can edit the selected user
  const canEditUser = (targetUser) => {
    if (!currentUser || !targetUser) return false;
    
    // Super Admin can edit anyone
    if (currentUser.role === 'super_admin') return true;
    
    // Admin can edit Managers, End Users, Sub Accounts (not Super Admin, not other Admins except self)
    if (currentUser.role === 'admin') {
      if (targetUser.role === 'super_admin') return false;
      if (targetUser.role === 'admin' && targetUser.id !== currentUser.id) return false;
      return true;
    }
    
    // Manager and End User have limited access (handled by backend)
    return true;
  };

  // Fetch audit logs for selected user
  const fetchAuditLogs = useCallback(async (userId, search, dateRange, actionFilter) => {
    if (!userId) return;
    setAuditLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (search) params.set('search', search);
      if (actionFilter && actionFilter !== 'all') params.set('action', actionFilter);
      if (dateRange?.from) params.set('from_date', dateRange.from.toISOString());
      if (dateRange?.to) {
                params.set('to_date', dateRange.to.toISOString());
      }
      const response = await api.get(`/users/${userId}/audit?${params}`);
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
    } finally {
      setAuditLogsLoading(false);
    }
  }, [api]);

  // Export user audit logs to CSV
  const handleExportUserAudit = () => {
    if (auditLogs.length === 0) { toast.info('No entries to export'); return; }
    const fmtTs = (t) => formatUserDateTime(t, userDateFmt, userTimeFmt, userTimezone);
    const headers = ['Time', 'Time (UTC)', 'Action', 'Actor', 'IP Address', 'Details', 'Changes'];
    const rows = auditLogs.map(l => [
      fmtTs(l.timestamp || l.created_at),
      formatUTCStamp(l.timestamp || l.created_at),
      formatAuditAction(l.action),
      l.actor_email || '',
      l.ip_address || '',
      l.details || '',
      l.changes ? Object.entries(l.changes).map(([k, v]) => {
        if (typeof v === 'object' && v !== null && ('old' in v || 'new' in v)) return `${k}: ${v.old ?? ''} → ${v.new ?? ''}`;
        return `${k}: ${v}`;
      }).join('; ') : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const ts = getFileTimestamp();
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `user_audit_${selectedUser?.email || 'unknown'}_${ts}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${auditLogs.length} entry(ies)`);
  };


  // Format audit action for display
  const formatAuditAction = (action) => {
    const actionMap = {
      'user_created': 'Account Created',
      'user_updated': 'Profile Updated',
      'user_deleted': 'Account Deleted',
      'user_login': 'Login',
      'user_login_failed': 'Failed Login',
      'user_logout': 'Logout',
      'user_blocked': 'Account Blocked',
      'user_unblocked': 'Account Unblocked',
      'password_changed': 'Password Changed',
      'role_changed': 'Role Changed',
      'expiry_changed': 'Account Expiry Changed',
      'features_updated': 'Features Updated',
      'devices_assigned': 'Devices Assigned',
      'resource_limits_changed': 'Resource Limits Changed',
      'device_allocated': 'Device Allocation Changed',
      'user_impersonated': 'Account Accessed (Impersonation)',
      'impersonation_ended': 'Impersonation Ended',
      'device_updated': 'Device Action',
      'command_sent': 'Command Sent',
      'device_deactivation_wizard': 'Device Deactivation Wizard',
      'device_reactivation_wizard': 'Device Reactivation Wizard',
      'user_deactivation_wizard': 'User Deactivation Wizard',
      'user_reactivation_wizard': 'User Reactivation Wizard',
      'banner_sent': 'Banner Sent',
      'banner_cancelled': 'Banner Cancelled',
      'banner_link_clicked': 'Banner Link Clicked',
      'session_expired': 'Session Expired',
      'session_ended_inactive': 'Session Ended (Inactive)',
      'session_resumed': 'Session Resumed',
      'geofence_created': 'Geofence Created',
      'geofence_updated': 'Geofence Updated',
      'geofence_deleted': 'Geofence Deleted',
      'global_settings_changed': 'Global Settings Changed',
    };
    return actionMap[action] || action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || action;
  };

  // Format timestamp for display using user's configured date/time format and timezone
  const formatTimestamp = (timestamp) => {
    return formatUserDateTime(timestamp, userDateFmt, userTimeFmt, userTimezone);
  };

  // Audit policy: audit entries additionally show the exact UTC moment.
  const formatTimestampUTC = (timestamp) => {
    return formatUserDateTimeWithUTC(timestamp, userDateFmt, userTimeFmt, userTimezone);
  };

  const getDeviceStatusMeta = (device) => {
    if (device?.is_active === false) {
      return {
        label: 'Deactivated',
        icon: XCircle,
        badgeClass: 'bg-red-600 text-white',
      };
    }

    const status = (device?.status || 'offline').toLowerCase();
    const statusMap = {
      online: {
        label: 'Online',
        icon: Wifi,
        badgeClass: 'bg-green-600 text-white',
      },
      moving: {
        label: 'Moving',
        icon: Activity,
        badgeClass: 'bg-blue-600 text-white',
      },
      stopped: {
        label: 'Stopped',
        icon: Circle,
        badgeClass: 'bg-red-600 text-white',
      },
      idle: {
        label: 'Idle',
        icon: Circle,
        badgeClass: 'bg-yellow-600 text-white',
      },
      offline: {
        label: 'Offline',
        icon: WifiOff,
        badgeClass: 'bg-gray-500 text-white',
      },
    };

    return statusMap[status] || {
      label: status ? status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Offline',
      icon: WifiOff,
      badgeClass: 'bg-gray-500 text-white',
    };
  };

  const fetchUsersSeqRef = useRef(0);
  const fetchUsers = useCallback(async () => {
    const seq = ++fetchUsersSeqRef.current;
    setLoading(true);
    try {
      const skip = pageIndex * pageSize;
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('skip', String(skip));
      // Push filters to the server so "N rows per page" actually renders N rows
      // (the previous client-side filter would slice out sub_accounts / non-matching
      // roles AFTER paging, causing e.g. "10 selected → 6 shown").
      if (searchQuery && searchQuery.trim()) params.set('search', searchQuery.trim());
      // The 'sub_account' filter option in the UI actually shows End Users that HAVE
      // sub-accounts — that's a computed cross-collection view we still handle on the
      // client. All other role filters go server-side.
      if (roleFilter && roleFilter !== 'all' && roleFilter !== 'sub_account') {
        params.set('role_filter', roleFilter);
      }
      // 'Sub Account' filter = End Users that HAVE sub accounts, computed SERVER-SIDE
      // so X-Total-Count and page slicing stay accurate (was client-side → broken paging).
      if (roleFilter === 'sub_account') params.set('has_sub_accounts', 'true');
      if (statusFilter === 'active' || statusFilter === 'blocked') {
        params.set('status_filter', statusFilter);
      }
      if (invitePendingOnly) params.set('invite_pending_only', 'true');
      // Sorting must be server-side — pagination slices the collection first, so a
      // client-only sort would only alphabetise WITHIN each page (the "Import Test
      // appeared on page 2" bug).
      params.set('sort_by', userSortField);
      params.set('sort_dir', userSortDir);
      const response = await api.get(`/users?${params.toString()}`);
      // Out-of-order guard: a slower older request (e.g. stale skip right before a
      // filter-driven page reset) must never overwrite the newest response.
      if (seq !== fetchUsersSeqRef.current) return;
      setUsers(response.data);
      const total = parseInt(response.headers?.['x-total-count'], 10);
      setTotalCount(Number.isFinite(total) ? total : (response.data?.length || 0));
      // Consume a one-shot deep-link signal from Device Management (stashed in
      // localStorage): pre-fill the search box to the target user, once, after the list
      // has loaded. Runs after the await (async) so it never blocks the synchronous render.
      const pendingId = window.localStorage.getItem('_openUserFromDevice');
      if (pendingId) {
        window.localStorage.removeItem('_openUserFromDevice');
        const target = (response.data || []).find(u => u.id === pendingId);
        if (target) setSearchQuery(target.email || target.full_name || '');
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (seq === fetchUsersSeqRef.current) toast.error('Failed to load users');
    } finally {
      if (seq === fetchUsersSeqRef.current) setLoading(false);
      setInitialLoadDone(true);
    }
  }, [api, pageIndex, pageSize, searchQuery, setSearchQuery, roleFilter, statusFilter, invitePendingOnly, userSortField, userSortDir]);

  useEffect(() => {
    let cancelled = false;
    api.get('/users/import-runs/latest')
      .then(res => { if (!cancelled && res.data?.run) setLastImportRun(res.data.run); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  // Invite Reminder Nudge — pending invitations whose last (re)send is older than the
  // admin-configured reminder window (System Settings → Communications → Account Invitations).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let days = 3;
        try {
          const cfg = await api.get('/admin/invite-reminder-settings');
          const d = parseInt(cfg.data?.days, 10);
          if (Number.isFinite(d) && d >= 1) days = d;
        } catch { /* fall back to 3 */ }
        const params = new URLSearchParams({ limit: '1000', skip: '0', invite_pending_only: 'true' });
        const res = await api.get(`/users?${params.toString()}`);
        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
        const stale = (res.data || []).filter(u => {
          if (u.invite_accepted_at || u.role === 'super_admin') return false;
          const lastTouch = u.invite_last_sent_at || u.invite_sent_at;
          return lastTouch && new Date(lastTouch).getTime() < cutoff;
        });
        if (!cancelled) { setStaleInvites(stale); setStaleInviteDays(days); }
      } catch { /* non-admins get 403 — no nudge */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const handleResendStaleInvites = async () => {
    if (staleInvites.length === 0 || resendingInvites) return;
    setResendingInvites(true);
    let sent = 0, regenerated = 0, failed = 0;
    for (const u of staleInvites) {
      try {
        const res = await api.post(`/users/${u.id}/resend-invite`, { channels: ['email'] });
        if (res.data?.email_sent) sent++; else regenerated++;
      } catch { failed++; }
    }
    if (sent > 0 && failed === 0 && regenerated === 0) {
      toast.success(`Resent ${sent} welcome invitation${sent === 1 ? '' : 's'}`);
    } else if (sent + regenerated > 0) {
      toast.warning(`${sent} emailed, ${regenerated} link${regenerated === 1 ? '' : 's'} regenerated without email (email delivery unavailable)${failed ? `, ${failed} failed` : ''}`);
    } else {
      toast.error('Failed to resend invitations');
    }
    setStaleInvites([]);
    setResendingInvites(false);
    fetchUsers();
  };

  const handleUndoImport = async () => {
    if (!lastImportRun || undoingImport) return;
    setUndoingImport(true);
    try {
      const res = await api.post(`/users/import-runs/${lastImportRun.id}/undo`);
      const { deleted = 0, restored = 0, errors: undoErrors = [] } = res.data || {};
      const parts = [];
      if (deleted) parts.push(`${deleted} imported user${deleted === 1 ? '' : 's'} removed`);
      if (restored) parts.push(`${restored} user${restored === 1 ? '' : 's'} restored`);
      if (undoErrors.length > 0) {
        toast.warning(`Import undone with issues — ${parts.join(', ') || 'no changes'}; ${undoErrors.length} issue(s): ${undoErrors.map(e => e.message).slice(0, 2).join('; ')}`);
      } else {
        toast.success(`Import undone — ${parts.join(', ') || 'nothing needed changing'}`);
      }
      setLastImportRun(null);
      fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to undo import');
      // Expired or already undone — the banner is stale, drop it.
      if (error?.response?.status === 400) setLastImportRun(null);
    } finally {
      setUndoingImport(false);
    }
  };

  /* ---- User Import/Export Log helpers ---- */
  const logUserIo = async (payload) => {
    try { const r = await api.post('/admin/user-io-logs', payload); return r.data?.id || null; } catch { return null; }
  };

  const openUserIoLog = () => {
    setUserIoLogOpen(true);
  };

  const buildUserIoLogFilterParams = () => {
    const params = new URLSearchParams();
    if (userIoLogTypeFilter !== 'all') params.set('kind', userIoLogTypeFilter);
    if (userIoLogPersonFilter !== 'all') params.set('person', userIoLogPersonFilter);
    if (userIoLogDateRange.from) params.set('date_from', new Date(userIoLogDateRange.from).toISOString());
    if (userIoLogDateRange.to) {
      const end = new Date(userIoLogDateRange.to);
      end.setHours(23, 59, 59, 999);
      params.set('date_to', end.toISOString());
    }
    return params.toString();
  };

  // Reset to page 1 whenever a filter changes
  useEffect(() => {
    setUserIoLogPage(1);
  }, [userIoLogTypeFilter, userIoLogPersonFilter, userIoLogDateRange]);

  // Server-side paginated fetch — runs while the modal is open
  useEffect(() => {
    if (!userIoLogOpen) return;
    let cancelled = false;
    (async () => {
      setUserIoLogsLoading(true);
      try {
        const params = buildUserIoLogFilterParams();
        const skip = (userIoLogPage - 1) * userIoLogPageSize;
        const r = await api.get(`/admin/user-io-logs?skip=${skip}&limit=${userIoLogPageSize}&${params}`);
        if (cancelled) return;
        setUserIoLogs(r.data?.logs || []);
        setUserIoLogTotal(r.data?.total || 0);
        setUserIoLogGrandTotal(r.data?.grand_total || 0);
        setUserIoLogPersons(r.data?.persons || []);
      } catch {
        if (!cancelled) toast.error('Failed to load Import/Export Log');
      } finally {
        if (!cancelled) setUserIoLogsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userIoLogOpen, userIoLogPage, userIoLogPageSize, userIoLogTypeFilter, userIoLogPersonFilter, userIoLogDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportUserIoLogCsv = async () => {
    let filtered = [];
    try {
      const r = await api.get(`/admin/user-io-logs?skip=0&limit=10000&${buildUserIoLogFilterParams()}`);
      filtered = r.data?.logs || [];
    } catch {
      toast.error('Failed to export log');
      return;
    }
    const headers = ['When', 'Type', 'Details', 'File Type', 'File Name', 'Rows', 'Created', 'Updated', 'Skipped', 'Failed', 'Undo Status', 'Undo Removed', 'By', 'Email'];
    const detailFor = (l) => l.kind === 'import'
      ? (l.source === 'wizard' ? 'Import Wizard' : 'Quick CSV Import')
      : 'Users Export';
    const esc = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map(l => [
      l.timestamp ? formatDT(l.timestamp) : '',
      l.kind === 'import' ? 'Import' : 'Export',
      detailFor(l),
      l.file_type || '',
      l.file_name || '',
      l.row_count ?? 0,
      l.kind === 'import' ? (l.created ?? 0) : '',
      l.kind === 'import' ? (l.updated ?? 0) : '',
      l.kind === 'import' ? (l.skipped ?? 0) : '',
      l.kind === 'import' ? (l.failed ?? 0) : '',
      l.undo_status === 'undone' ? 'Undone' : '',
      l.undo_status === 'undone' ? (l.undo_removed ?? 0) : '',
      l.actor_name || '',
      l.actor_email || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user_import_export_log_${getFileTimestamp()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} log entr${filtered.length === 1 ? 'y' : 'ies'}`);
  };

  const fetchAllUserIoLogs = async () => {
    try {
      const r = await api.get(`/admin/user-io-logs?skip=0&limit=10000&${buildUserIoLogFilterParams()}`);
      return r.data?.logs || [];
    } catch {
      toast.error('Failed to export log');
      return null;
    }
  };

  const buildUserIoLogTable = (filtered) => {
    const detailFor = (l) => l.kind === 'import'
      ? (l.source === 'wizard' ? 'Import Wizard' : 'Quick CSV Import')
      : 'Users Export';
    const columns = [
      { label: 'When', weight: 15 }, { label: 'Type', weight: 7 }, { label: 'Details', weight: 13 },
      { label: 'File', weight: 6 }, { label: 'File Name', weight: 18 }, { label: 'Rows', weight: 5 },
      { label: 'Created', weight: 7 }, { label: 'Updated', weight: 7 }, { label: 'Skipped', weight: 7 },
      { label: 'Failed', weight: 6 }, { label: 'Undo', weight: 8 }, { label: 'By', weight: 14 },
    ];
    const rows = filtered.map(l => [
      l.timestamp ? formatDT(l.timestamp) : '', l.kind === 'import' ? 'Import' : 'Export', detailFor(l),
      l.file_type || '', l.file_name || '', String(l.row_count ?? 0),
      l.kind === 'import' ? String(l.created ?? 0) : '', l.kind === 'import' ? String(l.updated ?? 0) : '',
      l.kind === 'import' ? String(l.skipped ?? 0) : '', l.kind === 'import' ? String(l.failed ?? 0) : '',
      l.undo_status === 'undone' ? `Undone (${l.undo_removed ?? 0})` : '',
      l.actor_name || l.actor_email || '',
    ]);
    return { columns, rows };
  };

  const exportUserIoLogPdf = async () => {
    const filtered = await fetchAllUserIoLogs();
    if (!filtered) return;
    try {
      const { doc, layout } = newLandscapeDoc();
      const logo = await loadLogoPng();
      const y = addBrandHeader(doc, layout, logo, 'User Import / Export Log',
        `${filtered.length} entr${filtered.length === 1 ? 'y' : 'ies'}  ·  ${new Date().toLocaleString()}`);
      const { columns, rows } = buildUserIoLogTable(filtered);
      drawPdfTable(doc, layout, y, columns, rows);
      addFooters(doc, layout);
      doc.save(`user_import_export_log_${getFileTimestamp()}.pdf`);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const exportUserIoLogHtml = async () => {
    const filtered = await fetchAllUserIoLogs();
    if (!filtered) return;
    const { columns, rows } = buildUserIoLogTable(filtered);
    await exportTableHtml('User Import / Export Log',
      `${filtered.length} entr${filtered.length === 1 ? 'y' : 'ies'}`,
      columns.map(c => c.label), rows, `user_import_export_log_${getFileTimestamp()}.html`);
    toast.success('HTML downloaded');
  };

  /* ---- Quick CSV Import (legacy one-shot loader, mirrors Device Management) ----
   * Skips existing users (by email), auto-generates a password when the CSV has
   * none, and applies Privileges + Devices & Beacons via the features endpoint. */
  const parseQuickLimit = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return null;
    if (s.toLowerCase() === 'unlimited') return -1;
    if (s.toLowerCase() === 'not set') return 0;
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? null : n;
  };

  // Bulk delete — SUPER ADMIN ONLY (server-enforced too)
  const handleBulkDeleteUsers = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post('/admin/users/bulk-delete', { user_ids: selectedUserIds });
      const deleted = r.data?.deleted ?? 0;
      const skipped = r.data?.skipped || [];
      toast.success(`Deleted ${deleted} user(s)`);
      if (skipped.length > 0) {
        const reasons = skipped.map(s => s.reason === 'self' ? 'your own account' : s.reason === 'super_admin' ? `Super Admin (${s.email})` : 'not found');
        toast.warning(`Skipped ${skipped.length}: ${[...new Set(reasons)].join(', ')}`, { duration: 8000 });
      }
      setSelectedUserIds([]);
      setBulkDeleteOpen(false);
      setBulkDeleteText('');
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleImportUsers = async (event) => {
    const f = event.target.files?.[0];
    if (!f) return;
    event.target.value = '';
    if (quickImporting) return;
    let parsed;
    try {
      parsed = await readUserImportFile(f);
    } catch (e) {
      toast.error(e.message || 'Failed to parse file');
      return;
    }
    const rowsData = parsed.rows.map(r => r.data).filter(d => String(d.email || '').trim());
    if (rowsData.length === 0) {
      toast.error('No rows with an email address found');
      return;
    }
    setQuickImporting(true);
    toast.info(`Importing ${rowsData.length} user(s)…`);
    const roleMap = {
      'super admin': 'super_admin', 'super_admin': 'super_admin',
      'administrator': 'admin', 'admin': 'admin', 'manager': 'manager',
      'end user': 'end_user', 'end_user': 'end_user',
      'sub account': 'sub_account', 'sub_account': 'sub_account',
    };
    const deviceByName = new Map(allDevices.map(d => [String(d.name || '').toLowerCase().trim(), d]));
    const beaconByName = new Map(allBeacons.map(d => [String(d.name || '').toLowerCase().trim(), d]));
    let created = 0, skipped = 0, failed = 0;
    const errors = [];
    const undoCreated = [];
    for (const d of rowsData) {
      const email = String(d.email).trim();
      try {
        const payload = {
          email,
          full_name: d.full_name || email.split('@')[0],
          username: d.username || email.split('@')[0],
          role: roleMap[String(d.role || '').toLowerCase().trim()] || 'end_user',
          company: d.company || '', phone: d.phone || '', mobile: d.mobile || '',
          address_street: d.address_street || '', address_city: d.address_city || '',
          address_postcode: d.address_postcode || '', address_state: d.address_state || '',
          address_country: d.address_country || 'Australia',
          notes: d.notes || '', account_reference: d.account_reference || '',
          is_active: !['blocked', 'false'].includes(String(d.is_active).toLowerCase()),
          password: d.password || `Temp${Math.random().toString(36).slice(-8)}${Math.floor(Math.random() * 10)}!`,
        };
        let res;
        try {
          res = await api.post('/users', payload);
        } catch (err) {
          const msg = String(err?.response?.data?.detail || '');
          if (/already registered|already exists|already taken/i.test(msg)) { skipped++; continue; }
          throw err;
        }
        const uid = res?.data?.id;
        created++;
        if (uid) {
          undoCreated.push({ user_id: uid, email });
          const fp = {};
          ['history_retention_days', 'max_devices', 'max_beacons', 'max_drivers', 'max_staff', 'max_passengers', 'max_end_users', 'max_sub_accounts'].forEach(k => {
            const v = parseQuickLimit(d[k]);
            if (v !== null) fp[k] = v;
          });
          const listedFeats = String(d.enabled_features || '').split(/[;,]/).map(s => s.trim()).filter(Boolean);
          if (listedFeats.length > 0) {
            const feats = {};
            listedFeats.forEach(k => { feats[k] = true; });
            fp.features = feats;
          }
          const devIds = String(d.assigned_device_names || '').split(/[;,]/).map(s => s.trim()).filter(Boolean)
            .map(n => deviceByName.get(n.toLowerCase())?.id).filter(Boolean);
          const bcnIds = String(d.assigned_beacon_names || '').split(/[;,]/).map(s => s.trim()).filter(Boolean)
            .map(n => beaconByName.get(n.toLowerCase())?.id).filter(Boolean);
          if (devIds.length > 0) fp.assigned_device_ids = devIds;
          if (bcnIds.length > 0) fp.assigned_beacon_ids = bcnIds;
          if (Object.keys(fp).length > 0) {
            try { await api.put(`/users/${uid}/features`, fp); }
            catch (fe) { errors.push({ email, message: `User created, but privileges/allocations failed: ${fe?.response?.data?.detail || fe.message}` }); }
          }
        }
      } catch (err) {
        failed++;
        errors.push({ email, message: String(err?.response?.data?.detail || err.message || 'unknown error') });
      }
    }
    const ioLogId = await logUserIo({
      kind: 'import', source: 'quick',
      file_type: /\.xlsx$/i.test(f.name) ? 'xlsx' : 'csv',
      file_name: f.name, row_count: rowsData.length,
      created, skipped, failed,
    });
    if (undoCreated.length > 0) {
      try {
        const runRes = await api.post('/users/import-runs', {
          created: undoCreated, updated: [],
          summary: { created, updated: 0, skipped, failed },
          io_log_id: ioLogId,
        });
        if (runRes?.data?.id) setLastImportRun(runRes.data);
      } catch { /* undo recording must not block */ }
    }
    setQuickImporting(false);
    const parts = [];
    if (created) parts.push(`${created} created`);
    if (skipped) parts.push(`${skipped} skipped (already exist)`);
    if (failed) parts.push(`${failed} failed`);
    if (failed === 0 && created > 0) toast.success(`Import complete — ${parts.join(', ')}`);
    else if (created > 0) toast.warning(`Import finished with issues — ${parts.join(', ')}. First error (${errors[0]?.email}): ${errors[0]?.message}`, { duration: 10000 });
    else toast.error(`Import failed — ${parts.join(', ') || 'no users imported'}${errors[0] ? `. First error (${errors[0].email}): ${errors[0].message}` : ''}`, { duration: 10000 });
    fetchUsers();
  };

  const fetchDevices = useCallback(async () => {
    try {
      // limit=100000 so the FULL fleet loads (backend default is limit=100). A truncated
      // list could otherwise cause the Edit-User save to silently drop allocations.
      const response = await api.get('/devices?show_all=true&limit=100000');
      // Split into GPS devices and beacons
      const all = response.data || [];
      setAllDevices(all.filter(d => d.device_type !== 'beacon'));
      setAllBeacons(all.filter(d => d.device_type === 'beacon'));
      setDevicesLoaded(true);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      setAllDevices([]);
      setAllBeacons([]);
    }
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Defer to a microtask so the data-loading calls below (fetchUsers flips `loading`)
      // don't run synchronously inside the effect pass. The cancellation guard avoids
      // work after unmount. (Date/time format now comes from useGeneralSettings above.)
      await Promise.resolve();
      if (cancelled) return;
      fetchUsers();
      fetchDevices();
    })();
    return () => { cancelled = true; };
  }, [fetchUsers, fetchDevices]);

  // Reset to page 0 when the page-size, sort, or any filter changes (otherwise searching
  // from page 2+ keeps the stale skip and shows an empty list even when matches exist —
  // same bug previously fixed in Device Management). Skips mount so ?page= deep-links survive.
  useSkipFirstEffect(() => {
    setPageIndex(0);
  }, [pageSize, userSortField, userSortDir, searchQuery, roleFilter, statusFilter, invitePendingOnly]);

  // Fetch cross-page pending invite total + role counts. Uses a lightweight
  // limit=1 request that reads X-Total-Count so the pending-invite badge and
  // role summary stay accurate no matter which page is displayed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pParams = new URLSearchParams();
        pParams.set('limit', '1');
        pParams.set('skip', '0');
        pParams.set('invite_pending_only', 'true');
        if (searchQuery && searchQuery.trim()) pParams.set('search', searchQuery.trim());
        const pRes = await api.get(`/users?${pParams.toString()}`);
        const pTotal = parseInt(pRes.headers?.['x-total-count'], 10);
        if (!cancelled) setTotalPendingCount(Number.isFinite(pTotal) ? pTotal : 0);
      } catch { if (!cancelled) setTotalPendingCount(0); }

      try {
        const roles = ['super_admin', 'admin', 'manager', 'end_user'];
        const results = await Promise.all(roles.map(async (role) => {
          const rParams = new URLSearchParams();
          rParams.set('limit', '1');
          rParams.set('skip', '0');
          rParams.set('role_filter', role);
          if (searchQuery && searchQuery.trim()) rParams.set('search', searchQuery.trim());
          const rRes = await api.get(`/users?${rParams.toString()}`);
          const rTotal = parseInt(rRes.headers?.['x-total-count'], 10);
          return [role, Number.isFinite(rTotal) ? rTotal : 0];
        }));
        if (!cancelled) setTotalRoleCounts(Object.fromEntries(results));
      } catch { if (!cancelled) setTotalRoleCounts({}); }
    })();
    return () => { cancelled = true; };
  }, [api, searchQuery, users]);

  // Auto-refresh users when page becomes visible (handles tab switching, navigation back)
  // This ensures expired accounts show correct status without manual refresh
  // BUT only when no dialog is open to prevent flash/reload issues
  useEffect(() => {
    let lastRefresh = 0;
    const REFRESH_DEBOUNCE = 5000; // Minimum 5 seconds between refreshes
    
    const handleVisibilityChange = () => {
      // Don't refresh if a dialog is open or if we recently refreshed
      if (document.visibilityState === 'visible' && !isEditDialogOpen && !isAddDialogOpen) {
        const now = Date.now();
        if (now - lastRefresh > REFRESH_DEBOUNCE) {
          lastRefresh = now;
          fetchUsers();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUsers, isEditDialogOpen, isAddDialogOpen]);

  // Fetch devices for the deactivation/reactivation wizard — refresh when wizard opens
  useEffect(() => {
    const fetchDevicesForWizard = async () => {
      try {
        const res = await api.get('/admin/devices?limit=10000');
        setAllDevicesForWizard(res.data || []);
      } catch { /* ignore device fetch errors */ }
    };
    fetchDevicesForWizard();
  }, [api, deactivateWizardUser, reactivateWizardUser]);



  // Fetch all End Users that can be allocated to Managers — derived from the loaded users
  // list when editing a Manager (calculated during render; no effect/extra render needed).
  const allocatableUsers = useMemo(() => {
    if (selectedUser?.role === 'manager' && isEditDialogOpen) {
      return users.filter(u => u.role === 'end_user');
    }
    return [];
  }, [selectedUser, isEditDialogOpen, users]);

  // ── Server-side paginated Available panels (Edit User > Devices / Beacons) ──
  const debouncedDeviceSearch = useDebouncedValue(deviceSearchQuery, 350);
  const debouncedBeaconSearch = useDebouncedValue(beaconSearchQuery, 350);
  const assignedDeviceIdsKey = assignedDevices.map(d => d.id).join(',');
  const assignedBeaconIdsKey = assignedBeacons.map(d => d.id).join(',');
  // Unsaved deltas vs persisted assignments (small lists — safe in the URL)
  const persistedDeviceIds = persistedAssignedIdsRef.current.devices;
  const persistedBeaconIds = persistedAssignedIdsRef.current.beacons;
  const newlyAssignedDevicesKey = assignedDevices.filter(d => !persistedDeviceIds.includes(d.id)).map(d => d.id).join(',');
  const unassignedDevicesKey = persistedDeviceIds.filter(id => !assignedDevices.some(d => d.id === id)).join(',');
  const newlyAssignedBeaconsKey = assignedBeacons.filter(d => !persistedBeaconIds.includes(d.id)).map(d => d.id).join(',');
  const unassignedBeaconsKey = persistedBeaconIds.filter(id => !assignedBeacons.some(d => d.id === id)).join(',');

  const fetchAvailablePage = useCallback(async ({ type, page, pageSize, search, userId, excludeKey, includeKey, assignedKey, setRows, setTotal, setLoading }) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        show_all: 'true',
        device_type: type,
        skip: String((page - 1) * pageSize),
        limit: String(pageSize),
      });
      if (search.trim()) params.set('search', search.trim());
      if (userId) params.set('exclude_assigned_to', userId);
      if (excludeKey) params.set('exclude_ids', excludeKey);
      if (includeKey) params.set('include_ids', includeKey);
      const res = await api.get(`/devices?${params.toString()}`);
      // Safety net: never show a currently-assigned row in the available list.
      const assignedSet = new Set(assignedKey ? assignedKey.split(',') : []);
      setRows((res.data || []).filter(d => !assignedSet.has(d.id)));
      const t = parseInt(res.headers?.['x-total-count'], 10);
      setTotal(Number.isFinite(t) ? t : (res.data || []).length);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isEditDialogOpen || activeEditTab !== 'devices') return;
    fetchAvailablePage({
      type: 'gps', page: availDevicesPage, pageSize: availDevicesPageSize, search: debouncedDeviceSearch,
      userId: selectedUser?.id, excludeKey: newlyAssignedDevicesKey, includeKey: unassignedDevicesKey, assignedKey: assignedDeviceIdsKey,
      setRows: setAvailDeviceRows, setTotal: setAvailDevicesTotal, setLoading: setAvailDevicesLoading,
    });
  }, [fetchAvailablePage, isEditDialogOpen, activeEditTab, availDevicesPage, availDevicesPageSize, debouncedDeviceSearch, selectedUser?.id, newlyAssignedDevicesKey, unassignedDevicesKey, assignedDeviceIdsKey]);

  useEffect(() => {
    if (!isEditDialogOpen || activeEditTab !== 'beacons') return;
    fetchAvailablePage({
      type: 'beacon', page: availBeaconsPage, pageSize: availBeaconsPageSize, search: debouncedBeaconSearch,
      userId: selectedUser?.id, excludeKey: newlyAssignedBeaconsKey, includeKey: unassignedBeaconsKey, assignedKey: assignedBeaconIdsKey,
      setRows: setAvailBeaconRows, setTotal: setAvailBeaconsTotal, setLoading: setAvailBeaconsLoading,
    });
  }, [fetchAvailablePage, isEditDialogOpen, activeEditTab, availBeaconsPage, availBeaconsPageSize, debouncedBeaconSearch, selectedUser?.id, newlyAssignedBeaconsKey, unassignedBeaconsKey, assignedBeaconIdsKey]);

  // Back to page 1 whenever the search / page size changes or the dialog re-opens.
  useEffect(() => { setAvailDevicesPage(1); }, [debouncedDeviceSearch, availDevicesPageSize, isEditDialogOpen]);
  useEffect(() => { setAvailBeaconsPage(1); }, [debouncedBeaconSearch, availBeaconsPageSize, isEditDialogOpen]);

  // Resolve placeholder allocation rows to real device/beacon objects once the full list
  // finishes loading. Placeholders are created on dialog-open when the list hasn't loaded
  // yet (a load-timing race) so the saved allocation is never lost. Derived during render
  // so the raw `assignedDevices`/`assignedBeacons` state stays the single source of truth
  // for save payloads and slot counts (id/length are identical on placeholders and real
  // rows). Only the rendered list (`filteredAssigned*`) consumes the resolved version.
  const resolvedAssignedDevices = useMemo(() => {
    if (!allDevices.length) return assignedDevices;
    const byId = new Map(allDevices.map(d => [d.id, d]));
    return assignedDevices.map(d => (d._placeholder && byId.has(d.id)) ? byId.get(d.id) : d);
  }, [assignedDevices, allDevices]);

  const resolvedAssignedBeacons = useMemo(() => {
    if (!allBeacons.length) return assignedBeacons;
    const byId = new Map(allBeacons.map(d => [d.id, d]));
    return assignedBeacons.map(d => (d._placeholder && byId.has(d.id)) ? byId.get(d.id) : d);
  }, [assignedBeacons, allBeacons]);

  const handleInputChange = (field, value) => {
    // Show confirmation when turning off expiry
    if (field === 'expiry_enabled' && value === false && formData.expiry_enabled === true) {
      setPendingInputChange({
        title: 'Disable Account Expiry',
        message: 'Are you sure you want to disable Account Expiry? This will automatically activate the account and remove the expiry date restriction.',
        field, value,
      });
      return;
    }
    
    // Show confirmation when changing role (only when editing an existing user —
    // for a brand-new user there are no privileges to reset, so apply directly).
    if (field === 'role' && value !== formData.role && !isCreatingUser) {
      setPendingInputChange({
        title: 'Change User Role',
        message: `Are you sure you want to change the role to ${value.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}? This will reset the user's page access and privileges to match the new role's defaults.`,
        field, value,
      });
      return;
    }
    
    applyInputChange(field, value);
  };

  const applyInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // AUTO-ACTIVATE: When expiry is disabled, automatically activate the account
      if (field === 'expiry_enabled' && value === false && prev.expiry_enabled === true) {
        newData.account_status = 'active';
      }
      
      // ROLE CHANGE: Reset enabled_pages and limits to match new role's defaults
      if (field === 'role' && value !== prev.role) {
        const roleDefaults = {
          super_admin: {
            enabled_pages: [
              'dashboard_page', 'live_tracking_page', 'media_page', 'analytics_page',
              'geofencing_page', 'people_page', 'fleet_page', 'operations_page',
              'chat_page', 'settings_page', 'admin_panel_page', 'admin_user_management',
              'admin_device_management', 'admin_feature_management', 'admin_system_settings',
              'admin_commands', 'settings_general', 'settings_notifications',
              'settings_integrations', 'settings_sub_accounts',
              'live_tracking_driver', 'live_tracking_staff', 'live_tracking_passengers', 'live_tracking_assets',
              'live_tracking_telemetry_data', 'live_tracking_commands', 'live_tracking_events'
            ],
            max_devices: -1, max_beacons: -1, max_drivers: -1, max_staff: -1, max_passengers: -1,
            max_end_users: -1, max_sub_accounts: -1, history_retention_days: 365
          },
          admin: {
            enabled_pages: [
              'dashboard_page', 'live_tracking_page', 'media_page', 'analytics_page',
              'geofencing_page', 'people_page', 'fleet_page', 'operations_page',
              'chat_page', 'settings_page', 'admin_panel_page', 'admin_user_management',
              'admin_device_management', 'admin_feature_management', 'admin_system_settings',
              'admin_commands', 'settings_general',
              'settings_notifications', 'settings_integrations', 'settings_sub_accounts',
              'live_tracking_driver', 'live_tracking_staff', 'live_tracking_passengers', 'live_tracking_assets',
              'live_tracking_telemetry_data', 'live_tracking_commands', 'live_tracking_events'
            ],
            max_devices: -1, max_beacons: -1, max_drivers: -1, max_staff: -1, max_passengers: -1,
            max_end_users: -1, max_sub_accounts: -1, history_retention_days: 365
          },
          manager: {
            enabled_pages: [
              'dashboard_page', 'live_tracking_page', 'chat_page', 'settings_page',
              'admin_panel_page', 'admin_user_management', 'admin_commands',
              'settings_general', 'settings_notifications'
            ],
            max_devices: 0, max_beacons: 0, max_drivers: 0, max_staff: 0, max_passengers: 0,
            max_end_users: 0, max_sub_accounts: 0, history_retention_days: 90
          },
          end_user: {
            enabled_pages: [
              'dashboard_page', 'live_tracking_page', 'settings_page', 'settings_general'
            ],
            max_devices: 0, max_beacons: 0, max_drivers: 0, max_staff: 0, max_passengers: 0,
            max_end_users: 0, max_sub_accounts: 0, history_retention_days: 90
          },
          sub_account: {
            enabled_pages: [],
            max_devices: 0, max_beacons: 0, max_drivers: 0, max_staff: 0, max_passengers: 0,
            max_end_users: 0, max_sub_accounts: 0, history_retention_days: 0
          }
        };
        
        const defaults = roleDefaults[value] || roleDefaults.end_user;
        newData.enabled_pages = defaults.enabled_pages;
        newData.max_devices = defaults.max_devices;
        newData.max_beacons = defaults.max_beacons;
        newData.max_drivers = defaults.max_drivers;
        newData.max_staff = defaults.max_staff;
        newData.max_passengers = defaults.max_passengers;
        newData.max_end_users = defaults.max_end_users;
        newData.max_sub_accounts = defaults.max_sub_accounts;
        newData.history_retention_days = defaults.history_retention_days;
        // Clear allocated users when changing away from Manager role
        if (value !== 'manager') {
          newData.allocated_user_ids = [];
        }
      }
      
      return newData;
    });
  };

  // Snapshot of the full Edit User form taken when the dialog opens — compares
  // ALL fields (the old version only compared a 7-field subset, so edits to
  // Account Reference, mobile, address, limits etc. bypassed the warning).
  useEffect(() => {
    if (isEditDialogOpen) setSavedUserSnapshot(JSON.stringify(formData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditDialogOpen]);
  const hasUserUnsavedChanges = () => {
    if (!savedUserSnapshot) return false;
    return JSON.stringify(formData) !== savedUserSnapshot;
  };

  const handleUserEditDialogClose = (forceClose = false) => {
    if (!forceClose && hasUserUnsavedChanges()) {
      setShowUserUnsavedWarning(true);
      return;
    }
    setIsEditDialogOpen(false);
    resetForm();
    setSavedUserSnapshot(null);
    setShowUserUnsavedWarning(false);
  };

  useEffect(() => {
    if (isAddDialogOpen) addUserSnapshotRef.current = JSON.stringify(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddDialogOpen]);
  const addUserFormHasChanges = () => JSON.stringify(formData) !== addUserSnapshotRef.current;

  const handleAddUserDialogClose = (forceClose = false) => {
    if (!forceClose && addUserFormHasChanges()) {
      setShowAddUserUnsavedWarning(true);
      return;
    }
    setIsAddDialogOpen(false);
    resetForm();
    setShowAddUserUnsavedWarning(false);
  };

  const resetForm = () => {
    setFormData({
      account_reference: '',
      username: '',
      full_name: '',
      email: '',
      password: '',
      role: 'end_user',
      account_status: 'active',
      expiry_date: '',
      expiry_enabled: false,
      send_credentials: false,
      send_invite: true,
      invite_email: true,
      invite_sms: false,
      company: '',
      display_name_preference: 'personal',
      address_street: '',
      address_city: '',
      address_postcode: '',
      address_state: '',
      address_country: 'Australia',
      phone: '',
      mobile: '',
      notes: '',
      enabled_pages: ['dashboard_page', 'live_tracking_page', 'settings_page', 'settings_general'],
      landing_page: 'dashboard_page',
      enabled_modules: [],
      map_providers: [],
      map_features: {
        map_satellite_view: false,
        map_street_view: false,
        map_traffic_layer: false,
        map_terrain_view: false,
        map_heatmap: false,
        map_weather: false,
        map_nearest_device: false,
        map_dark_mode: false,
        map_geofence_overlay: false,
      },
      history_retention_days: 90, // End User default; auto-adjusts when role changes
      ai_enabled: false,
      max_devices: 0, max_beacons: 0,
      max_drivers: 0,
      max_staff: 0,
      max_passengers: 0,
      max_end_users: 0,
      max_sub_accounts: 0,
      allocated_user_ids: [],
      assigned_devices: [],
      is_active: true,
    });
    setAssignedDevices([]);
    setAssignedBeacons([]);
    setNewPassword('');
    setDeviceSearchQuery('');
    setBeaconSearchQuery('');
    setActiveEditTab('account');
    setQuickLoginToken(null);
    setAuditSearch('');
    setAuditDateRange({ from: undefined, to: undefined });
    setSelectedUser(null);
  };

  const handleAddUser = async () => {
    if (!formData.email || !formData.full_name) {
      toast.error('Please fill in required fields (Full Name and Email)');
      return;
    }
    if (userEmailAvail?.available === false) {
      toast.error(userEmailAvail.reason || 'This email is already in use');
      return;
    }
    if (!formData.send_invite && !formData.password) {
      toast.error('Enter a password, or enable "Send welcome email" to let the user set their own.');
      return;
    }
    if (!formData.send_invite && !isPasswordValid(formData.password)) {
      toast.error(PASSWORD_POLICY_TEXT);
      return;
    }

    setSubmitting(true);
    try {
      // Auto-generate username from email if not provided.
      // Build the invite delivery channels from the ticked + actually-deliverable options.
      const inviteChannels = formData.send_invite
        ? [
            ...((formData.invite_email && (formData.email || '').trim() && isNotificationChannelActive('notification_email')) ? ['email'] : []),
            ...((formData.invite_sms && (formData.mobile || '').trim() && isNotificationChannelActive('notification_sms')) ? ['sms'] : []),
          ]
        : undefined;
      const userData = {
        ...formData,
        username: formData.username || formData.email.split('@')[0],
        assigned_device_ids: assignedDevices.map(d => d.id),
        invite_channels: inviteChannels,
      };
      const response = await api.post('/users', userData);
      const newUser = response.data;
      const newUserId = newUser?.id;

      // Single-step create: also persist privileges/devices/beacons/limits (mirrors the Edit save),
      // so the admin configures everything in one modal and never has to reopen the user.
      if (newUserId) {
        try {
          if (canSeePrivilegesTab) {
            // Build the full page/subpage key universe from the chosen role's privilege tree so
            // any default that the admin deselected is explicitly turned off.
            const treeKeys = [];
            getPrivilegesTreeForUser({ role: formData.role }).forEach(node => {
              treeKeys.push(node.id);
              if (node.children) treeKeys.push(...getAllChildIds(node.children));
            });
            const featuresPayload = {};
            treeKeys.forEach(k => { featuresPayload[k] = false; });
            (formData.enabled_pages || []).forEach(page => { featuresPayload[page] = true; });
            (formData.map_providers || []).forEach(p => { featuresPayload[p] = true; });
            ['osm_maps', 'google_maps'].forEach(p => { if (!(formData.map_providers || []).includes(p)) featuresPayload[p] = false; });
            Object.entries(formData.map_features || {}).forEach(([k, v]) => { featuresPayload[k] = v; });
            featuresPayload['ai_assistant'] = !!formData.ai_enabled;

            await api.put(`/users/${newUserId}/features`, {
              account_name: formData.company || formData.full_name,
              features: featuresPayload,
              landing_page: formData.landing_page || getFallbackLandingPageKey(formData.enabled_pages),
              history_retention_days: formData.history_retention_days,
              sms_provider: formData.sms_provider,
              assigned_device_ids: assignedDevices.map(d => d.id),
              assigned_beacon_ids: assignedBeacons.map(d => d.id),
              max_devices: formData.max_devices,
              max_beacons: formData.max_beacons,
              max_drivers: formData.max_drivers,
              max_staff: formData.max_staff,
              max_passengers: formData.max_passengers,
              max_end_users: formData.max_end_users,
              max_sub_accounts: formData.max_sub_accounts,
              allocated_user_ids: formData.allocated_user_ids,
              max_stream_duration_minutes:
                formData.max_stream_duration_minutes === '' || formData.max_stream_duration_minutes == null
                  ? 0 : parseInt(formData.max_stream_duration_minutes),
              stream_inactivity_timeout_minutes:
                formData.stream_inactivity_timeout_minutes === '' || formData.stream_inactivity_timeout_minutes == null
                  ? 0 : parseInt(formData.stream_inactivity_timeout_minutes),
              notes: formData.notes,
            });
          } else if (canSeeAllocationTabs) {
            // Manager/Admin without privilege rights: just persist device/beacon allocations.
            await api.put(`/users/${newUserId}/features`, {
              assigned_device_ids: assignedDevices.map(d => d.id),
              assigned_beacon_ids: assignedBeacons.map(d => d.id),
            });
          }
        } catch (featErr) {
          toast.warning('User created, but saving privileges/allocations failed — open the user to review.');
        }
      }

      const delivery = newUser?.invite_delivery || {};
      if (formData.send_invite) {
        const sentChans = Object.entries(delivery).filter(([, s]) => s === 'sent').map(([c]) => (c === 'sms' ? 'SMS' : 'Email'));
        toast.success(sentChans.length
          ? `User created — invitation sent via ${sentChans.join(' & ')}`
          : 'User created — invitation link is ready to copy from the user card');
        if (delivery.sms && delivery.sms !== 'sent') {
          const smsMsg = delivery.sms === 'not_configured'
            ? 'SMS not sent: no SMS provider is configured (Settings → Communications → SMS).'
            : delivery.sms === 'no_recipient'
              ? 'SMS not sent: no mobile number on file.'
              : delivery.sms === 'not_allowed'
                ? 'SMS not sent: your SMS notification privilege is off.'
                : 'SMS delivery failed — you can resend or copy the link from the user card.';
          toast.warning(smsMsg);
        }
      } else {
        toast.success('User created successfully');
      }
      setShowAddUserUnsavedWarning(false);
      setIsAddDialogOpen(false);
      resetForm();
      await fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to create user'));
    } finally {
      setSubmitting(false);
    }
  };

  // Detects whether saving would REMOVE any device/beacon from the user's existing
  // allocation; if so, routes through a confirmation prompt before the actual save so an
  // accidental drop (e.g. an incomplete in-memory list) becomes a catchable moment.
  const handleEditUser = async () => {
    if (!selectedUser) return;
    if (userEmailAvail?.available === false) {
      toast.error(userEmailAvail.reason || 'This email is already in use');
      return;
    }

    const origDeviceIds = formData.assigned_devices || [];
    const origBeaconIds = formData.assigned_beacon_ids || [];
    const curDeviceIds = assignedDevices.map(d => d.id);
    const curBeaconIds = assignedBeacons.map(d => d.id);
    const removedDevices = origDeviceIds.filter(id => !curDeviceIds.includes(id));
    const removedBeacons = origBeaconIds.filter(id => !curBeaconIds.includes(id));
    const removedCount = removedDevices.length + removedBeacons.length;

    if (removedCount > 0) {
      const parts = [];
      if (removedDevices.length) parts.push(`${removedDevices.length} device${removedDevices.length !== 1 ? 's' : ''}`);
      if (removedBeacons.length) parts.push(`${removedBeacons.length} beacon${removedBeacons.length !== 1 ? 's' : ''}`);
      setPendingUserAction({
        title: 'Remove allocation?',
        message: `This will remove ${parts.join(' and ')} from "${formData.full_name || 'this user'}". They will lose access to ${removedCount === 1 ? 'it' : 'them'}. Continue?`,
        confirmLabel: 'Remove & Save',
        variant: 'destructive',
        action: doSaveEditUser,
      });
      return;
    }
    await doSaveEditUser();
  };

  const doSaveEditUser = async () => {
    if (!selectedUser) return;

    setSubmitting(true);
    try {
      // Update basic user info
      await api.put(`/users/${selectedUser.id}`, {
        full_name: formData.full_name,
        email: formData.email,
        username: formData.username,
        role: formData.role,
        company: formData.company,
        display_name_preference: formData.display_name_preference || 'personal',
        phone: formData.phone,
        mobile: formData.mobile,
        is_active: formData.account_status === 'active',
        address_street: formData.address_street,
        address_city: formData.address_city,
        address_postcode: formData.address_postcode,
        address_state: formData.address_state,
        address_country: formData.address_country,
        notes: formData.notes,
        expiry_date: formData.expiry_date || null,
        expiry_enabled: formData.expiry_enabled,
      });

      // Privileges payload only when the viewer may edit privileges (Super Admin, or Admin
      // editing ANOTHER user). When editing self, Admins & Managers can ONLY change their own
      // device/beacon allocations — never privileges or limits.
      if (canSeePrivilegesTab) {
        // Update user features/privileges separately
        const featuresPayload = {};
        // First set ALL known page/subpage keys to false
        (formData.all_page_keys || []).forEach(key => {
          featuresPayload[key] = false;
        });
        // Then override enabled ones to true
        formData.enabled_pages.forEach(page => {
          featuresPayload[page] = true;
        });
        formData.map_providers.forEach(provider => {
          featuresPayload[provider] = true;
        });
        // Explicitly set disabled map providers to false
        ['osm_maps', 'google_maps'].forEach(p => {
          if (!formData.map_providers.includes(p)) featuresPayload[p] = false;
        });
        // Add map features
        Object.entries(formData.map_features).forEach(([key, value]) => {
          featuresPayload[key] = value;
        });
        featuresPayload['ai_assistant'] = !!formData.ai_enabled;

        await api.put(`/users/${selectedUser.id}/features`, {
          account_name: formData.company || formData.full_name,
          features: featuresPayload,
          landing_page: formData.landing_page || getFallbackLandingPageKey(formData.enabled_pages),
          history_retention_days: formData.history_retention_days,
          sms_provider: formData.sms_provider,
          assigned_device_ids: assignedDevices.map(d => d.id),
          assigned_beacon_ids: assignedBeacons.map(d => d.id),
          // Resource limits
          max_devices: formData.max_devices,
          max_beacons: formData.max_beacons,
          max_drivers: formData.max_drivers,
          max_staff: formData.max_staff,
          max_passengers: formData.max_passengers,
          // User management limits
          max_end_users: formData.max_end_users,
          max_sub_accounts: formData.max_sub_accounts,
          // Allocated users (for Managers)
          allocated_user_ids: formData.allocated_user_ids,
          // Live streaming limits — convert '' → 0 (disabled) at save time
          max_stream_duration_minutes:
            formData.max_stream_duration_minutes === '' || formData.max_stream_duration_minutes == null
              ? 0 : parseInt(formData.max_stream_duration_minutes),
          stream_inactivity_timeout_minutes:
            formData.stream_inactivity_timeout_minutes === '' || formData.stream_inactivity_timeout_minutes == null
              ? 0 : parseInt(formData.stream_inactivity_timeout_minutes),
          notes: formData.notes,
        });
      } else if (canSeeAllocationTabs) {
        // Self-allocation only (Admin/Manager editing themselves): save device/beacon
        // allocations to their own operational pages, without touching privileges or limits.
        await api.put(`/users/${selectedUser.id}/features`, {
          assigned_device_ids: assignedDevices.map(d => d.id),
          assigned_beacon_ids: assignedBeacons.map(d => d.id),
        });
      }

      toast.success('User updated successfully');
      // If the admin edited their own privileges, refresh feature flags immediately
      if (selectedUser?.id === currentUser?.id) {
        refetchFeatureFlags();
      }
      setSavedUserSnapshot(null);
      setShowUserUnsavedWarning(false);
      setIsEditDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    
    // Prevent deleting Super Admin
    if (user?.role === 'super_admin') {
      toast.error('Cannot delete Super Administrator account');
      return;
    }
    
    setPendingUserAction({
      title: 'Delete User?',
      requireTyped: true,
      message: (
        <>Are you sure you want to delete <strong className="text-foreground">{displayName(user) || 'this user'}</strong>? This action cannot be undone.</>
      ),
      action: async () => {
        try {
          await api.delete(`/users/${userId}`);
          toast.success('User deleted');
          // Remove the deleted id from any selection so the "N selected" chip
          // stays accurate without needing a full page reload.
          setSelectedUserIds(prev => prev.filter(id => id !== userId));
          // If we just emptied the current page and we're past page 1, walk back
          // one page — otherwise the list stays blank until the user clicks Prev.
          if (users.length === 1 && pageIndex > 0) {
            setPageIndex(pageIndex - 1); // fetchUsers re-runs via the pageIndex effect
          } else {
            fetchUsers();
          }
        } catch (error) {
          toast.error('Failed to delete user');
        }
      },
    });
  };

  // Helper to find node in tree
  const findNodeById = (id, nodes) => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(id, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper to get all child IDs recursively
  const getAllChildIds = (children) => {
    let ids = [];
    for (const child of children) {
      ids.push(child.id);
      if (child.children) {
        ids = [...ids, ...getAllChildIds(child.children)];
      }
    }
    return ids;
  };

  const openEditDialog = async (user) => {
    setNewPassword('');
    setDeviceSearchQuery('');
    setActiveEditTab('account');
    
    // Fetch fresh user data to ensure we have the latest status
    // This handles cases where background tasks may have blocked an expired account
    let freshUser = user;
    try {
      const userRes = await api.get(`/users/${user.id}`);
      if (userRes.data) {
        freshUser = userRes.data;
      }
    } catch (error) {
      console.error('Failed to fetch fresh user data:', error);
      // Continue with stale data if fetch fails
    }
    
    setSelectedUser(freshUser);
    setQuickLoginToken(freshUser.quick_login_token || null);  // Initialize with existing token
    
    // Load user's current feature configuration
    try {
      const featuresRes = await api.get(`/users/${freshUser.id}/features`);
      const userFeatures = featuresRes.data.features || {};
      
      // Extract enabled pages from features (excluding map providers and modules)
      const mapProviderKeys = ['osm_maps', 'google_maps'];
      const moduleKeys = ['ai_assistant', 'report_scheduling'];
      const mapFeatureKeys = ['map_satellite_view', 'map_street_view', 'map_traffic_layer', 'map_terrain_view', 'map_heatmap', 'map_weather', 'map_nearest_device', 'map_dark_mode', 'map_geofence_overlay', 'live_tracking_edit_asset', 'live_tracking_edit_icon', 'share_position', 'generate_link', 'media_share_event_evidence', 'media_video_show_trail_map', 'notification_email', 'notification_sms', 'notification_push', 'event_webhook_commands', 'google_directions_eta', 'trip_direction_trail', 'animated_movement', 'live_stop_timer', 'device_card_customization', 'speed_alert_config', 'snap_to_road', 'osrm_route_eta', 'google_reverse_geocoding'];
      
      // All page/subpage feature keys (excluding providers, modules, map features)
      const allPageKeys = Object.keys(userFeatures).filter(key => 
        !mapProviderKeys.includes(key) && 
        !moduleKeys.includes(key) &&
        !mapFeatureKeys.includes(key)
      );
      
      const enabledPages = allPageKeys.filter(key => userFeatures[key] === true);
      const landingPage = featuresRes.data.landing_page || getFallbackLandingPageKey(enabledPages) || 'dashboard_page';
      
      // Extract map providers
      const enabledMapProviders = Object.keys(userFeatures).filter(key => 
        userFeatures[key] === true && mapProviderKeys.includes(key)
      );
      
      // Extract map features
      const mapFeatures = {};
      mapFeatureKeys.forEach(key => {
        mapFeatures[key] = userFeatures[key] === true;
      });
      
      setFormData({
        account_reference: freshUser.account_reference || '',
        username: freshUser.username || freshUser.email.split('@')[0],
        full_name: freshUser.full_name,
        email: freshUser.email,
        password: '',
        role: freshUser.role,
        account_status: freshUser.is_active === false ? 'blocked' : 'active',
        expiry_date: freshUser.expiry_date || '',
        expiry_enabled: freshUser.expiry_enabled || false,
        send_credentials: false,
      send_invite: true,
        company: freshUser.company || '',
        display_name_preference: freshUser.display_name_preference || 'personal',
        address_street: freshUser.address_street || '',
        address_city: freshUser.address_city || '',
        address_postcode: freshUser.address_postcode || '',
        address_state: freshUser.address_state || '',
        address_country: freshUser.address_country || 'Australia',
        phone: freshUser.phone || '',
        mobile: freshUser.mobile || '',
        notes: freshUser.notes || '',
        enabled_pages: enabledPages,
        landing_page: landingPage,
        all_page_keys: allPageKeys,
        enabled_modules: [],
        map_providers: enabledMapProviders.length > 0 ? enabledMapProviders : [],
        map_features: mapFeatures,
        sms_provider: featuresRes.data.sms_provider || 'mobilemessage',
        history_retention_days: featuresRes.data.history_retention_days || 90,
        ai_enabled: userFeatures['ai_assistant'] === true,
        // Resource limits
        max_devices: featuresRes.data.max_devices ?? 0,
        max_beacons: featuresRes.data.max_beacons ?? 0,
        max_drivers: featuresRes.data.max_drivers ?? 0,
        max_staff: featuresRes.data.max_staff ?? 0,
        max_passengers: featuresRes.data.max_passengers ?? 0,
        // User management limits
        max_end_users: featuresRes.data.max_end_users ?? 0,
        max_sub_accounts: featuresRes.data.max_sub_accounts ?? 0,
        // Allocated users
        allocated_user_ids: featuresRes.data.allocated_user_ids || [],
        // Live streaming limits
        max_stream_duration_minutes: featuresRes.data.max_stream_duration_minutes ?? 3,
        stream_inactivity_timeout_minutes: featuresRes.data.stream_inactivity_timeout_minutes ?? 1,
        assigned_devices: featuresRes.data.assigned_device_ids || [],
        assigned_beacon_ids: featuresRes.data.assigned_beacon_ids || [],
        is_active: !freshUser.disabled,
      });
      
      // Set assigned devices for drag-drop. Preserve EVERY saved allocation id even if its
      // device object isn't in the loaded list yet (e.g. the list is still loading — a
      // mobile/slow-network race — or a >100-device fleet). Unresolved ids become
      // placeholders so a later "Save Changes" can NEVER silently drop allocations; they
      // are upgraded to real objects once the device list finishes loading.
      const deviceIds = featuresRes.data.assigned_device_ids || [];
      const deviceById = new Map(allDevices.map(d => [d.id, d]));
      const assignedDeviceObjects = deviceIds.map(
        id => deviceById.get(id) || { id, name: 'Loading…', device_type: 'gps_tracker', _placeholder: true }
      );
      setAssignedDevices(assignedDeviceObjects);
      persistedAssignedIdsRef.current.devices = deviceIds;

      // Set assigned beacons (same placeholder-preservation guard as devices above).
      const beaconIds = featuresRes.data.assigned_beacon_ids || [];
      const beaconById = new Map(allBeacons.map(d => [d.id, d]));
      const assignedBeaconObjects = beaconIds.map(
        id => beaconById.get(id) || { id, name: 'Loading…', device_type: 'beacon', _placeholder: true }
      );
      setAssignedBeacons(assignedBeaconObjects);
      persistedAssignedIdsRef.current.beacons = beaconIds;
      
      // Pre-fetch audit logs
      setAuditActionFilter('all');
      fetchAuditLogs(freshUser.id);
    } catch (error) {
      console.error('Failed to load user features:', error);
      // Use basic user data if features can't be loaded
      setFormData({
        account_reference: user.account_reference || '',
        username: user.username || user.email.split('@')[0],
        full_name: user.full_name,
        email: user.email,
        password: '',
        role: user.role,
        account_status: user.is_active === false ? 'blocked' : 'active',
        expiry_date: user.expiry_date || '',
        expiry_enabled: user.expiry_enabled || false,
        send_credentials: false,
      send_invite: true,
        company: user.company || '',
        display_name_preference: user.display_name_preference || 'personal',
        address_street: user.address_street || '',
        address_city: user.address_city || '',
        address_postcode: user.address_postcode || '',
        address_state: user.address_state || '',
        address_country: user.address_country || 'Australia',
        phone: user.phone || '',
        mobile: user.mobile || '',
        notes: user.notes || '',
        enabled_pages: [],
        enabled_modules: [],
        map_providers: ['osm_maps'],
        history_retention_days: 90,
        ai_enabled: false,
        max_devices: 0, max_beacons: 0,
        max_drivers: 0,
        max_staff: 0,
        max_passengers: 0,
        max_end_users: 0,
        max_sub_accounts: 0,
        allocated_user_ids: [],
        assigned_devices: user.assigned_devices || [],
        is_active: !user.disabled,
      });
      setAssignedDevices([]);
    }
    
    setIsEditDialogOpen(true);
  };
  
  // Handle password change
  const handlePasswordChange = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (!isPasswordValid(newPassword)) {
      toast.error(PASSWORD_POLICY_TEXT);
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.put(`/users/${selectedUser.id}/password`, {
        new_password: newPassword
      });
      toast.success('Password changed successfully. User will need to log in again.');
      setNewPassword('');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to change password'));
    } finally {
      setChangingPassword(false);
    }
  };

  // Quick Login Link functions
  const handleGenerateQuickLogin = async () => {
    if (!selectedUser) return;
    
    setQuickLoginLoading(true);
    try {
      const response = await api.post(`/users/${selectedUser.id}/quick-login-token`);
      const token = response.data.quick_login_token;
      setQuickLoginToken(token);
      
      // Update the selected user object to reflect the new token
      setSelectedUser(prev => ({ ...prev, quick_login_token: token }));
      
      toast.success('Quick login link generated successfully');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to generate quick login link'));
    } finally {
      setQuickLoginLoading(false);
    }
  };

  const handleRevokeQuickLogin = async () => {
    if (!selectedUser) return;
    
    setQuickLoginLoading(true);
    try {
      await api.delete(`/users/${selectedUser.id}/quick-login-token`);
      setQuickLoginToken(null);
      
      // Update the selected user object to reflect the removed token
      setSelectedUser(prev => ({ ...prev, quick_login_token: null }));
      
      toast.success('Quick login link revoked');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to revoke quick login link'));
    } finally {
      setQuickLoginLoading(false);
    }
  };

  // Account activation / welcome invitation
  const handleSendLoginDetails = async () => {
    if (!selectedUser?.id) return;
    const channels = [...(loginDetailsEmail ? ['email'] : []), ...(loginDetailsSms ? ['sms'] : [])];
    if (channels.length === 0) { toast.error('Select at least one delivery channel'); return; }
    setSendingLoginDetails(true);
    try {
      const res = await api.post(`/users/${selectedUser.id}/send-login-details`, { channels });
      const d = res.data?.invite_delivery || {};
      const sentVia = Object.entries(d).filter(([, s]) => s === 'sent').map(([c]) => (c === 'sms' ? 'SMS' : 'Email'));
      if (sentVia.length > 0) {
        toast.success(`Login details sent via ${sentVia.join(' & ')}`);
      } else {
        toast.error('Delivery failed — check your email/SMS provider configuration');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send login details');
    } finally {
      setSendingLoginDetails(false);
    }
  };

  const handleResendInvite = async () => {
    if (!selectedUser) return;
    const emailActive = !!((selectedUser.email || '').trim()) && isNotificationChannelActive('notification_email');
    const smsActive = !!((selectedUser.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
    const channels = [
      ...(resendInviteEmail && emailActive ? ['email'] : []),
      ...(resendInviteSms && smsActive ? ['sms'] : []),
    ];
    setInviteLoading(true);
    try {
      const res = await api.post(`/users/${selectedUser.id}/resend-invite`, { channels });
      const delivery = res.data?.invite_delivery || {};
      const sentKeys = Object.entries(delivery).filter(([, s]) => s === 'sent').map(([c]) => c);
      setSelectedUser(prev => ({
        ...prev,
        invite_status: 'pending_invitation',
        ...(sentKeys.length ? { invite_last_sent_at: new Date().toISOString(), invite_last_channels: sentKeys } : {}),
      }));
      const sentChans = sentKeys.map((c) => (c === 'sms' ? 'SMS' : 'Email'));
      if (sentChans.length) {
        toast.success(`Invitation re-sent via ${sentChans.join(' & ')}`);
      } else {
        toast.warning('Invitation link regenerated, but nothing was delivered. Tick a channel above or use "Copy activation link".');
      }
      if (resendInviteSms && delivery.sms && delivery.sms !== 'sent') {
        const smsMsg = delivery.sms === 'not_configured'
          ? 'SMS not sent: no SMS provider is configured (Settings → Communications → SMS).'
          : delivery.sms === 'no_recipient'
            ? 'SMS not sent: no mobile number on file (add one in the Contact tab and save).'
            : delivery.sms === 'not_allowed'
              ? 'SMS not sent: your SMS notification privilege is off.'
              : 'SMS delivery failed — try again or copy the link.';
        toast.warning(smsMsg);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to resend invitation'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!selectedUser) return;
    setInviteLoading(true);
    try {
      const res = await api.post(`/users/${selectedUser.id}/resend-invite?send_email=false`);
      setSelectedUser(prev => ({ ...prev, invite_status: 'pending_invitation' }));
      const url = res.data?.invite_url;
      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Activation link copied to clipboard');
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to generate activation link'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = () => {
    if (!selectedUser) return;
    setPendingUserAction({
      title: 'Cancel Invitation',
      message: `Cancel the pending invitation for "${displayName(selectedUser) || selectedUser.email}"? The activation link we emailed will stop working immediately. The account is kept — you can correct the email and resend a fresh invitation afterwards.`,
      confirmLabel: 'Cancel Invitation',
      action: async () => {
        setInviteLoading(true);
        try {
          await api.post(`/users/${selectedUser.id}/cancel-invite`);
          setSelectedUser(prev => ({ ...prev, invite_status: 'cancelled' }));
          toast.success('Invitation cancelled — the activation link has been revoked');
          fetchUsers();
        } catch (error) {
          toast.error(extractErrorMessage(error, 'Failed to cancel invitation'));
        } finally {
          setInviteLoading(false);
        }
      },
    });
  };

  const handlePrivilegeToggle = (privilegeId) => {
    setFormData(prev => {
      const currentPages = prev.enabled_pages || [];
      let newPages;
      
      if (currentPages.includes(privilegeId)) {
        // UNCHECKING - Remove this page and all its children
        newPages = currentPages.filter(p => p !== privilegeId);
        if (privilegeId === 'admin_user_management_view_all_non_admin_users') {
          newPages = newPages.filter(p => p !== 'admin_user_management_manage_all_non_admin_users');
        }
        // Cascade: unchecking a call tier also unchecks every higher tier that implies it
        const dependents = getDependentCallPrivileges(privilegeId);
        if (dependents.size) {
          newPages = newPages.filter(p => !dependents.has(p));
        }
        
        // Find and remove all child pages/subpages
        const node = findNodeById(privilegeId, getPrivilegesTreeForUser(effectiveUser));
        if (node && node.children) {
          const childIds = getAllChildIds(node.children);
          newPages = newPages.filter(p => !childIds.includes(p));
        }
      } else {
        // CHECKING - Add this page
        newPages = [...currentPages, privilegeId];
        if (privilegeId === 'admin_user_management_manage_all_non_admin_users') {
          newPages = [...new Set([...newPages, 'admin_user_management_view_all_non_admin_users'])];
        }
        // Cascade: checking a call tier auto-checks every lower tier it implies
        const implied = getImpliedCallPrivileges(privilegeId);
        if (implied.size) {
          newPages = [...new Set([...newPages, ...implied])];
        }
        if (['admin_user_management_view_all_non_admin_users', 'admin_user_management_manage_all_non_admin_users'].includes(privilegeId)) {
          newPages = [...new Set([...newPages, 'admin_user_management', 'admin_panel_page'])];
        }
        
        // If this is a subpage, also check the parent page
        for (const parentNode of getPrivilegesTreeForUser(effectiveUser)) {
          if (parentNode.children && parentNode.children.some(child => child.id === privilegeId)) {
            if (!newPages.includes(parentNode.id)) {
              newPages.push(parentNode.id);
            }
            break;
          }
        }
      }
      
      const allowedLandingOptions = getAllowedLandingPageOptions(newPages);
      const landingStillAllowed = allowedLandingOptions.some((option) => option.key === prev.landing_page);
      const nextLandingPage = landingStillAllowed
        ? prev.landing_page
        : getFallbackLandingPageKey(newPages) || 'dashboard_page';

      return { ...prev, enabled_pages: newPages, landing_page: nextLandingPage };
    });
  };

  // Handle "Login as User" (impersonation)
  const handleLoginAsUser = async (user) => {
    // Don't allow impersonating yourself
    if (user.email === 'admin@ensue.com.au') {
      toast.info('You are already logged in as this user');
      return;
    }
    
    try {
      const response = await api.post(`/users/${user.id}/impersonate`);
      const { token, user: impersonatedUser, impersonated_by_name, impersonation_session_id, started_at } = response.data;
      
      // Open new window with the impersonated session using sessionStorage for isolation
      const baseUrl = window.location.origin;
      const newWindow = window.open('about:blank', '_blank');
      
      if (newWindow) {
        // Write HTML to the new window that stores token in sessionStorage (tab-isolated)
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Logging in as ${displayName(impersonatedUser)}...</title>
            <style>
              body { font-family: system-ui; display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; background: #f5f5f5; }
              .loader { text-align: center; }
              .spinner { border: 3px solid #f3f3f3; border-top: 3px solid #2563eb; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 16px; }
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            </style>
          </head>
          <body>
            <div class="loader">
              <div class="spinner"></div>
              <p>Logging in as <strong>${displayName(impersonatedUser)}</strong>...</p>
            </div>
            <script>
              // Store in sessionStorage for tab-level isolation (doesn't affect main tab)
              sessionStorage.setItem('impersonate_token', '${token}');
              sessionStorage.setItem('impersonate_user', '${JSON.stringify(impersonatedUser).replace(/'/g, "\\'")}');
              sessionStorage.setItem('impersonate_by_name', '${String(impersonated_by_name || '').replace(/'/g, "\\'")}');
              sessionStorage.setItem('impersonate_session_id', '${impersonation_session_id || ''}');
              sessionStorage.setItem('impersonate_started_at', '${started_at || ''}');
              
              // Redirect to smart landing (picks the user's first available page)
              setTimeout(() => {
                window.location.href = '${baseUrl}/';
              }, 500);
            </script>
          </body>
          </html>
        `);
        newWindow.document.close();
        
        toast.success(`Opened new window as ${displayName(impersonatedUser)}`);
      } else {
        toast.error('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to login as user'));
    }
  };

  const handleAssignDevice = (device) => {
    // Check device limit before assigning
    const maxDevices = formData.max_devices;
    const currentCount = assignedDevices.length;
    
    // -1 means unlimited, allow assignment
    if (maxDevices === -1) {
      setAssignedDevices(prev => [...prev, device]);
      return;
    }
    
    // 0 or reached limit = blocked
    if (currentCount >= maxDevices) {
      toast.error(`Device limit reached (${currentCount}/${maxDevices}). Cannot assign more devices. Increase the limit in the Privileges tab.`);
      return;
    }
    
    setAssignedDevices(prev => [...prev, device]);
  };

  const handleUnassignDevice = (device) => {
    setAssignedDevices(prev => prev.filter(d => d.id !== device.id));
  };

  const handleAssignBeacon = (beacon) => {
    const maxBeacons = formData.max_beacons;
    const currentCount = assignedBeacons.length;
    if (maxBeacons === -1) {
      setAssignedBeacons(prev => [...prev, beacon]);
      return;
    }
    if (currentCount >= maxBeacons) {
      toast.error(`Beacon limit reached (${currentCount}/${maxBeacons}). Increase the limit in the Privileges tab.`);
      return;
    }
    setAssignedBeacons(prev => [...prev, beacon]);
  };

  const handleUnassignBeacon = (beacon) => {
    setAssignedBeacons(prev => prev.filter(d => d.id !== beacon.id));
  };

  const getRemainingBeaconSlots = () => {
    const maxBeacons = formData.max_beacons;
    if (maxBeacons === -1) return 'Unlimited';
    return `${maxBeacons - assignedBeacons.length} remaining`;
  };

  const isBeaconLimitReached = () => {
    const maxBeacons = formData.max_beacons;
    if (maxBeacons === -1) return false;
    return assignedBeacons.length >= maxBeacons;
  };

  // Calculate remaining device slots
  const getRemainingDeviceSlots = () => {
    const maxDevices = formData.max_devices;
    if (maxDevices === -1) return 'Unlimited';
    const remaining = maxDevices - assignedDevices.length;
    return `${remaining} remaining`;
  };

  const isDeviceLimitReached = () => {
    const maxDevices = formData.max_devices;
    if (maxDevices === -1) return false; // Unlimited
    return assignedDevices.length >= maxDevices; // 0 = blocked (0 >= 0 is true)
  };


  // Shared builder for Users export (CSV + Excel-safe XLSX). Fetches features
  // for the selected users and returns headers + display-formatted rows.
  // NOTE: `users` state only holds the CURRENT page — when the selection spans
  // multiple pages we fetch the full filtered set (one bulk call, filters
  // honoured) and pick out the selected IDs, same as the Device export fix.
  const buildUsersExportData = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('Select users to export first');
      return null;
    }
    const inPage = users.filter(u => selectedUserIds.includes(u.id));
    let usersToExport;
    if (inPage.length >= selectedUserIds.length) {
      // Everything selected is on the current page — no need to refetch.
      usersToExport = inPage;
    } else {
      // Cross-page selection — fetch the full filtered set and pick out the selected IDs.
      try {
        const params = new URLSearchParams();
        params.set('limit', '100000');
        params.set('skip', '0');
        if (searchQuery && searchQuery.trim()) params.set('search', searchQuery.trim());
        if (roleFilter && roleFilter !== 'all' && roleFilter !== 'sub_account') params.set('role_filter', roleFilter);
        if (statusFilter === 'active' || statusFilter === 'blocked') params.set('status_filter', statusFilter);
        if (invitePendingOnly) params.set('invite_pending_only', 'true');
        params.set('sort_by', userSortField);
        params.set('sort_dir', userSortDir);
        const r = await api.get(`/users?${params.toString()}`);
        const all = Array.isArray(r.data) ? r.data : [];
        const idSet = new Set(selectedUserIds);
        usersToExport = all.filter(u => idSet.has(u.id));
      } catch {
        toast.error('Failed to fetch selected users — export aborted');
        return null;
      }
    }
    
    try {
      // Fetch features for all users being exported
      const usersWithFeatures = await Promise.all(
        usersToExport.map(async (user) => {
          try {
            const featuresRes = await api.get(`/users/${user.id}/features`);
            const features = featuresRes.data;
            
            // Get assigned device names
            const assignedDeviceIds = features.assigned_device_ids || [];
            const assignedDeviceNames = allDevices
              .filter(d => assignedDeviceIds.includes(d.id))
              .map(d => d.name)
              .join('; ');
            
            // Get assigned beacon names
            const assignedBeaconIds = features.assigned_beacon_ids || [];
            const assignedBeaconNames = allBeacons
              .filter(d => assignedBeaconIds.includes(d.id))
              .map(d => d.name)
              .join('; ');
            
            // Get enabled features list
            const enabledFeatures = Object.entries(features.features || {})
              .filter(([key, value]) => value === true)
              .map(([key]) => key)
              .join('; ');
            
            return {
              ...user,
              ...features,
              assigned_device_names: assignedDeviceNames,
              assigned_device_count: assignedDeviceIds.length,
              assigned_beacon_names: assignedBeaconNames,
              assigned_beacon_count: assignedBeaconIds.length,
              enabled_features: enabledFeatures,
            };
          } catch (error) {
            console.error(`Failed to fetch features for user ${user.id}:`, error);
            return {
              ...user,
              assigned_device_names: '',
              assigned_device_count: 0,
              assigned_beacon_names: '',
              assigned_beacon_count: 0,
              enabled_features: '',
            };
          }
        })
      );
      
      // Define all headers grouped by tab
      const headers = [
        // Account Tab
        'account_reference',
        'username', 
        'full_name',
        'email',
        'role',
        'is_active',
        'expiry_enabled',
        'expiry_date',
        'created_at',
        'last_login',
        
        // Relationships
        'managed_by_names',
        'parent_user_name',
        
        // Contact Tab
        'company',
        'phone',
        'mobile',
        'address_street',
        'address_city',
        'address_postcode',
        'address_state',
        'address_country',
        'notes',
        
        // Privileges Tab (importable round-trip)
        'account_name',
        'history_retention_days',
        'max_devices',
        'max_beacons',
        'max_drivers',
        'max_staff',
        'max_passengers',
        'max_end_users',
        'max_sub_accounts',
        'enabled_features',
        
        // Devices & Beacons Tab (importable round-trip)
        'assigned_device_count',
        'assigned_device_names',
        'assigned_beacon_count',
        'assigned_beacon_names',
      ];
      
      // Create header row with friendly names
      // Fields marked (Read Only) are for reference only and will NOT be imported
      const headerNames = {
        // Importable fields
        'account_reference': 'Account Reference',
        'username': 'Username',
        'full_name': 'Full Name',
        'email': 'Email',
        'role': 'Role',
        'is_active': 'Status',
        'expiry_enabled': 'Expiry Enabled',
        'expiry_date': 'Expiry Date',
        'company': 'Company',
        'phone': 'Phone',
        'mobile': 'Mobile',
        'address_street': 'Street Address',
        'address_city': 'City',
        'address_postcode': 'Postcode',
        'address_state': 'State',
        'address_country': 'Country',
        'notes': 'Notes',
        
        // Read-only fields (not imported - for reference only)
        'created_at': 'Created At (Read Only)',
        'last_login': 'Last Login (Read Only)',
        'managed_by_names': 'Managed By (Read Only)',
        'parent_user_name': 'Parent User (Read Only)',
        'account_name': 'Account Name (Read Only)',
        
        // Importable Privileges / Devices & Beacons (round-trip with the Import Wizard & Quick Import)
        'assigned_device_count': 'Assigned Devices Count',
        'assigned_beacon_count': 'Assigned Beacons Count',
        'history_retention_days': 'History Retention Days',
        'max_devices': 'Max Devices',
        'max_beacons': 'Max Beacons',
        'max_drivers': 'Max Drivers',
        'max_staff': 'Max Staff',
        'max_passengers': 'Max Passengers',
        'max_end_users': 'Max End Users',
        'max_sub_accounts': 'Max Sub Accounts',
        'enabled_features': 'Enabled Features',
        'assigned_device_names': 'Assigned Device Names',
        'assigned_beacon_names': 'Assigned Beacon Names',
      };
      
      // Format values for display (raw strings — escaping happens per output format)
      const formatValue = (user, header) => {
        let value = user[header];
        
        // Special formatting
        if (header === 'is_active') {
          value = value === false ? 'Blocked' : 'Active';
        } else if (header === 'created_at' || header === 'last_login' || header === 'expiry_date') {
          // Human-readable, in the user's date/time format + timezone (Settings page).
          value = value ? formatDT(value, { seconds: true }) : '';
        } else if (header === 'expiry_enabled') {
          value = value === true ? 'Yes' : 'No';
        } else if (header === 'managed_by_names') {
          // Join array of manager names
          value = Array.isArray(value) ? value.join('; ') : (value || '');
        } else if (header === 'role') {
          const roleMap = {
            'super_admin': 'Super Admin',
            'admin': 'Administrator',
            'manager': 'Manager',
            'end_user': 'End User',
            'sub_account': 'Sub Account',
          };
          value = roleMap[value] || value;
        } else if (header === 'max_devices' || header === 'max_beacons' || header === 'max_drivers' || header === 'max_staff' || header === 'max_passengers' || header === 'max_end_users' || header === 'max_sub_accounts') {
          if (value === -1) value = 'Unlimited';
          else if (value === 0 || value === undefined || value === null) value = 'Not Set';
        }
        
        return value === null || value === undefined ? '' : String(value);
      };
      
      return { headers, headerNames, usersWithFeatures, rawValue: formatValue };
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export users');
      return null;
    }
  };

  // Columns that must survive Excel as text (leading zeros / long digits)
  const USERS_EXPORT_TEXT_COLS = ['phone', 'mobile', 'address_postcode', 'account_reference'];

  const handleExportCSV = async () => {
    toast.info('Preparing export with all user data...');
    const data = await buildUsersExportData();
    if (!data) return;
    const { headers, headerNames, usersWithFeatures, rawValue } = data;
    const cell = (user, h) => {
      const s = rawValue(user, h);
      if (USERS_EXPORT_TEXT_COLS.includes(h) && s) return `="${s.replace(/"/g, '""')}"`;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csvContent = [
      headers.map(h => headerNames[h] || h).join(','),
      ...usersWithFeatures.map(user => headers.map(h => cell(user, h)).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // 'full' only when the selection covers every user; a subset is just 'users_export'
    const prefix = selectedUserIds.length >= totalCount ? 'users_full_export' : 'users_export';
    const fname = `${prefix}_${getFileTimestamp()}.csv`;
    a.download = fname;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${usersWithFeatures.length} user(s) with all data`);
    logUserIo({ kind: 'export', source: 'export_menu', export_type: 'users', file_type: 'csv', file_name: fname, row_count: usersWithFeatures.length });
  };

  const handleExportXlsx = async () => {
    toast.info('Preparing Excel-safe export...');
    const data = await buildUsersExportData();
    if (!data) return;
    const { headers, headerNames, usersWithFeatures, rawValue } = data;
    const aoa = [
      headers.map(h => headerNames[h] || h),
      ...usersWithFeatures.map(user => headers.map(h => rawValue(user, h))),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = headers.map(h => ({ wch: Math.min(Math.max((headerNames[h] || h).length + 2, 14), 30) }));
    const textIdx = headers.map((h, i) => (USERS_EXPORT_TEXT_COLS.includes(h) ? i : -1)).filter(i => i >= 0);
    for (let r = 1; r < aoa.length; r++) {
      textIdx.forEach(c => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '', z: '@' };
        else { ws[addr].t = 's'; ws[addr].z = '@'; ws[addr].v = String(ws[addr].v ?? ''); }
      });
    }
    ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    const prefix = selectedUserIds.length >= totalCount ? 'users_full_export' : 'users_export';
    const fname = `${prefix}_${getFileTimestamp()}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported ${usersWithFeatures.length} user(s) with all data`);
    logUserIo({ kind: 'export', source: 'export_menu', export_type: 'users', file_type: 'xlsx', file_name: fname, row_count: usersWithFeatures.length });
  };

  const handleToggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleToggleAllUsers = () => {
    // Header checkbox toggles ONLY the rows on the current page. Cross-page
    // "Select All" lives on the selection-bar button below the toolbar.
    const pageIds = filteredUsers.map(u => u.id);
    setSelectedUserIds((prev) => {
      const allPageSelected = pageIds.length > 0 && pageIds.every(id => prev.includes(id));
      return allPageSelected ? prev.filter(id => !pageIds.includes(id))
                             : Array.from(new Set([...prev, ...pageIds]));
    });
  };

  const getInitials = (name) => {
    return (name || '').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Display name preference: show company name (with personal name as subtitle) when chosen.
  const displayName = (u) => (u?.display_name_preference === 'company' && (u?.company || '').trim())
    ? u.company.trim()
    : (u?.full_name || u?.email || '');
  const displaySubtitle = (u) => (u?.display_name_preference === 'company' && (u?.company || '').trim() && u?.full_name)
    ? u.full_name
    : null;

  // Format last connection time
  const formatLastConnection = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 5) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Filter devices based on search
  const filteredAssignedDevices = resolvedAssignedDevices.filter(device => {
    if (!assignedDeviceSearchQuery) return true;
    const query = assignedDeviceSearchQuery.toLowerCase();
    return (
      device.name?.toLowerCase().includes(query) ||
      device.imei?.includes(query) ||
      device.beacon_id?.toLowerCase().includes(query) ||
      device.registration?.toLowerCase().includes(query) ||
      device.protocol?.toLowerCase().includes(query)
    );
  }).sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  // Page the assigned list in-memory (auto-clamps when rows are unassigned on the last page)
  const assignedDevicesEffectivePage = Math.min(assignedDevicesPage, Math.max(1, Math.ceil(filteredAssignedDevices.length / assignedDevicesPageSize)));
  const pagedAssignedDevices = filteredAssignedDevices.slice(
    (assignedDevicesEffectivePage - 1) * assignedDevicesPageSize,
    assignedDevicesEffectivePage * assignedDevicesPageSize
  );

  const beaconMatchesSearch = (beacon, queryText) => {
    if (!queryText) return true;
    const query = queryText.toLowerCase();
    return (
      beacon.name?.toLowerCase().includes(query) ||
      beacon.imei?.toLowerCase().includes(query) ||
      beacon.mac_address?.toLowerCase().includes(query) ||
      beacon.serial_number?.toLowerCase().includes(query) ||
      beacon.beacon_id?.toLowerCase().includes(query) ||
      beacon.asset_type?.toLowerCase().includes(query) ||
      beacon.protocol?.toLowerCase().includes(query)
    );
  };

  const filteredAssignedBeacons = resolvedAssignedBeacons
    .filter(beacon => beaconMatchesSearch(beacon, assignedBeaconSearchQuery))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  const assignedBeaconsEffectivePage = Math.min(assignedBeaconsPage, Math.max(1, Math.ceil(filteredAssignedBeacons.length / assignedBeaconsPageSize)));
  const pagedAssignedBeacons = filteredAssignedBeacons.slice(
    (assignedBeaconsEffectivePage - 1) * assignedBeaconsPageSize,
    assignedBeaconsEffectivePage * assignedBeaconsPageSize
  );

  // Remove all assigned devices, preserving placeholder rows (devices managed by another
  // admin / outside this admin's pool) so out-of-scope allocations are never wiped.
  const handleClearAssignedDevices = () => {
    setAssignedDevices(prev => prev.filter(d => d._placeholder));
  };

  const handleClearAssignedBeacons = () => {
    setAssignedBeacons(prev => prev.filter(b => b._placeholder));
  };

  // Group Sub Accounts by parent user ID for nested display
  const subAccountsByParent = users
    .filter(u => u.role === 'sub_account' && u.parent_user_id)
    .reduce((acc, subAccount) => {
      const parentId = subAccount.parent_user_id;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(subAccount);
      return acc;
    }, {});

  // Get End User IDs that have Sub Accounts (for the Sub Account filter)
  const endUsersWithSubAccounts = new Set(Object.keys(subAccountsByParent)); // eslint-disable-line no-unused-vars

  const filteredUsers = users.filter(user => {
    // Hide Sub Accounts from main list - they'll be shown nested under their parent.
    // Backend already excludes sub_account rows for the top-level page (via
    // `include_sub_accounts=false`), but we still filter here defensively so the nested
    // sub-account rows returned for parent lookup don't appear as top-level.
    if (user.role === 'sub_account') {
      return false;
    }

    // All other filters (search, role, has_sub_accounts, invite-pending) are applied
    // SERVER-SIDE now, so the visible row count matches the "Rows per page" selector.
    return true;
  }).sort((a, b) => {
    // Pin role groups to the top in this order: super_admin -> admin -> manager,
    // then everyone else. Within each group the active column sort applies
    // (defaults to name A-Z = alphabetical).
    const rolePriority = { super_admin: 0, admin: 1, manager: 2 };
    const aPri = rolePriority[a.role] ?? 3;
    const bPri = rolePriority[b.role] ?? 3;
    if (aPri !== bPri) return aPri - bPri;

    const dir = userSortDir === 'asc' ? 1 : -1;
    let av, bv;
    switch (userSortField) {
      case 'full_name':
        av = displayName(a).toLowerCase();
        bv = displayName(b).toLowerCase();
        break;
      case 'username':
        av = (a.username || a.email?.split('@')[0] || '').toLowerCase();
        bv = (b.username || b.email?.split('@')[0] || '').toLowerCase();
        break;
      case 'company':
        av = (a.company || '').toLowerCase();
        bv = (b.company || '').toLowerCase();
        break;
      case 'created_by_label':
        av = (a.created_by_label || '').toLowerCase();
        bv = (b.created_by_label || '').toLowerCase();
        break;
      case 'status':
        av = a.is_active === false ? 'blocked' : 'active';
        bv = b.is_active === false ? 'blocked' : 'active';
        break;
      default:
        av = displayName(a).toLowerCase();
        bv = displayName(b).toLowerCase();
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });

  const handleUserSort = (field) => {
    if (userSortField === field) {
      setUserSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortField(field);
      setUserSortDir('asc');
    }
  };

  const landingPageOptions = getAllowedLandingPageOptions(formData.enabled_pages);

  // Only show the full-page spinner on the FIRST load. Any subsequent refetch
  // triggered by search/filters keeps the page (and search input) mounted so
  // the input never loses focus and the layout doesn't flash — even when the
  // current query filters the list down to 0 rows.
  if (!initialLoadDone) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="hidden lg:flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <Users className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">User Management</h1>
            <p className="text-sm text-muted-foreground">Manage user accounts, permissions, and device assignments</p>
          </div>
        </div>
        {/* Actions — 2×2 full-width grid on phones, inline row on ≥sm. */}
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <input type="file" accept=".csv,.xlsx" onChange={handleImportUsers} className="hidden" id="user-csv-import" />
          {canViewIoLog && (
          <Button variant="outline" size="sm" onClick={openUserIoLog} data-testid="user-io-log-button">
            <History className="w-4 h-4 mr-2" />
            Import / Export Log
          </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="import-users-dropdown-trigger">
                <Download className="w-4 h-4 mr-2" />
                Import
                <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">Import options</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => setIsImportWizardOpen(true)}
                data-testid="import-users-open-wizard"
              >
                <Sparkles className="w-4 h-4 mr-2 text-[#2563eb]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">Import Wizard</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">Preview, edit &amp; validate before import</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => document.getElementById('user-csv-import').click()}
                data-testid="import-users-quick-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">Quick CSV Import</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">One-shot loader — skips existing users</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Templates</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); downloadUserImportTemplate(true); }}
                data-testid="import-users-template-example"
              >
                <FileText className="w-4 h-4 mr-2" />
                User Example Row (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); downloadUserImportTemplateXlsx(true); }}
                data-testid="import-users-template-xlsx-example"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                User Excel-Safe Example (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-users-dropdown-trigger" disabled={selectedUserIds.length === 0}>
                <Upload className="w-4 h-4 mr-2" />
                {selectedUserIds.length > 0 ? `Export (${selectedUserIds.length})` : 'Export'}
                <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">Export for Re-import</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={handleExportCSV}
                data-testid="export-users-csv"
              >
                <FileText className="w-4 h-4 mr-2" />
                Users (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleExportXlsx}
                data-testid="export-users-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                Users Excel-Safe (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {currentUser?.role === 'super_admin' && !isMobileOrWebview && (
          <Button
            variant="destructive"
            size="sm"
            disabled={selectedUserIds.length === 0}
            onClick={() => { setBulkDeleteText(''); setBulkDeleteOpen(true); }}
            data-testid="bulk-delete-users-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {selectedUserIds.length > 0 ? `Delete (${selectedUserIds.length})` : 'Delete'}
          </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBannerDialogOpen(true)}
            data-testid="send-banner-button"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            {selectedUserIds.length > 0 ? `Banner (${selectedUserIds.length})` : 'Banner'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchUsers}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" data-testid="add-user-button" className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[160px] justify-center" onClick={() => { resetForm(); setIsAddDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Import Undo banner — shown after a successful import run (24h window) */}
      {lastImportRun && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-4 py-3"
          data-testid="import-undo-banner"
        >
          <Undo2 className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-medium text-foreground" data-testid="import-undo-banner-summary">
              Import completed — {lastImportRun.summary?.created ?? lastImportRun.created_count ?? 0} created, {lastImportRun.summary?.updated ?? lastImportRun.updated_count ?? 0} updated{(lastImportRun.summary?.skipped ?? 0) > 0 ? `, ${lastImportRun.summary.skipped} skipped (already exist)` : ''}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="import-undo-banner-expiry">
              Undo available until {lastImportRun.expires_at ? new Date(lastImportRun.expires_at).toLocaleString() : '—'} — removes created users and restores updated users to their pre-import values
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndoImport}
            disabled={undoingImport}
            className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950"
            data-testid="import-undo-button"
          >
            {undoingImport ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
            {undoingImport ? 'Undoing…' : 'Undo import'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setLastImportRun(null)}
            disabled={undoingImport}
            data-testid="import-undo-dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Invite Reminder Nudge — pending welcome invitations older than 3 days */}
      {!staleInvitesDismissed && staleInvites.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-3"
          data-testid="invite-nudge-banner"
        >
          <Mail className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-medium text-foreground" data-testid="invite-nudge-summary">
              {staleInvites.length} user{staleInvites.length === 1 ? ' hasn\'t' : 's haven\'t'} activated their welcome email (sent {staleInviteDays}+ day{staleInviteDays === 1 ? '' : 's'} ago)
            </p>
            <p className="text-xs text-muted-foreground truncate" data-testid="invite-nudge-emails">
              {staleInvites.slice(0, 3).map(u => u.email).join(', ')}{staleInvites.length > 3 ? ` +${staleInvites.length - 3} more` : ''}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResendStaleInvites}
            disabled={resendingInvites}
            className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-950"
            data-testid="invite-nudge-resend-button"
          >
            {resendingInvites ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            {resendingInvites ? 'Resending…' : `Resend ${staleInvites.length} invite${staleInvites.length === 1 ? '' : 's'}`}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setStaleInvitesDismissed(true)}
            disabled={resendingInvites}
            data-testid="invite-nudge-dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Search and Filter — search input spans the full width per UI parity.
          Includes an [x] clear affordance. */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[240px]">
          {/* Decoy fields to prevent Chrome autofill on search input */}
          <input type="text" name="hidden-username" style={{ display: 'none' }} autoComplete="username" tabIndex={-1} />
          <input type="password" name="hidden-password" style={{ display: 'none' }} autoComplete="current-password" tabIndex={-1} />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search users by name, email, username, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-10 pr-9 dark:bg-[#0D1425]"
            data-testid="user-search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-lpignore="true"
            data-form-type="other"
            name="user-search-field"
          />
          {searchQuery && (
            <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="user-search-clear">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Role Filter */}
        <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v === '__all__' ? 'all' : v)}>
          <SelectTrigger className="h-8 w-[180px] dark:bg-[#0D1425]" data-testid="role-filter">
            <SelectValue>
              {roleFilter === 'all' ? <span className="text-muted-foreground">All Roles</span> : userRoles.find(r => r.value === roleFilter)?.label || roleFilter}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Roles</SelectItem>
            {userRoles
              .filter(role => {
                // Super Admin sees all roles
                if (currentUser?.role === 'super_admin') return true;
                // Admin sees Manager, End User, Sub Account
                if (currentUser?.role === 'admin') {
                  return ['manager', 'end_user', 'sub_account'].includes(role.value);
                }
                // Manager sees End User, Sub Account
                if (currentUser?.role === 'manager') {
                  return ['end_user', 'sub_account'].includes(role.value);
                }
                return false;
              })
              .map(role => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {/* Status Filter — Active / Blocked */}
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === '__all__' ? 'all' : v)}>
          <SelectTrigger className="h-8 w-[160px] dark:bg-[#0D1425]" data-testid="user-status-filter">
            <SelectValue>
              {statusFilter === 'all' ? <span className="text-muted-foreground">All Statuses</span> : (statusFilter === 'active' ? 'Active' : 'Blocked')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="user-status-filter-all">All Statuses</SelectItem>
            <SelectItem value="active" data-testid="user-status-filter-active">Active</SelectItem>
            <SelectItem value="blocked" data-testid="user-status-filter-blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('');
              setRoleFilter('all');
              setStatusFilter('all');
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Bulk Actions — always-visible selection bar (app-wide standard) */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="user-mgmt-selection-bar">
        <span data-testid="user-selection-count">{selectedUserIds.length} selected</span>
        {selectedUserIds.length > 0 && (<>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
          disabled={selectAllAcrossLoading}
          onClick={async () => {
            if (selectedUserIds.length >= totalCount) { setSelectedUserIds([]); return; }
            try {
              setSelectAllAcrossLoading(true);
              const params = new URLSearchParams();
              params.set('skip', '0');
              params.set('limit', String(Math.max(totalCount, 1)));
              if (searchQuery && searchQuery.trim()) params.set('search', searchQuery.trim());
              if (roleFilter && roleFilter !== 'all' && roleFilter !== 'sub_account') params.set('role_filter', roleFilter);
              if (statusFilter === 'active' || statusFilter === 'blocked') params.set('status_filter', statusFilter);
              if (invitePendingOnly) params.set('invite_pending_only', 'true');
              const res = await api.get(`/users?${params.toString()}`);
              // Backend appends nested sub_accounts to the response list for
              // UI nesting purposes, but they aren't top-level rows and must
              // not be counted in "Select All". Sub Accounts are only reached
              // via the parent End User's "Login As" flow.
              const allIds = (Array.isArray(res.data) ? res.data : [])
                .filter(u => u.role !== 'sub_account')
                .map(u => u.id)
                .filter(Boolean);
              setSelectedUserIds(allIds);
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to select all users');
            } finally {
              setSelectAllAcrossLoading(false);
            }
          }}
          data-testid="user-select-all-toggle"
        >
          {selectAllAcrossLoading ? 'Selecting…' : (selectedUserIds.length >= totalCount ? 'Deselect All' : 'Select All')}
        </Button>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedUserIds([])} data-testid="user-selection-clear">Clear</Button>
        </>)}
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap py-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <CardTitle data-testid="user-management-list-title">
              Users ({totalCount > filteredUsers.length ? `${filteredUsers.length} shown of ${totalCount}` : filteredUsers.length})
            </CardTitle>
            {(() => {
              const order = [
                ['super_admin', 'Super Admin', 'Super Admins'],
                ['admin', 'Admin', 'Admins'],
                ['manager', 'Manager', 'Managers'],
                ['end_user', 'End User', 'End Users'],
              ];
              // Use cross-page role counts so the summary stays accurate when
              // paginated. Falls back to current-page counts on first render
              // before the lightweight role-count fetch resolves.
              const pageCounts = filteredUsers.reduce((acc, u) => {
                acc[u.role] = (acc[u.role] || 0) + 1;
                return acc;
              }, {});
              const counts = Object.keys(totalRoleCounts).length > 0 ? totalRoleCounts : pageCounts;
              const parts = order
                .filter(([role]) => counts[role] > 0)
                .map(([role, sing, plur]) => `${counts[role]} ${counts[role] === 1 ? sing : plur}`);
              if (parts.length === 0) return null;
              return (
                <span className="text-xs text-muted-foreground" data-testid="user-mgmt-role-summary">
                  {parts.join(' · ')}
                </span>
              );
            })()}
            {(() => {
              // Cross-page pending count so the badge stays visible even when
              // the pending-invite rows sit on another page.
              const pendingCount = totalPendingCount;
              if (pendingCount === 0 && !invitePendingOnly) return null;
              return (
                <button
                  type="button"
                  onClick={() => setInvitePendingOnly(v => !v)}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-0.5 transition-colors ${invitePendingOnly ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50'}`}
                  title={invitePendingOnly ? 'Showing pending invitations — click to clear' : 'Show only pending invitations'}
                  data-testid="pending-invites-stat"
                >
                  <Mail className="w-3.5 h-3.5" />
                  {pendingCount} pending invitation{pendingCount === 1 ? '' : 's'}
                  {invitePendingOnly && <X className="w-3 h-3 ml-0.5" />}
                </button>
              );
            })()}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <SyncedTableScroll minWidth="1600px" testId="user-management-table-scroll">
          <Table className="table-fixed" wrapperClassName="overflow-visible">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 sticky top-0 left-0 bg-slate-100 dark:bg-slate-900 z-30" data-testid="user-mgmt-header-checkbox">
                  <Checkbox
                    checked={filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.includes(u.id))}
                    onCheckedChange={handleToggleAllUsers}
                  />
                </TableHead>
                <SortableTableHead className="w-64 sticky top-0 left-12 bg-slate-100 dark:bg-slate-900 z-30 border-r border-border" field="full_name" currentSort={userSortField} currentDir={userSortDir} onSort={handleUserSort}>User</SortableTableHead>
                <SortableTableHead className="w-40 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="username" currentSort={userSortField} currentDir={userSortDir} onSort={handleUserSort}>Username</SortableTableHead>
                <TableHead className="w-52 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Contact</TableHead>
                <SortableTableHead className="w-44 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="company" currentSort={userSortField} currentDir={userSortDir} onSort={handleUserSort}>Company</SortableTableHead>
                <TableHead className="w-36 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Role</TableHead>
                <SortableTableHead className="w-44 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="created_by_label" currentSort={userSortField} currentDir={userSortDir} onSort={handleUserSort}>Created By</SortableTableHead>
                <TableHead className="w-20 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Devices</TableHead>
                <TableHead className="w-20 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Beacons</TableHead>
                <SortableTableHead className="w-36 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="status" currentSort={userSortField} currentDir={userSortDir} onSort={handleUserSort}>Status</SortableTableHead>
                <TableHead className="w-32 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => {
                const roleInfo = userRoles.find(r => r.value === user.role);
                const deviceCount = user.assigned_device_ids?.length || 0;
                const maxDevices = user.max_devices ?? 0;
                const deviceLabel = maxDevices === -1 ? `${deviceCount}/∞` : `${deviceCount}/${maxDevices}`;
                const beaconCount = user.assigned_beacon_ids?.length || user.beacon_count || 0;
                const maxBeacons = user.max_beacons ?? 0;
                const beaconLabel = maxBeacons === -1 ? `${beaconCount}/∞` : `${beaconCount}/${maxBeacons}`;
                const isSuperAdmin = user.role === 'super_admin';
                const isSelfRow = user.id === currentUser?.id;
                return (
                  <TableRow key={user.id} className="group cursor-pointer hover:bg-muted/50">
                    <TableCell onClick={(e) => e.stopPropagation()} className="sticky left-0 sticky-cell z-10">
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onCheckedChange={() => handleToggleUserSelection(user.id)}
                      />
                    </TableCell>
                    <TableCell className="sticky left-12 sticky-cell z-10 border-r border-border">
                      <div className="flex items-center gap-3">
                        <div
                          onClick={() => openEditDialog(user)}
                          className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                        >
                          <Avatar>
                            <AvatarFallback className={`${roleInfo?.color || 'bg-slate-500 text-white'}`}>
                              {getInitials(displayName(user))}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium truncate">{displayName(user)}</span>
                            {displaySubtitle(user) && (
                              <span className="text-xs text-muted-foreground truncate" data-testid={`user-display-subtitle-${user.id}`}>
                                {displaySubtitle(user)}
                              </span>
                            )}
                            {/* Sub Accounts & People count badges — billing tracking */}
                            {((user.sub_accounts_count > 0) || (user.people_count > 0)) && (
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                {user.sub_accounts_count > 0 && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setSubAccountsViewer(user); }}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/70 transition-colors cursor-pointer"
                                    title={`View ${user.sub_accounts_count} Sub Account${user.sub_accounts_count === 1 ? '' : 's'}`}
                                    data-testid={`sub-accounts-count-badge-${user.id}`}
                                  >
                                    <User className="w-3 h-3" />
                                    {user.sub_accounts_count} Sub Account{user.sub_accounts_count === 1 ? '' : 's'}
                                  </button>
                                )}
                                {user.people_count > 0 && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                                    title={`Drivers: ${user.people_breakdown?.drivers || 0} · Staff: ${user.people_breakdown?.staff || 0} · Passengers: ${user.people_breakdown?.passengers || 0}`}
                                    data-testid={`people-count-badge-${user.id}`}
                                  >
                                    <Users className="w-3 h-3" />
                                    {user.people_count} {user.people_count === 1 ? 'Person' : 'People'}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Search matched via a sub account — parent surfaced with hint */}
                            {user.matched_sub_accounts?.length > 0 && (
                              <span
                                className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 w-fit"
                                title={user.matched_sub_accounts.map(s => s.email || s.full_name).join(', ')}
                                data-testid={`sub-account-match-hint-${user.id}`}
                              >
                                <Search className="w-3 h-3" />
                                matches sub account: {user.matched_sub_accounts[0].email || user.matched_sub_accounts[0].full_name}
                                {user.matched_sub_accounts.length > 1 ? ` +${user.matched_sub_accounts.length - 1}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Inline Login As button — keeps action visible without scrolling right */}
                        {user.is_active !== false && !isAccountExpired(user) && !isSelfRow && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoginAsUser(user);
                            }}
                            title={`Login as ${displayName(user)}`}
                            className="h-8 w-8 p-0 shrink-0 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-950 dark:hover:border-blue-700"
                            data-testid="login-as-user-btn"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      <span className="block max-w-[180px] truncate" title={user.username || user.email.split('@')[0]}>
                        {user.username || user.email.split('@')[0]}
                      </span>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs">
                          <Mail className="w-3 h-3 text-muted-foreground" />
                          <span>{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="flex items-center gap-1 text-xs">
                            <Phone className="w-3 h-3 text-muted-foreground" />
                            <span>{user.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      {user.company && (
                        <div className="flex items-center gap-1">
                          <Building className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm">{user.company}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      <div className="space-y-1">
                        <Badge className={roleInfo?.color}>{roleInfo?.label || user.role}</Badge>
                        {/* Show all managers if multiple, otherwise single manager - HIDDEN FOR MANAGERS */}
                        {currentUser?.role !== 'manager' && user.managed_by_names && user.managed_by_names.length > 0 && (
                          <div className="flex flex-col gap-0.5">
                            {user.managed_by_names.map((managerName, idx) => {
                              const managerId = user.managed_by_user_ids?.[idx];
                              const managerUser = users.find(u => u.id === managerId);
                              const managerLabel = managerUser ? displayName(managerUser) : managerName;
                              return (
                              <div 
                                key={managerId || idx}
                                className="flex items-center gap-1 text-xs text-blue-600/70 cursor-pointer hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (managerUser) openEditDialog(managerUser);
                                }}
                                title={`Managed by ${managerLabel}`}
                              >
                                <Users className="w-3 h-3" />
                                <span>{managerLabel}</span>
                              </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Fallback for old data with single manager - HIDDEN FOR MANAGERS */}
                        {currentUser?.role !== 'manager' && (!user.managed_by_names || user.managed_by_names.length === 0) && user.managed_by_name && (
                          <div 
                            className="flex items-center gap-1 text-xs text-yellow-600 cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              const manager = users.find(u => u.id === user.managed_by_user_id);
                              if (manager) openEditDialog(manager);
                            }}
                            title={`Managed by ${user.managed_by_name}`}
                          >
                            <Users className="w-3 h-3" />
                            <span>{user.managed_by_name}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)} data-testid={`user-created-by-${user.id}`}>
                      <div className="flex flex-col gap-0.5">
                        <Badge variant="outline" className="w-fit bg-slate-50 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
                          {user.created_by_label || 'Unknown'}
                        </Badge>
                        {user.created_by_email && (
                          <span className="text-xs text-muted-foreground truncate max-w-[160px]" title={user.created_by_email}>
                            {user.created_by_email}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      <Badge
                        variant="outline"
                        className={`gap-1 font-medium ${usageBadgeClass(deviceCount, maxDevices)}`}
                        title={`${deviceCount} device${deviceCount === 1 ? '' : 's'} assigned of ${maxDevices === -1 ? 'unlimited' : maxDevices} allowed`}
                        data-testid={`device-usage-badge-${user.id}`}
                      >
                        <HardDrive className="w-3 h-3" />
                        {deviceLabel}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      <Badge
                        variant="outline"
                        className={`gap-1 font-medium ${usageBadgeClass(beaconCount, maxBeacons)}`}
                        title={`${beaconCount} beacon${beaconCount === 1 ? '' : 's'} assigned of ${maxBeacons === -1 ? 'unlimited' : maxBeacons} allowed`}
                        data-testid={`beacon-usage-badge-${user.id}`}
                      >
                        <Radio className="w-3 h-3" />
                        {beaconLabel}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={() => openEditDialog(user)}>
                      {(() => {
                        const expired = isAccountExpired(user);
                        const isBlocked = user.is_active === false || expired;
                        return (
                          <div className="flex flex-col items-start gap-1">
                            <Badge 
                              variant="outline" 
                              className={`${isBlocked ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'} ${(!isSuperAdmin && !isSelfRow) ? 'cursor-pointer hover:opacity-80' : ''}`}
                              title={(!isSuperAdmin && !isSelfRow) ? (isBlocked ? 'Click to open the Reactivation Wizard' : 'Click to open the Deactivation Wizard') : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSuperAdmin || isSelfRow) return;
                                if (!isBlocked) {
                                  setDeactivateWizardUser(user);
                                } else {
                                  // Reactivate — open wizard
                                  setReactivateWizardUser(user);
                                }
                              }}
                            >
                              {isBlocked ? 'Blocked' : 'Active'}
                            </Badge>
                            {expired && user.is_active !== false && (
                              <span className="text-xs text-orange-600 dark:text-orange-400" title="Account expired - pending backend sync">
                                (Expired)
                              </span>
                            )}
                            {user.invite_status === 'pending_invitation' && (
                              <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 gap-1" title="Awaiting account activation" data-testid={`invite-pending-${user.id}`}>
                                <Mail className="w-3 h-3" /> Pending invite
                              </Badge>
                            )}
                            {user.invite_status === 'expired' && (
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 gap-1" title="Activation link expired" data-testid={`invite-expired-${user.id}`}>
                                <Mail className="w-3 h-3" /> Invite expired
                              </Badge>
                            )}
                            {user.invite_status === 'cancelled' && (
                              <Badge variant="outline" className="bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400 gap-1" title="Invitation cancelled - activation link revoked" data-testid={`invite-cancelled-${user.id}`}>
                                <XCircle className="w-3 h-3" /> Invite cancelled
                              </Badge>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {/* Edit User button */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEditDialog(user)}
                          title="Edit"
                          className="h-8 w-8 p-0"
                          data-testid="edit-user-btn"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {/* Deactivate / Reactivate wizard shortcut — hidden for Super Admin rows and your own row */}
                        {!isSuperAdmin && !isSelfRow && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => (user.is_active === false || isAccountExpired(user)) ? setReactivateWizardUser(user) : setDeactivateWizardUser(user)}
                            title={(user.is_active === false || isAccountExpired(user)) ? 'Reactivate (opens Reactivation Wizard)' : 'Deactivate (opens Deactivation Wizard)'}
                            className="h-8 w-8 p-0"
                            data-testid={`user-power-btn-${user.id}`}
                          >
                            {(user.is_active === false || isAccountExpired(user)) ? <Power className="w-4 h-4 text-green-600" /> : <PowerOff className="w-4 h-4 text-amber-600" />}
                          </Button>
                        )}
                        {/* Delete User button - hidden for managers and for your own row */}
                        {!isSuperAdmin && !isSelfRow && currentUser?.role !== 'manager' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete"
                            className="h-8 w-8 p-0"
                            data-testid="delete-user-btn"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                        {isSuperAdmin && (
                          <Button size="sm" variant="outline" disabled title="Cannot delete Super Administrator" className="h-8 w-8 p-0">
                            <Lock className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </SyncedTableScroll>

          {/* Server-side pagination — uses the shared PaginationFooter so the
              UI matches every other paginated list in the app. */}
          <PaginationFooter
            page={pageIndex}
            setPage={setPageIndex}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            setItemsPerPage={setPageSize}
            testIdPrefix="user-mgmt"
            zeroIndexed
            disabled={loading}
            alwaysShow
          />
        </CardContent>
      </Card>


      {/* User Dialog - Multi-Tab (Add in create mode + Edit) */}
      <Dialog open={isEditDialogOpen || isAddDialogOpen} onOpenChange={(open) => { if (!open) { isCreatingUser ? handleAddUserDialogClose() : handleUserEditDialogClose(); } }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col">
          {/* Fixed Header */}
          <div className="px-6 pt-6 pb-0">
            <DialogHeader>
              <DialogTitle>{isCreatingUser ? 'Add New User' : `Edit User: ${selectedUser ? displayName(selectedUser) : ''}`}</DialogTitle>
              <DialogDescription className="sr-only">{isCreatingUser ? 'Create a new user account' : 'Edit user account details'}</DialogDescription>
            </DialogHeader>
          </div>

          {/* Fixed Tab Navigation */}
          {/* Managers only see Account & Contact tabs; Admins/Super Admins see all tabs */}
          {(() => {
            const editTabs = [
              { value: 'account', label: 'Account', icon: <User className="w-4 h-4" /> },
              { value: 'contact', label: 'Contact', icon: <Building className="w-4 h-4" /> },
              ...(canSeePrivilegesTab ? [{ value: 'privileges', label: 'Privileges', icon: <Shield className="w-4 h-4" /> }] : []),
              ...(canSeeAllocationTabs ? [
                { value: 'devices', label: 'Devices', icon: <HardDrive className="w-4 h-4" /> },
                { value: 'beacons', label: 'Beacons', icon: <Radio className="w-4 h-4" /> },
                ...(((currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && selectedUser?.role === 'manager')
                  ? [{ value: 'allocated_users', label: 'Allocated Users', icon: <Users className="w-4 h-4" /> }] : []),
                ...(!isCreatingUser ? [{ value: 'audit', label: 'Audit', icon: <History className="w-4 h-4" /> }] : []),
              ] : []),
            ];
            return (
              <>
                {/* Mobile: dropdown tab picker (same pattern as main app nav) */}
                <MobileTabPicker tabs={editTabs} value={activeEditTab} onChange={setActiveEditTab} testId="edit-user-tab-picker" />
                {/* Desktop: horizontal tab strip */}
                <div className="hidden sm:flex gap-1 border-b px-6 pt-4 overflow-x-auto flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveEditTab('account')}
                  className={activeEditTab === 'account' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                >
                  <User className="w-4 h-4 mr-2" />
                  Account
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveEditTab('contact')}
                  className={activeEditTab === 'contact' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                >
                  <Building className="w-4 h-4 mr-2" />
                  Contact
                </Button>
                {/* Privileges tab - Super Admin (any user) or Admin editing ANOTHER user. Hidden when editing self. */}
                {canSeePrivilegesTab && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveEditTab('privileges')}
                    className={activeEditTab === 'privileges' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                    data-testid="edit-user-privileges-tab-button"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Privileges
                  </Button>
                )}
                {/* Devices / Beacons / Audit - Admins & Super Admins always; Managers when editing self.
                    Lets Admins & Managers self-allocate devices/beacons to their own operational pages. */}
                {canSeeAllocationTabs && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveEditTab('devices')}
                      className={activeEditTab === 'devices' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                      data-testid="edit-user-devices-tab-button"
                    >
                      <HardDrive className="w-4 h-4 mr-2" />
                      Devices
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveEditTab('beacons')}
                      className={activeEditTab === 'beacons' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                      data-testid="edit-user-beacons-tab-button"
                    >
                      <Radio className="w-4 h-4 mr-2" />
                      Beacons
                    </Button>
                    {/* Allocated Users tab - only when an Admin/Super Admin views a Manager account */}
                    {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && selectedUser?.role === 'manager' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveEditTab('allocated_users')}
                        className={activeEditTab === 'allocated_users' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Allocated Users
                      </Button>
                    )}
                    {!isCreatingUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveEditTab('audit')}
                      className={activeEditTab === 'audit' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
                      data-testid="edit-user-audit-tab-button"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Audit
                    </Button>
                    )}
                  </>
                )}
              </div>
              </>
            );
          })()}

          {/* Scrollable Tab Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* ACCOUNT — CREATE MODE (single-step Add User; mirrors old Quick Add fields) */}
            {activeEditTab === 'account' && isCreatingUser && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleInputChange('full_name', e.target.value)}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="johndoe"
                    />
                    <p className="text-xs text-muted-foreground">Leave empty to auto-generate from email</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john@example.com"
                      data-testid="create-user-email-input"
                    />
                    <EmailAvailabilityHint result={userEmailAvail} testid="create-user-email-availability" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password{formData.send_invite ? '' : ' *'}</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder={formData.send_invite ? 'Set by user via email' : 'Enter a strong password'}
                      disabled={formData.send_invite}
                      data-testid="create-user-password-input"
                    />
                    {!formData.send_invite && formData.password && (
                      <PasswordRequirements password={formData.password} className="mt-1" />
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <Input
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    placeholder="+61 4XX XXX XXX"
                    data-testid="create-user-mobile-input"
                  />
                  <p className="text-xs text-muted-foreground">Optional — required to send the invitation by SMS.</p>
                </div>

                <div className="p-3 rounded-lg border border-blue-100 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-900 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="pr-3">
                      <p className="font-medium text-sm flex items-center gap-1.5">
                        <Mail className="w-4 h-4 text-[#2563eb]" /> Send welcome invitation
                      </p>
                      <p className="text-xs text-muted-foreground">
                        The user receives a secure link to accept the Terms &amp; Privacy Policy and set their own password.
                      </p>
                    </div>
                    <Switch
                      checked={!!formData.send_invite}
                      onCheckedChange={(v) => handleInputChange('send_invite', v)}
                      data-testid="create-user-send-invite-toggle"
                    />
                  </div>
                  {formData.send_invite && (() => {
                    const emailInviteActive = !!((formData.email || '').trim()) && isNotificationChannelActive('notification_email');
                    const smsInviteActive = !!((formData.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
                    return (
                      <div className="space-y-1.5 pt-2 border-t border-blue-100 dark:border-blue-900" data-testid="create-user-invite-channels">
                        <span className="block text-xs text-muted-foreground">Deliver the invitation via (optional):</span>
                        <div className="flex items-center gap-5">
                          <label className={`flex items-center gap-2 text-sm ${emailInviteActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                            title={emailInviteActive ? '' : 'Enter an email above and enable your Email notification channel in Settings'}>
                            <Checkbox checked={!!formData.invite_email && emailInviteActive} disabled={!emailInviteActive}
                              onCheckedChange={(v) => handleInputChange('invite_email', !!v)} data-testid="create-user-invite-email" />
                            Email
                          </label>
                          <label className={`flex items-center gap-2 text-sm ${smsInviteActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                            title={smsInviteActive ? '' : 'Enter a mobile number above and enable your SMS notification channel in Settings'}>
                            <Checkbox checked={!!formData.invite_sms && smsInviteActive} disabled={!smsInviteActive}
                              onCheckedChange={(v) => handleInputChange('invite_sms', !!v)} data-testid="create-user-invite-sms" />
                            SMS
                          </label>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Tick a channel to deliver the link now, or leave both off and copy the link from the user&apos;s card afterwards.
                        </p>
                      </div>
                    );
                  })()}
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableRoles().map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Configure privileges, devices and beacons in the tabs above before creating.</p>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div>
                    <p className="font-medium text-sm">Active Account</p>
                    <p className="text-xs text-muted-foreground">User can access the platform</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => handleInputChange('is_active', v)}
                  />
                </div>
              </div>
            )}
            {/* ACCOUNT DETAILS TAB */}
            {activeEditTab === 'account' && !isCreatingUser && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Account Reference</Label>
                    <Input
                      value={formData.account_reference}
                      onChange={(e) => handleInputChange('account_reference', e.target.value)}
                      placeholder="ACC-12345"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50/70 dark:bg-slate-900/30" data-testid="edit-user-created-by-label">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-950/40 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Created by {selectedUser?.created_by_label || 'Unknown'}</p>
                      {selectedUser?.created_by_email && (
                        <p className="text-xs text-muted-foreground">{selectedUser.created_by_email}</p>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline">Creator</Badge>
                </div>

                {/* SECURITY: Super Admin cannot be blocked */}
                {selectedUser?.role === 'super_admin' ? (
                  <div className="flex items-center justify-between p-4 rounded-lg border-2 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/30">
                        <Shield className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">Super Administrator Status</p>
                        <p className="text-xs text-green-700 dark:text-green-400">
                          This account is always active and cannot be blocked
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-green-600">Always Active</Badge>
                      <Lock className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                ) : (
                  (() => {
                    // Check if account is expired based on form data
                    const isExpiredByDate = formData.expiry_enabled && formData.expiry_date && parseExpiryDate(formData.expiry_date) < new Date();
                    const effectiveStatus = isExpiredByDate ? 'blocked' : formData.account_status;
                    
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${isExpiredByDate ? 'border-orange-300 dark:border-orange-700 bg-orange-50/50 dark:bg-orange-900/10' : 'border-slate-200 dark:border-slate-700'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            effectiveStatus === 'active' 
                              ? 'bg-green-100 dark:bg-green-900/30' 
                              : 'bg-red-100 dark:bg-red-900/30'
                          }`}>
                            {effectiveStatus === 'active' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : isExpiredByDate ? (
                              <Clock className="w-5 h-5 text-orange-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">Account Status</p>
                            <p className="text-xs text-muted-foreground">
                              {isExpiredByDate 
                                ? 'Account expired - disable expiry to reactivate'
                                : effectiveStatus === 'active' 
                                  ? 'User can access the platform' 
                                  : 'User is blocked from logging in'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-medium ${
                            isExpiredByDate ? 'text-orange-600' : effectiveStatus === 'active' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {isExpiredByDate ? 'Expired' : effectiveStatus === 'active' ? 'Active' : 'Blocked'}
                          </span>
                          <Switch
                            checked={effectiveStatus === 'active'}
                            onCheckedChange={(checked) => setStatusTogglePrompt({ next: checked })}
                            disabled={isExpiredByDate}
                            title={isExpiredByDate ? 'Disable Account Expiry to manually control status' : undefined}
                            data-testid="user-account-status-switch"
                          />
                        </div>
                      </div>
                    );
                  })()
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username *</Label>
                    <Input
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="johndoe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john@example.com"
                      data-testid="edit-user-email-input"
                    />
                    <EmailAvailabilityHint result={userEmailAvail} testid="edit-user-email-availability" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Role
                      {isRoleLocked() && <Lock className="w-3 h-3 text-muted-foreground" />}
                    </Label>
                    {isRoleLocked() ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/50">
                          <Badge className={userRoles.find(r => r.value === formData.role)?.color}>
                            {userRoles.find(r => r.value === formData.role)?.label || formData.role}
                          </Badge>
                          <Lock className="w-4 h-4 text-muted-foreground ml-auto" />
                        </div>
                        <p className="text-xs text-muted-foreground">{getRoleLockReason()}</p>
                      </div>
                    ) : (
                      <Select value={formData.role} onValueChange={(v) => handleInputChange('role', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableRoles().map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  {/* Account Expiry - Hidden for Super Admin users */}
                  {selectedUser?.role !== 'super_admin' && (
                    <div className="space-y-2">
                      <Label>Account Expiry (Optional)</Label>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.expiry_enabled}
                          onCheckedChange={(v) => handleInputChange('expiry_enabled', v)}
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              disabled={!formData.expiry_enabled}
                              className={`w-[280px] justify-start text-left font-normal ${!formData.expiry_enabled ? 'opacity-50' : ''} ${!formData.expiry_date ? 'text-muted-foreground' : ''}`}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-slate-500 dark:text-slate-400" />
                              {formData.expiry_date 
                                ? format(parseExpiryDate(formData.expiry_date), "dd/MM/yyyy 'at' h:mm a")
                                : "Pick a date & time"
                              }
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.expiry_date ? parseExpiryDate(formData.expiry_date) : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  // Create a new date in local timezone
                                  const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                  
                                  // Preserve the time if already set, otherwise default to end of day (local time)
                                  const existingDate = formData.expiry_date ? parseExpiryDate(formData.expiry_date) : null;
                                  const hours = existingDate ? existingDate.getHours() : 23;
                                  const minutes = existingDate ? existingDate.getMinutes() : 59;
                                  localDate.setHours(hours, minutes, 59, 999);
                                  
                                  // Store as ISO string (UTC) for backend consistency
                                  handleInputChange('expiry_date', localDate.toISOString());
                                }
                              }}
                              disabled={(date) => {
                                // Use local time for the disabled check (user's perspective)
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const checkDate = new Date(date);
                                checkDate.setHours(0, 0, 0, 0);
                                return checkDate < today;
                              }}
                              initialFocus
                            />
                            <div className="p-3 border-t border-border">
                              <Label className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Clock className="w-3 h-3" />
                                Time (Local)
                              </Label>
                              <Input
                                type="time"
                                value={formData.expiry_date ? parseExpiryDate(formData.expiry_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '23:59'}
                                onChange={(e) => {
                                  if (formData.expiry_date) {
                                    const [hours, minutes] = e.target.value.split(':');
                                    const date = parseExpiryDate(formData.expiry_date);
                                    date.setHours(parseInt(hours), parseInt(minutes), 59, 999);
                                    handleInputChange('expiry_date', date.toISOString());
                                  }
                                }}
                                className="w-full dark:[color-scheme:dark]"
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formData.expiry_enabled 
                          ? `Account will be automatically blocked after this date/time (${Intl.DateTimeFormat().resolvedOptions().timeZone})` 
                          : 'Enable to set an automatic account expiry'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Account Activation / Welcome Invitation */}
                {selectedUser?.role !== 'super_admin' && (
                  <div className="pt-4 border-t" data-testid="account-activation-section">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Account Activation
                    </h3>
                    {(selectedUser?.invite_accepted_at || selectedUser?.invite_status === 'active' || !selectedUser?.invite_status) ? (
                      <div className="space-y-3" data-testid="login-details-section">
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg p-3" data-testid="invite-accepted-info">
                          <CheckCircle className="w-4 h-4" />
                          <span>{selectedUser?.invite_accepted_at ? `Activated by the user on ${formatDT(selectedUser.invite_accepted_at)}.` : selectedUser?.invite_status === 'active' ? 'Activated account.' : 'Active account (created before welcome invitations existed).'}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Need to resend their login details? This sends the app address, their username and a secure "set a new password" link (valid 24 hours) so they can log back in.
                        </p>
                        {(() => {
                          const emailActive = !!((selectedUser?.email || '').trim()) && isNotificationChannelActive('notification_email');
                          const smsActive = !!((selectedUser?.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
                          return (
                            <div className="flex items-center gap-5" data-testid="login-details-channels">
                              <span className="text-xs text-muted-foreground">Send via:</span>
                              <label className={`flex items-center gap-2 text-sm ${emailActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={emailActive ? '' : 'Enable your Email notification channel in Settings'}>
                                <Checkbox checked={loginDetailsEmail && emailActive} disabled={!emailActive}
                                  onCheckedChange={(v) => setLoginDetailsEmail(!!v)} data-testid="login-details-email" />
                                Email
                              </label>
                              <label className={`flex items-center gap-2 text-sm ${smsActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={smsActive ? '' : 'Add a mobile number (Contact tab) and enable your SMS notification channel in Settings'}>
                                <Checkbox checked={loginDetailsSms && smsActive} disabled={!smsActive}
                                  onCheckedChange={(v) => setLoginDetailsSms(!!v)} data-testid="login-details-sms" />
                                SMS
                              </label>
                            </div>
                          );
                        })()}
                        <Button
                          variant="outline"
                          onClick={handleSendLoginDetails}
                          disabled={sendingLoginDetails}
                          data-testid="send-login-details-btn"
                        >
                          {sendingLoginDetails ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                          Send Login Details
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground mb-3">
                          {selectedUser?.invite_status === 'cancelled'
                            ? 'This invitation was cancelled and the activation link no longer works. Correct the email/mobile above if it was sent to the wrong person, then resend a fresh invitation (valid 48 hours).'
                            : `This user has ${selectedUser?.invite_status === 'expired' ? 'an expired' : 'a pending'} welcome invitation and has not set a password yet. Resend it by email and/or SMS, or copy a fresh activation link (valid 48 hours).`}
                        </p>
                        {selectedUser?.invite_last_sent_at && (
                          <p className="text-xs text-muted-foreground -mt-1 mb-3 flex items-center gap-1.5" data-testid="last-invite-summary">
                            <Clock className="w-3 h-3 shrink-0" />
                            Last invite: {(selectedUser.invite_last_channels && selectedUser.invite_last_channels.length)
                              ? `sent via ${selectedUser.invite_last_channels.map(c => (c === 'sms' ? 'SMS' : 'Email')).join(' & ')}`
                              : 'link generated'} · {formatDT(selectedUser.invite_last_sent_at)}
                          </p>
                        )}
                        {(() => {
                          const emailActive = !!((selectedUser?.email || '').trim()) && isNotificationChannelActive('notification_email');
                          const smsActive = !!((selectedUser?.mobile || '').trim()) && isNotificationChannelActive('notification_sms');
                          return (
                            <div className="flex items-center gap-5 mb-3" data-testid="resend-invite-channels">
                              <span className="text-xs text-muted-foreground">Send via:</span>
                              <label className={`flex items-center gap-2 text-sm ${emailActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={emailActive ? '' : 'Enable your Email notification channel in Settings'}>
                                <Checkbox checked={resendInviteEmail && emailActive} disabled={!emailActive}
                                  onCheckedChange={(v) => setResendInviteEmail(!!v)} data-testid="resend-invite-email" />
                                Email
                              </label>
                              <label className={`flex items-center gap-2 text-sm ${smsActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                title={smsActive ? '' : 'Add a mobile number (Contact tab) and enable your SMS notification channel in Settings'}>
                                <Checkbox checked={resendInviteSms && smsActive} disabled={!smsActive}
                                  onCheckedChange={(v) => setResendInviteSms(!!v)} data-testid="resend-invite-sms" />
                                SMS
                              </label>
                            </div>
                          );
                        })()}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            onClick={handleResendInvite}
                            disabled={inviteLoading}
                            data-testid="resend-invite-btn"
                          >
                            {inviteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                            {selectedUser?.invite_status === 'cancelled' ? 'Resend new invitation' : 'Resend invitation'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleCopyInviteLink}
                            disabled={inviteLoading}
                            data-testid="copy-invite-link-btn"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy activation link
                          </Button>
                          {selectedUser?.invite_status !== 'cancelled' && (
                            <Button
                              variant="outline"
                              onClick={handleCancelInvite}
                              disabled={inviteLoading}
                              className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950"
                              data-testid="cancel-invite-btn"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel Invite
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Quick Login Link Section - Only for non-Super Admin users */}
                {selectedUser?.role !== 'super_admin' && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      Quick Login Link
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Generate a temporary URL that allows direct login without a password. Useful for support access or quick sharing.
                    </p>
                    
                    {(quickLoginToken || selectedUser?.quick_login_token) ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={`${window.location.origin}/quick-login/${quickLoginToken || selectedUser?.quick_login_token}`}
                            readOnly
                            className="font-mono text-xs"
                          />
                          <Button
                            type="button"
                            onClick={() => setShareQuickLoginOpen(true)}
                            className="shrink-0"
                            data-testid="share-quick-login-open-btn"
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share Login Link
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRevokeQuickLogin}
                          disabled={quickLoginLoading}
                        >
                          {quickLoginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash className="w-4 h-4 mr-2" />}
                          Revoke Quick Login Link
                        </Button>
                        <p className="text-xs text-amber-600">
                          ⚠️ This link allows anyone with it to log in as this user. Revoke if no longer needed.
                        </p>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGenerateQuickLogin}
                        disabled={quickLoginLoading}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-950"
                      >
                        {quickLoginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                        Generate Quick Login Link
                      </Button>
                    )}
                  </div>
                )}

                {/* Password Change Section - Only visible for non-Super Admin users */}
                {selectedUser?.role !== 'super_admin' && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Change Password
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Set a new password for this user. They will be logged out and need to log in again.
                    </p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter a strong password"
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                      <Button 
                        onClick={handlePasswordChange}
                        disabled={changingPassword || !isPasswordValid(newPassword)}
                        variant="outline"
                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      >
                        {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                        Change Password
                      </Button>
                    </div>
                    {newPassword && (
                      <PasswordRequirements password={newPassword} className="mt-2" />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CONTACT INFORMATION TAB */}
            {activeEditTab === 'contact' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => {
                      handleInputChange('company', e.target.value);
                      if (!e.target.value.trim() && formData.display_name_preference === 'company') {
                        handleInputChange('display_name_preference', 'personal');
                      }
                    }}
                    placeholder="Acme Corp"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Display As</Label>
                  <Select
                    value={formData.display_name_preference || 'personal'}
                    onValueChange={(v) => handleInputChange('display_name_preference', v)}
                  >
                    <SelectTrigger data-testid="display-name-preference-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal" data-testid="display-name-preference-personal">
                        Personal name{formData.full_name ? ` (${formData.full_name})` : ''}
                      </SelectItem>
                      <SelectItem value="company" disabled={!(formData.company || '').trim()} data-testid="display-name-preference-company">
                        Company name{(formData.company || '').trim() ? ` (${formData.company.trim()})` : ' — set a company first'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Chooses which name is shown in User Management (list, avatar initials and dialogs). When company is shown, the personal name appears as a subtitle.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Input
                    value={formData.address_street}
                    onChange={(e) => handleInputChange('address_street', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.address_city}
                      onChange={(e) => handleInputChange('address_city', e.target.value)}
                      placeholder="Sydney"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      value={formData.address_postcode}
                      onChange={(e) => handleInputChange('address_postcode', e.target.value)}
                      placeholder="2000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.address_state}
                      onChange={(e) => handleInputChange('address_state', e.target.value)}
                      placeholder="NSW"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select value={formData.address_country} onValueChange={(v) => handleInputChange('address_country', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+61 2 1234 5678"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mobile</Label>
                    <Input
                      value={formData.mobile}
                      onChange={(e) => handleInputChange('mobile', e.target.value)}
                      placeholder="+61 412 345 678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Internal Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Internal comments about this user..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* PRIVILEGES TAB */}
            {activeEditTab === 'privileges' && canSeePrivilegesTab && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-3">Page & Module Access</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select which pages and features this user can access
                  </p>

                  <div className="mb-4 rounded-lg border bg-muted/20 p-3">
                    <Label htmlFor="landing-page-after-login" className="text-sm font-semibold">
                      Landing Page After Login
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-2">
                      Choose the page this user opens first after login. Only currently allowed pages are shown.
                    </p>
                    <Select
                      value={formData.landing_page || getFallbackLandingPageKey(formData.enabled_pages) || 'dashboard_page'}
                      onValueChange={(value) => handleInputChange('landing_page', value)}
                      disabled={landingPageOptions.length === 0}
                    >
                      <SelectTrigger id="landing-page-after-login" data-testid="user-landing-page-select-trigger">
                        <SelectValue placeholder="Select landing page" />
                      </SelectTrigger>
                      <SelectContent data-testid="user-landing-page-select-content">
                        {landingPageOptions.map((option) => (
                          <SelectItem key={option.key} value={option.key} data-testid={`user-landing-page-select-option-${option.key}`}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    {getPrivilegesTreeForUser(effectiveUser).map((node) => (
                      <PrivilegeTreeNode
                        key={node.id}
                        node={node}
                        selected={formData.enabled_pages}
                        onToggle={handlePrivilegeToggle}
                      />
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Map Provider Access</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    OSM Maps will be shown as default if no map providers are selected
                  </p>
                  <div className="space-y-2">
                    {[
                      { key: 'osm_maps', label: 'OSM Maps' },
                      { key: 'google_maps', label: 'Google Maps' }
                    ].map((provider) => (
                      <CheckboxOption
                        key={provider.key}
                        bordered={false}
                        checked={formData.map_providers.includes(provider.key)}
                        onChange={(checked) => handleInputChange('map_providers', checked ? [...formData.map_providers, provider.key] : formData.map_providers.filter(p => p !== provider.key))}
                        label={provider.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Map Features - Only show if Google Maps is enabled */}
                {formData.map_providers.includes('google_maps') && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3">Google Maps Features</h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Enable additional Google Maps layers and views
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'map_satellite_view', label: 'Satellite View' },
                        { key: 'map_traffic_layer', label: 'Traffic Layer' },
                        { key: 'map_terrain_view', label: 'Terrain View' },
                        { key: 'map_street_view', label: 'Google Street View (Future)' },
                      ].map((feature) => (
                        <CheckboxOption
                          key={feature.key}
                          checked={formData.map_features[feature.key] || false}
                          onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                          label={feature.label}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Map Advanced Features - Only show if Google Maps is enabled */}
                {formData.map_providers.includes('google_maps') && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3">Map Advanced Features</h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Enable advanced map overlays and tools
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'map_heatmap', label: 'Heat Map' },
                        { key: 'map_weather', label: 'Weather Overlay' },
                        { key: 'map_nearest_device', label: 'Nearest Device Finder' },
                        { key: 'map_dark_mode', label: 'Dark Mode Map' },
                        { key: 'map_geofence_overlay', label: 'Geofence Overlay' },
                        { key: 'google_directions_eta', label: 'Google Directions ETA' },
                      ].map((feature) => (
                        <CheckboxOption
                          key={feature.key}
                          checked={formData.map_features[feature.key] || false}
                          onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                          label={feature.label}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Device Edit Options - Always visible */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Device Edit Options</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Control which device properties users can edit from the Live Tracking page. 
                    Both the user privilege here AND the per-device &quot;Allow End User Editing&quot; setting must be enabled.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'live_tracking_edit_asset', label: 'Edit Asset Type' },
                      { key: 'live_tracking_edit_icon', label: 'Edit Device Icon' },
                    ].map((feature) => (
                      <CheckboxOption
                        key={feature.key}
                        checked={formData.map_features[feature.key] || false}
                        onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                        label={feature.label}
                      />
                    ))}
                  </div>
                </div>

                {/* More Options - Always visible */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">More Options</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Control access to sharing and tracking link features from the Live Tracking &quot;More&quot; menu.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'share_position', label: 'Share Position' },
                      { key: 'generate_link', label: 'Generate Link' },
                    ].map((feature) => (
                      <CheckboxOption
                        key={feature.key}
                        checked={formData.map_features[feature.key] || false}
                        onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                        label={feature.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Map Enhancement Features */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Map Enhancement Features</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Control access to advanced map features available in the Live Tracking &quot;More Features&quot; panel.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'trip_direction_trail', label: 'Trip Direction Trail' },
                      { key: 'animated_movement', label: 'Animated Movement' },
                      { key: 'live_stop_timer', label: 'Live Stop Timer' },
                      { key: 'device_card_customization', label: 'Device Card Customization' },
                      { key: 'speed_alert_config', label: 'Advanced Road Speed Alert' },
                      { key: 'snap_to_road', label: 'Snap to Road' },
                      { key: 'osrm_route_eta', label: 'OSRM Route ETA' },
                      { key: 'google_reverse_geocoding', label: 'Google Reverse Geocoding' },
                    ].map((feature) => (
                      <CheckboxOption
                        key={feature.key}
                        testId={`privilege-${feature.key}`}
                        checked={formData.map_features[feature.key] || false}
                        onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                        label={feature.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Notification Channels */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3">Notification Channels</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Grant permission for this user to send and receive notifications via these channels. 
                    These settings are reflected in the user&apos;s Settings &gt; Notifications page.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'notification_email', label: 'Email' },
                      { key: 'notification_sms', label: 'SMS' },
                      { key: 'notification_push', label: 'Push Notifications' },
                      { key: 'event_webhook_commands', label: 'Event Webhooks & Commands' },
                    ].map((feature) => (
                      <CheckboxOption
                        key={feature.key}
                        checked={formData.map_features[feature.key] || false}
                        onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                        label={feature.label}
                      />
                    ))}
                  </div>
                  {/* SMS Provider selector — only when SMS is enabled */}
                  {formData.map_features.notification_sms && (
                    <div className="mt-3 p-3 rounded border bg-muted/30 space-y-2">
                      <Label className="text-xs font-medium">SMS Provider for this User</Label>
                      <Select
                        value={formData.sms_provider}
                        onValueChange={(v) => handleInputChange('sms_provider', v)}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="twilio">Twilio</SelectItem>
                          <SelectItem value="mobilemessage">MobileMessage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>History Retention (Days)</Label>
                      <NumberInput
                        value={formData.history_retention_days}
                        onValueChange={(n) => handleInputChange('history_retention_days', n)}
                        min="0"
                        data-testid="history-retention-days-input"
                      />
                      <p className="text-xs text-muted-foreground">Controls how much operational history this user can view. Global Operational History still determines how long the platform stores the data.</p>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <Label className="text-sm">AI Features</Label>
                      <Switch
                        checked={formData.ai_enabled}
                        onCheckedChange={(v) => handleInputChange('ai_enabled', v)}
                      />
                    </div>
                  </div>
                </div>

                {/* Media Section — premium media features + streaming limits */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Video className="w-4 h-4 text-blue-500" />
                    Media
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Controls for Event Videos, Remote Media, and Live Streaming.
                  </p>

                  {/* Sub-section: Video Enhancements */}
                  <div className="mb-5">
                    <h4 className="font-medium text-sm mb-2">Video Enhancements</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Premium video features for Event Videos and Remote Media — trail playback inside the modal, and tokenised public evidence sharing.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'media_video_show_trail_map', label: 'Show Trail Map (Event Videos)' },
                        { key: 'media_share_event_evidence', label: 'Share Event Evidence Link' },
                      ].map((feature) => (
                        <CheckboxOption
                          key={feature.key}
                          testId={`media-priv-${feature.key}`}
                          checked={formData.map_features[feature.key] || false}
                          onChange={(checked) => handleInputChange('map_features', { ...formData.map_features, [feature.key]: checked })}
                          label={feature.label}
                        />
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      <strong>Show Trail Map:</strong> drops down a mini GPS trail map inside every Event Video modal with a time-synced cursor that follows the video.<br />
                      <strong>Share Evidence Link:</strong> mint <code>/evidence/&lt;token&gt;</code> URLs with configurable expiry (1 day to 1 year). Every public view is audited with timestamp + IP + user-agent for chain-of-custody compliance.
                    </p>
                  </div>

                  {/* Sub-section: Live Streaming Limits — moved inside Media */}
                  <div className="pt-4 border-t border-dashed">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Play className="w-4 h-4 text-red-500" />
                      Live Streaming Limits
                    </h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Prevent bandwidth waste from streams left running. Set <strong>0</strong> to disable either check.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Max Stream Duration (Minutes)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="240"
                          value={formData.max_stream_duration_minutes === '' || formData.max_stream_duration_minutes == null ? '' : formData.max_stream_duration_minutes}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleInputChange('max_stream_duration_minutes', v === '' ? '' : (parseInt(v) || 0));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') handleInputChange('max_stream_duration_minutes', 0);
                          }}
                          data-testid="max-stream-duration"
                        />
                        <p className="text-xs text-muted-foreground">
                          Live video auto-stops after this many minutes. User sees a countdown and can click &quot;Extend&quot;. Set <strong>0</strong> to disable.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Inactivity Timeout (Minutes)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="60"
                          value={formData.stream_inactivity_timeout_minutes === '' || formData.stream_inactivity_timeout_minutes == null ? '' : formData.stream_inactivity_timeout_minutes}
                          onChange={(e) => {
                            const v = e.target.value;
                            handleInputChange('stream_inactivity_timeout_minutes', v === '' ? '' : (parseInt(v) || 0));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') handleInputChange('stream_inactivity_timeout_minutes', 0);
                          }}
                          data-testid="stream-inactivity-timeout"
                        />
                        <p className="text-xs text-muted-foreground">
                          Auto-stop stream if tab is hidden AND no mouse/keyboard activity for this long. Set <strong>0</strong> to disable.
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-900 dark:text-blue-200">
                        <strong>💡 Tip:</strong> To stream a device continuously (e.g. critical fleet vehicles), enable <strong>&quot;Always-on Streaming&quot;</strong> on the device in Device Management. That bypasses these limits for that specific device.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Resource Limits Section */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Resource Limits
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set maximum limits for this user. Use <strong>-1</strong> for unlimited or <strong>0</strong> to block adding new resources.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <HardDrive className="w-4 h-4 text-muted-foreground" />
                        Max Devices
                      </Label>
                      <NumberInput
                        value={formData.max_devices}
                        onValueChange={(n) => handleInputChange('max_devices', n)}
                        min="-1"
                        data-testid="max-devices-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.max_devices === -1 ? 'Unlimited' : formData.max_devices === 0 ? 'Not set (blocked)' : `Max ${formData.max_devices} devices`}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Radio className="w-4 h-4 text-muted-foreground" />
                        Max Beacons
                      </Label>
                      <NumberInput
                        value={formData.max_beacons}
                        onValueChange={(n) => handleInputChange('max_beacons', n)}
                        min="-1"
                        data-testid="max-beacons-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.max_beacons === -1 ? 'Unlimited' : formData.max_beacons === 0 ? 'Not set (blocked)' : `Max ${formData.max_beacons} beacons`}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-muted-foreground" />
                        Max Drivers
                      </Label>
                      <NumberInput
                        value={formData.max_drivers}
                        onValueChange={(n) => handleInputChange('max_drivers', n)}
                        min="-1"
                        data-testid="max-drivers-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.max_drivers === -1 ? 'Unlimited' : formData.max_drivers === 0 ? 'Not set (blocked)' : `Max ${formData.max_drivers} drivers`}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        Max Staff
                      </Label>
                      <NumberInput
                        value={formData.max_staff}
                        onValueChange={(n) => handleInputChange('max_staff', n)}
                        min="-1"
                        data-testid="max-staff-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.max_staff === -1 ? 'Unlimited' : formData.max_staff === 0 ? 'Not set (blocked)' : `Max ${formData.max_staff} staff`}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <UserX className="w-4 h-4 text-muted-foreground" />
                        Max Passengers
                      </Label>
                      <NumberInput
                        value={formData.max_passengers}
                        onValueChange={(n) => handleInputChange('max_passengers', n)}
                        min="-1"
                        data-testid="max-passengers-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.max_passengers === -1 ? 'Unlimited' : formData.max_passengers === 0 ? 'Not set (blocked)' : `Max ${formData.max_passengers} passengers`}
                      </p>
                    </div>
                  </div>

                  {/* User Management Limits - Only show for Manager and End User roles */}
                  {(effectiveUser.role === 'manager' || effectiveUser.role === 'end_user') && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        User Management Limits
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        {effectiveUser.role === 'manager' 
                          ? 'Set how many End Users this Manager can manage.'
                          : 'Set how many Sub Accounts this End User can create.'
                        }
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        {effectiveUser.role === 'manager' && (
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              Max End Users
                            </Label>
                            <NumberInput
                              value={formData.max_end_users}
                              onValueChange={(n) => handleInputChange('max_end_users', n)}
                              min="-1"
                              data-testid="max-end-users-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              {formData.max_end_users === -1 ? 'Unlimited' : formData.max_end_users === 0 ? 'Not set (blocked)' : `Max ${formData.max_end_users} End Users`}
                            </p>
                          </div>
                        )}
                        {/* Max Sub Accounts - Only for End Users */}
                        {effectiveUser.role === 'end_user' && (
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-muted-foreground" />
                              Max Sub Accounts
                            </Label>
                            <NumberInput
                              value={formData.max_sub_accounts}
                              onValueChange={(n) => handleInputChange('max_sub_accounts', n)}
                              min="-1"
                              data-testid="max-sub-accounts-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              {formData.max_sub_accounts === -1 ? 'Unlimited' : formData.max_sub_accounts === 0 ? 'Not set (blocked)' : `Max ${formData.max_sub_accounts} Sub Accounts`}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick preset buttons */}
                  <div className="mt-4 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleInputChange('max_devices', -1);
                        handleInputChange('max_drivers', -1);
                        handleInputChange('max_staff', -1);
                        handleInputChange('max_passengers', -1);
                      }}
                    >
                      Set All Unlimited
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleInputChange('max_devices', 10);
                        handleInputChange('max_drivers', 10);
                        handleInputChange('max_staff', 5);
                        handleInputChange('max_passengers', 20);
                      }}
                    >
                      Basic Plan (10/10/5/20)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleInputChange('max_devices', 50);
                        handleInputChange('max_drivers', 50);
                        handleInputChange('max_staff', 25);
                        handleInputChange('max_passengers', 100);
                      }}
                    >
                      Pro Plan (50/50/25/100)
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* DEVICES TAB - Option A: Table Layout */}
            {activeEditTab === 'devices' && (
              <div className="flex flex-col h-[calc(85vh-220px)]">
                <div className="flex items-center justify-between flex-shrink-0">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      GPS Device Assignment
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Slots: {getRemainingDeviceSlots()} ({assignedDevices.length}/{formData.max_devices === -1 ? '∞' : formData.max_devices})
                    </p>
                  </div>
                </div>

                {isDeviceLimitReached() && (
                  <div className="p-3 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200 flex-shrink-0 mt-4">
                    <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Device limit reached ({assignedDevices.length}/{formData.max_devices}).
                      Remove devices or increase the limit in Privileges tab.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 min-h-0 mt-4">
                  {/* Available Devices Panel */}
                  <Card className={`flex flex-col self-start w-full max-h-full ${isDeviceLimitReached() ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3 flex-shrink-0">
                      <CardTitle className="text-sm flex items-center justify-between">
                        Available Devices
                        <Badge variant="outline">{availDevicesTotal} devices</Badge>
                      </CardTitle>
                      {/* Search Field */}
                      <div className="relative mt-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search name, IMEI, Beacon ID, Registration..."
                          value={deviceSearchQuery}
                          onChange={(e) => setDeviceSearchQuery(e.target.value)}
                          className="pl-9 pr-8 h-8 text-sm"
                          disabled={isDeviceLimitReached()}
                          data-testid="user-management-available-devices-search-input"
                        />
                        {deviceSearchQuery && !isDeviceLimitReached() && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setDeviceSearchQuery('')}
                            data-testid="user-management-available-devices-search-clear-button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 flex-1 min-h-0 flex flex-col">
                      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
                        <Table>
                          <TableHeader>
                            <TableRow className="text-xs">
                              <TableHead className="px-2 w-10"></TableHead>
                              <TableHead className="px-2">Device</TableHead>
                              <TableHead className="px-2">IMEI</TableHead>
                              <TableHead className="px-2 text-center">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {availDeviceRows.map((device) => {
                              const statusMeta = getDeviceStatusMeta(device);
                              const StatusIcon = statusMeta.icon;
                              return (
                              <TableRow 
                                key={device.id} 
                                className={`text-xs ${isDeviceLimitReached() ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted/50'}`}
                                onClick={() => !isDeviceLimitReached() && handleAssignDevice(device)}
                                data-testid={`user-management-available-device-row-${device.id}`}
                              >
                                <TableCell className="px-2">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0"
                                    disabled={isDeviceLimitReached()}
                                    data-testid={`user-management-assign-device-button-${device.id}`}
                                  >
                                    <ChevronRight className="w-3 h-3" />
                                  </Button>
                                </TableCell>
                                <TableCell className="px-2">
                                  <div className="flex items-center gap-2">
                                    <HardDrive className="w-3 h-3 text-muted-foreground" />
                                    <div>
                                      <p className="font-medium text-xs">{device.name}</p>
                                      <p className="text-[10px] text-muted-foreground">{`${device.registration || device.beacon_id || '-'} · ${device.protocol || 'GT06'}`}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="px-2 font-mono text-[10px]">{device.imei}</TableCell>
                                <TableCell className="px-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <Badge 
                                      className={`text-[10px] px-1.5 py-0 ${statusMeta.badgeClass}`}
                                      data-testid={`user-management-available-device-status-${device.id}`}
                                    >
                                      <StatusIcon className="w-3 h-3 mr-1" />
                                      {statusMeta.label}
                                    </Badge>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );})}
                            {availDeviceRows.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-sm text-muted-foreground">
                                  {availDevicesLoading ? 'Loading devices…' : (deviceSearchQuery ? 'No devices match your search' : 'No available devices')}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                      <div className="px-3 border-t flex-shrink-0">
                        <PaginationFooter
                          page={availDevicesPage}
                          setPage={setAvailDevicesPage}
                          totalItems={availDevicesTotal}
                          itemsPerPage={availDevicesPageSize}
                          setItemsPerPage={setAvailDevicesPageSize}
                          testIdPrefix="available-devices"
                          alwaysShow
                          scrollOnChange={false}
                          disabled={availDevicesLoading}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Assigned Devices Panel */}
                  <Card className="border-green-200 dark:border-green-800 flex flex-col self-start w-full max-h-full">
                    <CardHeader className="pb-3 flex-shrink-0">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          Assigned to User
                          <Badge className="bg-green-600">{filteredAssignedDevices.length}</Badge>
                        </span>
                        {assignedDevices.some(d => !d._placeholder) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={handleClearAssignedDevices}
                            data-testid="user-management-clear-assigned-devices"
                          >
                            <X className="w-3.5 h-3.5" /> Clear all
                          </Button>
                        )}
                      </CardTitle>
                      {/* Search Field */}
                      <div className="relative mt-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search name, IMEI, Beacon ID, Registration..."
                          value={assignedDeviceSearchQuery}
                          onChange={(e) => setAssignedDeviceSearchQuery(e.target.value)}
                          className="pl-9 pr-8 h-8 text-sm"
                          data-testid="user-management-assigned-devices-search-input"
                        />
                        {assignedDeviceSearchQuery && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setAssignedDeviceSearchQuery('')}
                            data-testid="user-management-assigned-devices-search-clear-button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 flex-1 min-h-0 flex flex-col">
                      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
                        <Table>
                          <TableHeader>
                            <TableRow className="text-xs">
                              <TableHead className="px-2 w-10"></TableHead>
                              <TableHead className="px-2">Device</TableHead>
                              <TableHead className="px-2">IMEI</TableHead>
                              <TableHead className="px-2 text-center">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pagedAssignedDevices.map((device) => {
                              const statusMeta = device._placeholder ? null : getDeviceStatusMeta(device);
                              const StatusIcon = statusMeta?.icon;
                              return (
                              <TableRow key={device.id} className="text-xs bg-green-50/50 dark:bg-green-950/10" data-testid={`user-management-assigned-device-row-${device.id}`}>
                                <TableCell className="px-2">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 dark:hover:text-red-400"
                                    onClick={() => handleUnassignDevice(device)}
                                    disabled={device._placeholder}
                                    title={device._placeholder ? 'Loading device — please wait' : undefined}
                                    data-testid={`user-management-unassign-device-button-${device.id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </TableCell>
                                <TableCell className="px-2">
                                  <div className="flex items-center gap-2">
                                    <HardDrive className={`w-3 h-3 ${device._placeholder ? 'text-muted-foreground' : 'text-green-600'}`} />
                                    <div>
                                      {device._placeholder ? (
                                        devicesLoaded ? (
                                          <>
                                            <p className="font-medium text-xs text-muted-foreground italic">Managed by another admin</p>
                                            <p className="text-[10px] text-muted-foreground">Outside your device list — read only</p>
                                          </>
                                        ) : (
                                          <p className="font-medium text-xs text-muted-foreground italic">Loading…</p>
                                        )
                                      ) : (
                                        <>
                                          <p className="font-medium text-xs">{device.name}</p>
                                          <p className="text-[10px] text-muted-foreground">{device.registration || device.beacon_id || '-'}</p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="px-2 font-mono text-[10px]">{device._placeholder ? '—' : device.imei}</TableCell>
                                <TableCell className="px-2 text-center">
                                  {device._placeholder ? (
                                    <span className="text-[10px] text-muted-foreground">—</span>
                                  ) : (
                                    <Badge 
                                      className={`text-[10px] px-1.5 py-0 ${statusMeta.badgeClass}`}
                                      data-testid={`user-management-assigned-device-status-${device.id}`}
                                    >
                                      <StatusIcon className="w-3 h-3 mr-1" />
                                      {statusMeta.label}
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );})}
                            {filteredAssignedDevices.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8">
                                  <HardDrive className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                  <p className="text-sm text-muted-foreground">
                                    {assignedDeviceSearchQuery ? 'No assigned devices match your search' : 'No devices assigned'}
                                  </p>
                                  {!assignedDeviceSearchQuery && (
                                    <p className="text-xs text-muted-foreground">Click devices from the left to assign</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                      <div className="px-3 border-t flex-shrink-0">
                        <PaginationFooter
                          page={assignedDevicesEffectivePage}
                          setPage={setAssignedDevicesPage}
                          totalItems={filteredAssignedDevices.length}
                          itemsPerPage={assignedDevicesPageSize}
                          setItemsPerPage={setAssignedDevicesPageSize}
                          testIdPrefix="assigned-devices"
                          alwaysShow
                          scrollOnChange={false}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* BEACONS TAB */}
            {activeEditTab === 'beacons' && (
              <div className="flex flex-col h-[calc(85vh-220px)]">
                <div className="flex items-center justify-between flex-shrink-0">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      BLE Beacon Assignment
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Slots: {getRemainingBeaconSlots()} ({assignedBeacons.length}/{formData.max_beacons === -1 ? '∞' : formData.max_beacons})
                    </p>
                  </div>
                </div>

                {isBeaconLimitReached() && (
                  <div className="p-3 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200 flex-shrink-0 mt-4">
                    <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Beacon limit reached ({assignedBeacons.length}/{formData.max_beacons}).
                      Remove beacons or increase the limit in Privileges tab.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 min-h-0 mt-4">
                  {/* Available Beacons Panel */}
                  <Card className={`flex flex-col self-start w-full max-h-full ${isBeaconLimitReached() ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-3 flex-shrink-0">
                      <CardTitle className="text-sm flex items-center justify-between">
                        Available Beacons
                        <Badge variant="outline">{availBeaconsTotal} beacons</Badge>
                      </CardTitle>
                      <div className="relative mt-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search name, MAC, Serial, Asset Type..."
                          value={beaconSearchQuery}
                          onChange={(e) => setBeaconSearchQuery(e.target.value)}
                          className="pl-9 pr-8 h-8 text-sm"
                          disabled={isBeaconLimitReached()}
                          data-testid="user-management-available-beacons-search-input"
                        />
                        {beaconSearchQuery && !isBeaconLimitReached() && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setBeaconSearchQuery('')}
                            data-testid="user-management-available-beacons-search-clear-button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 flex-1 min-h-0 flex flex-col">
                      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
                        <Table>
                          <TableHeader>
                            <TableRow className="text-xs">
                              <TableHead className="px-2 w-10"></TableHead>
                              <TableHead className="px-2">Beacon</TableHead>
                              <TableHead className="px-2">MAC / ID</TableHead>
                              <TableHead className="px-2 text-center">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {availBeaconRows.map((beacon) => (
                              <TableRow 
                                key={beacon.id} 
                                className={`text-xs ${isBeaconLimitReached() ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted/50'}`}
                                onClick={() => !isBeaconLimitReached() && handleAssignBeacon(beacon)}
                                data-testid={`user-management-available-beacon-row-${beacon.id}`}
                              >
                                <TableCell className="px-2">
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={isBeaconLimitReached()} data-testid={`user-management-assign-beacon-button-${beacon.id}`}>
                                    <ChevronRight className="w-3 h-3" />
                                  </Button>
                                </TableCell>
                                <TableCell className="px-2">
                                  <div className="flex items-center gap-2">
                                    <Radio className="w-3 h-3 text-muted-foreground" />
                                    <div>
                                      <p className="font-medium text-xs">{beacon.name}</p>
                                      <p className="text-[10px] text-muted-foreground capitalize">{`${beacon.serial_number || '-'} · ${beacon.asset_type || 'people'}`}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="px-2 font-mono text-[10px]">{beacon.mac_address || beacon.imei}</TableCell>
                                <TableCell className="px-2 text-center">
                                  <Badge 
                                    variant="outline" 
                                    className={`text-[10px] px-1 py-0 ${
                                      beacon.is_active === false ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                                    }`}
                                  >
                                    {beacon.is_active === false ? 'Deactivated' : 'Active'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                            {availBeaconRows.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-sm text-muted-foreground">
                                  {availBeaconsLoading ? 'Loading beacons…' : (beaconSearchQuery ? 'No beacons match your search' : 'No available beacons')}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                      <div className="px-3 border-t flex-shrink-0">
                        <PaginationFooter
                          page={availBeaconsPage}
                          setPage={setAvailBeaconsPage}
                          totalItems={availBeaconsTotal}
                          itemsPerPage={availBeaconsPageSize}
                          setItemsPerPage={setAvailBeaconsPageSize}
                          testIdPrefix="available-beacons"
                          alwaysShow
                          scrollOnChange={false}
                          disabled={availBeaconsLoading}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Assigned Beacons Panel */}
                  <Card className="border-green-200 dark:border-green-800 flex flex-col self-start w-full max-h-full">
                    <CardHeader className="pb-3 flex-shrink-0">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          Assigned to User
                          <Badge className="bg-green-600">{filteredAssignedBeacons.length}</Badge>
                        </span>
                        {assignedBeacons.some(b => !b._placeholder) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={handleClearAssignedBeacons}
                            data-testid="user-management-clear-assigned-beacons"
                          >
                            <X className="w-3.5 h-3.5" /> Clear all
                          </Button>
                        )}
                      </CardTitle>
                      <div className="relative mt-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search name, MAC, Serial, Asset Type..."
                          value={assignedBeaconSearchQuery}
                          onChange={(e) => setAssignedBeaconSearchQuery(e.target.value)}
                          className="pl-9 pr-8 h-8 text-sm"
                          data-testid="user-management-assigned-beacons-search-input"
                        />
                        {assignedBeaconSearchQuery && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setAssignedBeaconSearchQuery('')}
                            data-testid="user-management-assigned-beacons-search-clear-button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 flex-1 min-h-0 flex flex-col">
                      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
                        <Table>
                          <TableHeader>
                            <TableRow className="text-xs">
                              <TableHead className="px-2 w-10"></TableHead>
                              <TableHead className="px-2">Beacon</TableHead>
                              <TableHead className="px-2">MAC / ID</TableHead>
                              <TableHead className="px-2 text-center">Asset Type</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pagedAssignedBeacons.map((beacon) => (
                              <TableRow key={beacon.id} className="text-xs bg-green-50/50 dark:bg-green-950/10" data-testid={`user-management-assigned-beacon-row-${beacon.id}`}>
                                <TableCell className="px-2">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 dark:hover:text-red-400"
                                    onClick={() => handleUnassignBeacon(beacon)}
                                    disabled={beacon._placeholder}
                                    title={beacon._placeholder ? 'Loading beacon — please wait' : undefined}
                                    data-testid={`user-management-unassign-beacon-button-${beacon.id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </TableCell>
                                <TableCell className="px-2">
                                  <div className="flex items-center gap-2">
                                    <Radio className={`w-3 h-3 ${beacon._placeholder ? 'text-muted-foreground' : 'text-green-600'}`} />
                                    <div>
                                      {beacon._placeholder ? (
                                        devicesLoaded ? (
                                          <>
                                            <p className="font-medium text-xs text-muted-foreground italic">Managed by another admin</p>
                                            <p className="text-[10px] text-muted-foreground">Outside your device list — read only</p>
                                          </>
                                        ) : (
                                          <p className="font-medium text-xs text-muted-foreground italic">Loading…</p>
                                        )
                                      ) : (
                                        <>
                                          <p className="font-medium text-xs">{beacon.name}</p>
                                          <p className="text-[10px] text-muted-foreground">{beacon.serial_number || '-'}</p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="px-2 font-mono text-[10px]">{beacon._placeholder ? '—' : (beacon.mac_address || beacon.imei)}</TableCell>
                                <TableCell className="px-2 text-center">
                                  {beacon._placeholder ? (
                                    <span className="text-[10px] text-muted-foreground">—</span>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] px-1 py-0 capitalize">{beacon.asset_type || 'people'}</Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {filteredAssignedBeacons.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8">
                                  <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                  <p className="text-sm text-muted-foreground">
                                    {assignedBeaconSearchQuery ? 'No assigned beacons match your search' : 'No beacons assigned'}
                                  </p>
                                  {!assignedBeaconSearchQuery && (
                                    <p className="text-xs text-muted-foreground">Click beacons from the left to assign</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                      <div className="px-3 border-t flex-shrink-0">
                        <PaginationFooter
                          page={assignedBeaconsEffectivePage}
                          setPage={setAssignedBeaconsPage}
                          totalItems={filteredAssignedBeacons.length}
                          itemsPerPage={assignedBeaconsPageSize}
                          setItemsPerPage={setAssignedBeaconsPageSize}
                          testIdPrefix="assigned-beacons"
                          alwaysShow
                          scrollOnChange={false}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* ALLOCATED USERS TAB - Only for Manager accounts */}
            {activeEditTab === 'allocated_users' && selectedUser?.role === 'manager' && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Allocated End Users
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select which End Users this Manager can view and manage. The Manager will only see these users in their User Management.
                  </p>
                </div>

                {/* Allocation Limit Banner */}
                {formData.max_end_users !== -1 && (
                  <div className={`p-3 rounded-lg border ${
                    formData.allocated_user_ids.length >= formData.max_end_users && formData.max_end_users > 0
                      ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800' 
                      : 'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Allocation Limit</span>
                      <Badge variant={formData.allocated_user_ids.length >= formData.max_end_users && formData.max_end_users > 0 ? 'destructive' : 'default'}>
                        {formData.allocated_user_ids.length} / {formData.max_end_users === 0 ? 'Not Set' : formData.max_end_users === -1 ? '∞' : formData.max_end_users}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Search and Select */}
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search End Users by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="h-8 pl-10"
                    />
                  </div>

                  {/* User Selection Table */}
                  <Card>
                    <CardContent className="p-0">
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="w-10 p-3 text-left">
                                <Checkbox
                                  checked={allocatableUsers.length > 0 && formData.allocated_user_ids.length === allocatableUsers.filter(u => 
                                    u.full_name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                    u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                                  ).length}
                                  onCheckedChange={(checked) => {
                                    const filteredUsers = allocatableUsers.filter(u => 
                                      u.full_name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                      u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                                    );
                                    if (checked) {
                                      // Check limit before selecting all
                                      const maxEndUsers = formData.max_end_users;
                                      if (maxEndUsers === 0) {
                                        toast.error('Max End Users is set to 0. Cannot allocate any users.');
                                        return;
                                      }
                                      if (maxEndUsers !== -1 && filteredUsers.length > maxEndUsers) {
                                        toast.error(`Cannot select all. Maximum ${maxEndUsers} End Users allowed. Select individually.`);
                                        return;
                                      }
                                      handleInputChange('allocated_user_ids', filteredUsers.map(u => u.id));
                                    } else {
                                      handleInputChange('allocated_user_ids', []);
                                    }
                                  }}
                                />
                              </th>
                              <th className="p-3 text-left text-sm font-medium">End User</th>
                              <th className="p-3 text-left text-sm font-medium">Email</th>
                              <th className="p-3 text-left text-sm font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allocatableUsers
                              .filter(u => 
                                u.full_name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                                u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                              )
                              .map((endUser) => {
                                const isAllocated = formData.allocated_user_ids.includes(endUser.id);
                                // Get all managers except the current one being edited
                                const otherManagers = (endUser.managed_by_names || []).filter((name, idx) => {
                                  const managerId = endUser.managed_by_user_ids?.[idx];
                                  return managerId && managerId !== selectedUser?.id;
                                });
                                const otherManagerNames = otherManagers.join(', ');
                                
                                return (
                                  <tr 
                                    key={endUser.id} 
                                    className={`border-b hover:bg-muted/30 cursor-pointer ${isAllocated ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                                    onClick={() => {
                                      if (isAllocated) {
                                        // Always allow removing
                                        handleInputChange('allocated_user_ids', formData.allocated_user_ids.filter(id => id !== endUser.id));
                                      } else {
                                        // Check limit before adding
                                        const maxEndUsers = formData.max_end_users;
                                        const currentCount = formData.allocated_user_ids.length;
                                        
                                        if (maxEndUsers === 0) {
                                          toast.error('Max End Users is set to 0. Cannot allocate any users.');
                                          return;
                                        }
                                        if (maxEndUsers !== -1 && currentCount >= maxEndUsers) {
                                          toast.error(`Limit reached. Maximum ${maxEndUsers} End Users allowed.`);
                                          return;
                                        }
                                        handleInputChange('allocated_user_ids', [...formData.allocated_user_ids, endUser.id]);
                                      }
                                    }}
                                  >
                                    <td className="p-3">
                                      <Checkbox
                                        checked={isAllocated}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            // Check limit before adding
                                            const maxEndUsers = formData.max_end_users;
                                            const currentCount = formData.allocated_user_ids.length;
                                            
                                            if (maxEndUsers === 0) {
                                              toast.error('Max End Users is set to 0. Cannot allocate any users.');
                                              return;
                                            }
                                            if (maxEndUsers !== -1 && currentCount >= maxEndUsers) {
                                              toast.error(`Limit reached. Maximum ${maxEndUsers} End Users allowed.`);
                                              return;
                                            }
                                            handleInputChange('allocated_user_ids', [...formData.allocated_user_ids, endUser.id]);
                                          } else {
                                            handleInputChange('allocated_user_ids', formData.allocated_user_ids.filter(id => id !== endUser.id));
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </td>
                                    <td className="p-3">
                                      <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-muted-foreground" />
                                        <span className="font-medium text-sm">{endUser.full_name}</span>
                                      </div>
                                    </td>
                                    <td className="p-3 text-sm text-muted-foreground">{endUser.email}</td>
                                    <td className="p-3">
                                      <div className="flex flex-wrap gap-1">
                                        {isAllocated && (
                                          <Badge className="text-xs bg-green-600">Allocated</Badge>
                                        )}
                                        {otherManagerNames && (
                                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                            Managed by {otherManagerNames}
                                          </Badge>
                                        )}
                                        {!isAllocated && !otherManagerNames && (
                                          <Badge variant="outline" className="text-xs">Available</Badge>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            {allocatableUsers.filter(u => 
                              u.full_name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                              u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                            ).length === 0 && (
                              <tr>
                                <td colSpan="4" className="p-6 text-center text-muted-foreground">
                                  {userSearchQuery ? 'No End Users match your search' : 'No End Users available for allocation'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <span className="text-sm">
                      <strong>{formData.allocated_user_ids.length}</strong> End User{formData.allocated_user_ids.length !== 1 ? 's' : ''} allocated to this Manager
                    </span>
                    {formData.allocated_user_ids.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleInputChange('allocated_user_ids', [])}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* AUDIT TAB */}
            {activeEditTab === 'audit' && (
              <div className="flex flex-col h-[calc(85vh-220px)]">
                <div className="flex flex-wrap items-start justify-between gap-2 flex-shrink-0">
                  <div>
                    <h3 className="font-semibold">Audit Trail</h3>
                    <p className="text-sm text-muted-foreground">
                      Complete history of changes to this user account
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative w-44">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search device, action, details..."
                        value={auditSearch}
                        onChange={e => { setAuditSearch(e.target.value); fetchAuditLogs(selectedUser?.id, e.target.value, auditDateRange, auditActionFilter); }}
                        className="w-full h-8 pl-8 pr-7 text-xs"
                        data-testid="user-audit-search"
                      />
                      {auditSearch && <button onClick={() => { setAuditSearch(''); fetchAuditLogs(selectedUser?.id, '', auditDateRange, auditActionFilter); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                    <Select value={auditActionFilter} onValueChange={(v) => { setAuditActionFilter(v); fetchAuditLogs(selectedUser?.id, auditSearch, auditDateRange, v); }}>
                      <SelectTrigger className="w-44 h-8 text-xs" data-testid="user-audit-action-filter">
                        <SelectValue placeholder="All actions" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="all">All Actions</SelectItem>
                        {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Date range — Event Log "All Time" preset filter (shared component). */}
                    <DateRangePresetFilter
                      value={auditDateRange}
                      onChange={(range) => { setAuditDateRange(range); fetchAuditLogs(selectedUser?.id, auditSearch, range, auditActionFilter); }}
                      testIdPrefix="user-audit-date"
                      resetSignal={auditDateResetKey}
                    />
                    <Button variant="outline" size="sm" className="h-8" onClick={handleExportUserAudit} data-testid="export-user-audit-btn">
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      <span className="text-xs">{auditSearch || auditDateRange.from || auditActionFilter !== 'all' ? `Export (${auditLogs.length})` : 'Export'}</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8"
                      onClick={() => { const cleared = { from: undefined, to: undefined }; setAuditDateRange(cleared); setAuditDateResetKey(k => k + 1); fetchAuditLogs(selectedUser?.id, auditSearch, cleared, auditActionFilter); }}
                      disabled={auditLogsLoading}
                      data-testid="user-audit-refresh"
                    >
                      {auditLogsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
                
                {auditLogsLoading ? (
                  <div className="flex items-center justify-center flex-1">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center flex-1 flex flex-col items-center justify-center text-muted-foreground">
                    <History className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-sm">No audit logs found</p>
                    <p className="text-xs">Activity will be recorded here</p>
                  </div>
                ) : (
                  <ScrollArea className="flex-1 min-h-0 mt-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
                    <div className="space-y-2">
                      {auditLogs.map((log) => (
                        <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            log.action === 'user_login' ? 'bg-green-100 text-green-600' :
                            log.action === 'user_login_failed' ? 'bg-red-100 text-red-600' :
                            log.action === 'user_blocked' ? 'bg-red-100 text-red-600' :
                            log.action === 'user_unblocked' ? 'bg-green-100 text-green-600' :
                            log.action === 'password_changed' ? 'bg-amber-100 text-amber-600' :
                            log.action === 'role_changed' ? 'bg-purple-100 text-purple-600' :
                            log.action === 'expiry_changed' ? 'bg-orange-100 text-orange-600' :
                            log.action === 'user_impersonated' ? 'bg-blue-100 text-blue-600' :
                            log.action === 'impersonation_ended' ? 'bg-blue-100 text-blue-600' :
                            log.action === 'user_logout' || log.action === 'session_expired' ? 'bg-slate-100 text-slate-600' :
                            log.action === 'command_sent' ? 'bg-amber-100 text-amber-600' :
                            log.action?.startsWith('geofence_') ? 'bg-teal-100 text-teal-600' :
                            log.action === 'global_settings_changed' ? 'bg-indigo-100 text-indigo-600' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {log.action === 'user_login' ? <LogIn className="w-4 h-4" /> :
                             log.action === 'user_login_failed' ? <XCircle className="w-4 h-4" /> :
                             log.action === 'user_blocked' ? <Lock className="w-4 h-4" /> :
                             log.action === 'user_unblocked' ? <Unlock className="w-4 h-4" /> :
                             log.action === 'password_changed' ? <Key className="w-4 h-4" /> :
                             log.action === 'role_changed' ? <Shield className="w-4 h-4" /> :
                             log.action === 'expiry_changed' ? <Clock className="w-4 h-4" /> :
                             log.action === 'user_impersonated' ? <ExternalLink className="w-4 h-4" /> :
                             log.action === 'impersonation_ended' ? <LogOut className="w-4 h-4" /> :
                             log.action === 'user_logout' ? <LogOut className="w-4 h-4" /> :
                             log.action === 'session_expired' || log.action === 'session_ended_inactive' ? <Clock className="w-4 h-4" /> :
                             log.action === 'session_resumed' ? <LogIn className="w-4 h-4" /> :
                             log.action === 'command_sent' ? <Radio className="w-4 h-4" /> :
                             log.action?.startsWith('geofence_') ? <MapPin className="w-4 h-4" /> :
                             log.action === 'global_settings_changed' ? <SettingsIcon className="w-4 h-4" /> :
                             <History className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium text-sm">{formatAuditAction(log.action)}</p>
                              <Badge variant="outline" className="text-xs flex-shrink-0">{formatTimestampUTC(log.timestamp || log.created_at)}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{log.details || '-'}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span>By: {log.actor_email || 'System'}</span>
                              {log.ip_address && <span>IP: {log.ip_address}</span>}
                            </div>
                            {log.changes && Object.keys(log.changes).length > 0 && (
                              <div className="mt-2 space-y-1">
                                {Object.entries(log.changes).map(([field, value]) => {
                                  if (!value && value !== false && value !== 0) return null;
                                  const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                  if (typeof value === 'object' && value !== null && ('old' in value || 'new' in value)) {
                                    return (
                                      <div key={field} className="text-xs p-1.5 bg-muted rounded flex flex-wrap gap-x-2">
                                        <span className="font-medium">{label}:</span>
                                        <span className="text-muted-foreground line-through">{value.old != null ? String(value.old) : 'empty'}</span>
                                        <span>&rarr;</span>
                                        <span className="font-mono">{value.new != null ? String(value.new) : 'empty'}</span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={field} className="text-xs p-1.5 bg-muted rounded flex flex-wrap gap-x-2">
                                      <span className="font-medium">{label}:</span>
                                      <span className="font-mono">{String(value)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>

          {/* Fixed Footer Actions */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => { isCreatingUser ? handleAddUserDialogClose() : handleUserEditDialogClose(); }}>
              Cancel
            </Button>
            <Button onClick={isCreatingUser ? handleAddUser : handleEditUser} disabled={submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid={isCreatingUser ? 'create-user-submit-btn' : 'edit-user-save-btn'}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isCreatingUser ? 'Create User' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Action Confirmation Dialog (delete user, remove-allocation, and other destructive actions) */}
      <Dialog open={!!pendingUserAction} onOpenChange={(open) => { if (!open) { setPendingUserAction(null); setDeleteConfirmText(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {pendingUserAction?.title}
            </DialogTitle>
            <DialogDescription>
              {pendingUserAction?.message}
            </DialogDescription>
          </DialogHeader>
          {pendingUserAction?.requireTyped && (
            <div className="space-y-3">
              <p className="text-sm">To confirm, type the following exactly:</p>
              <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">DELETE</code>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type confirmation text..."
                className="font-mono"
                autoFocus
                data-testid="user-delete-confirm-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => { setPendingUserAction(null); setDeleteConfirmText(''); }} data-testid="user-action-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" variant={pendingUserAction?.variant || "destructive"}
              disabled={pendingUserAction?.requireTyped && deleteConfirmText !== 'DELETE'}
              onClick={async () => { await pendingUserAction?.action(); setPendingUserAction(null); setDeleteConfirmText(''); }} data-testid="user-action-confirm-btn">
              {pendingUserAction?.confirmLabel || 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Input Change Confirmation Dialog (Role Change, Account Expiry) */}
      <Dialog open={!!pendingInputChange} onOpenChange={(open) => { if (!open) setPendingInputChange(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {pendingInputChange?.title}
            </DialogTitle>
            <DialogDescription>
              {pendingInputChange?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setPendingInputChange(null)}>
              Cancel
            </Button>
            <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={() => { applyInputChange(pendingInputChange.field, pendingInputChange.value); setPendingInputChange(null); }}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning Dialog (Edit) */}
      <Dialog open={showUserUnsavedWarning} onOpenChange={setShowUserUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowUserUnsavedWarning(false)}>
              Stay
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleUserEditDialogClose(true)}>
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning Dialog (Add) */}
      <Dialog open={showAddUserUnsavedWarning} onOpenChange={setShowAddUserUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowAddUserUnsavedWarning(false)}>
              Stay
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleAddUserDialogClose(true)}>
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status-switch wizard prompt (User edit dialog) */}
      <Dialog open={!!statusTogglePrompt} onOpenChange={(o) => { if (!o) setStatusTogglePrompt(null); }}>
        <DialogContent className="max-w-xl" data-testid="user-status-wizard-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Power className={`w-5 h-5 ${statusTogglePrompt?.next ? 'text-green-500' : 'text-red-500'}`} />
              {statusTogglePrompt?.next ? 'Reactivate' : 'Deactivate'} this user?
            </DialogTitle>
            <DialogDescription>
              The {statusTogglePrompt?.next ? 'Reactivation' : 'Deactivation'} Wizard walks you through the user's devices, allocations, sub-accounts and billing. Opening it will close this edit dialog. Alternatively, change the status flag only — remember to Save afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setStatusTogglePrompt(null)} data-testid="user-status-prompt-cancel">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => { handleInputChange('account_status', statusTogglePrompt.next ? 'active' : 'blocked'); setStatusTogglePrompt(null); }}
              data-testid="user-status-prompt-toggle-only"
            >
              Change status only
            </Button>
            <Button
              className={statusTogglePrompt?.next ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={() => {
                const next = statusTogglePrompt.next;
                const usr = selectedUser;
                setStatusTogglePrompt(null);
                handleUserEditDialogClose(true);
                if (next) setReactivateWizardUser(usr); else setDeactivateWizardUser(usr);
              }}
              data-testid="user-status-prompt-open-wizard"
            >
              Open {statusTogglePrompt?.next ? 'Reactivation' : 'Deactivation'} Wizard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Deactivation Wizard */}
      <UserDeactivationWizard
        open={!!deactivateWizardUser}
        onClose={() => setDeactivateWizardUser(null)}
        user={deactivateWizardUser}
        api={api}
        devices={allDevicesForWizard}
        allUsers={users}
        onOpenDeviceWizard={(device) => {
          setDeactivateWizardDevice(device);
        }}
        onComplete={() => { fetchUsers(); }}
      />

      {/* User Reactivation Wizard */}
      <UserReactivationWizard
        open={!!reactivateWizardUser}
        onClose={() => setReactivateWizardUser(null)}
        user={reactivateWizardUser}
        api={api}
        devices={allDevicesForWizard}
        allUsers={users}
        onOpenDeviceReactivationWizard={(device) => {
          setReactivateWizardDevice(device);
        }}
        onComplete={() => { fetchUsers(); }}
      />

      {/* Device Deactivation Wizard (opened from User wizard) */}
      <DeviceDeactivationWizard
        open={!!deactivateWizardDevice}
        onClose={() => setDeactivateWizardDevice(null)}
        device={deactivateWizardDevice}
        api={api}
        onComplete={() => {
          api.get('/admin/devices?limit=10000').then(res => setAllDevicesForWizard(res.data || [])).catch(() => {});
        }}
      />

      {/* Device Reactivation Wizard (opened from User wizard) */}
      <DeviceReactivationWizard
        open={!!reactivateWizardDevice}
        onClose={() => setReactivateWizardDevice(null)}
        device={reactivateWizardDevice}
        api={api}
        users={users}
        onComplete={() => {
          api.get('/admin/devices?limit=10000').then(res => setAllDevicesForWizard(res.data || [])).catch(() => {});
        }}
      />

      {/* Share Quick Login Link modal (Copy / Email / SMS) */}
      <ShareQuickLoginModal
        open={shareQuickLoginOpen}
        onOpenChange={setShareQuickLoginOpen}
        user={selectedUser}
        userId={selectedUser?.id}
        quickLoginUrl={(quickLoginToken || selectedUser?.quick_login_token) ? `${window.location.origin}/quick-login/${quickLoginToken || selectedUser?.quick_login_token}` : ''}
        api={api}
      />

      {/* Read-only Sub Accounts viewer (management stays in parent's Settings > Sub Accounts) */}
      <Dialog open={!!subAccountsViewer} onOpenChange={(o) => { if (!o) setSubAccountsViewer(null); }}>
        <DialogContent className="max-w-2xl" data-testid="sub-accounts-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Sub Accounts of {subAccountsViewer ? displayName(subAccountsViewer) : ''}
            </DialogTitle>
            <DialogDescription>
              Read-only view. Sub accounts are managed by the parent account in Settings &gt; Sub Accounts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(subAccountsByParent[subAccountsViewer?.id] || []).length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center" data-testid="sub-accounts-viewer-empty">No sub accounts found.</p>
            )}
            {(subAccountsByParent[subAccountsViewer?.id] || []).map((sub) => (
              <div
                key={sub.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-muted/20"
                data-testid={`sub-accounts-viewer-row-${sub.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{sub.full_name || sub.username || sub.email}</span>
                    <Badge variant={sub.is_active === false ? 'destructive' : 'outline'} className="text-[10px]">
                      {sub.is_active === false ? 'Blocked' : 'Active'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{sub.email}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {(sub.device_count ?? (sub.assigned_device_ids?.length || 0))} device{(sub.device_count ?? (sub.assigned_device_ids?.length || 0)) === 1 ? '' : 's'} assigned
                    <span className="mx-1">·</span>
                    <span
                      className={sub.last_login ? 'text-gray-700 dark:text-gray-300' : 'text-amber-600 dark:text-amber-400'}
                      title={sub.last_login ? format(new Date(sub.last_login), 'PPpp') : 'This sub account has never logged in'}
                      data-testid={`sub-accounts-viewer-last-login-${sub.id}`}
                    >
                      {sub.last_login
                        ? `Last login ${formatDistanceToNow(new Date(sub.last_login), { addSuffix: true })}`
                        : 'Never logged in'}
                    </span>
                  </p>
                </div>
                {sub.is_active !== false && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLoginAsUser(sub)}
                    title={`Login as ${sub.full_name || sub.email}`}
                    className="shrink-0"
                    data-testid={`sub-accounts-viewer-login-as-${sub.id}`}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Login As
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete confirmation — SUPER ADMIN ONLY */}
      <BannerBroadcastDialog
        open={bannerDialogOpen}
        onOpenChange={setBannerDialogOpen}
        api={api}
        selectedUserIds={selectedUserIds}
        formatDT={formatDT}
      />

      <Dialog open={bulkDeleteOpen} onOpenChange={(o) => { setBulkDeleteOpen(o); if (!o) setBulkDeleteText(''); }}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-users-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete {selectedUserIds.length} user(s)?
            </DialogTitle>
            <DialogDescription>
              This permanently deletes the selected user accounts (End Users also lose their Sub Accounts). Your own account and Super Admin accounts are automatically skipped. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="bulk-delete-users-input">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
            <Input
              id="bulk-delete-users-input"
              value={bulkDeleteText}
              onChange={e => setBulkDeleteText(e.target.value)}
              placeholder="DELETE"
              data-testid="bulk-delete-users-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} data-testid="bulk-delete-users-cancel">Cancel</Button>
            <Button
              variant="destructive"
              disabled={bulkDeleteText !== 'DELETE' || bulkDeleting}
              onClick={handleBulkDeleteUsers}
              data-testid="bulk-delete-users-confirm"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Import/Export Log */}
      <Dialog open={userIoLogOpen} onOpenChange={setUserIoLogOpen}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="user-io-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Import / Export Log
            </DialogTitle>
            <DialogDescription>
              Every user import and export — who ran it, row counts and undo status
            </DialogDescription>
          </DialogHeader>
          {userIoLogGrandTotal === 0 && !userIoLogsLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground" data-testid="user-io-log-empty">
              <History className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">No imports or exports recorded yet</p>
              <p className="text-xs">Run an import or export and it will appear here</p>
            </div>
          ) : (
            <>
            <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="user-io-log-filters">
              <Select value={userIoLogTypeFilter} onValueChange={setUserIoLogTypeFilter}>
                <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="user-io-log-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="import">Imports</SelectItem>
                  <SelectItem value="export">Exports</SelectItem>
                </SelectContent>
              </Select>
              <Select value={userIoLogPersonFilter} onValueChange={setUserIoLogPersonFilter}>
                <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="user-io-log-person-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All people</SelectItem>
                  {userIoLogPersons.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <DateRangePresetFilter
                value={userIoLogDateRange}
                onChange={setUserIoLogDateRange}
                testIdPrefix="user-io-log-date"
                resetSignal={userIoLogDateResetKey}
              />
              {(userIoLogTypeFilter !== 'all' || userIoLogPersonFilter !== 'all' || userIoLogDateRange.from || userIoLogDateRange.to) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => { setUserIoLogTypeFilter('all'); setUserIoLogPersonFilter('all'); setUserIoLogDateRange({ from: undefined, to: undefined }); setUserIoLogDateResetKey(k => k + 1); }}
                  data-testid="user-io-log-clear-filters"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto" data-testid="user-io-log-count">
                {userIoLogTotal} of {userIoLogGrandTotal} entr{userIoLogGrandTotal === 1 ? 'y' : 'ies'}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={userIoLogTotal === 0}
                    data-testid="user-io-log-export-menu"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportUserIoLogCsv} data-testid="user-io-log-export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportUserIoLogHtml} data-testid="user-io-log-export-html">HTML</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportUserIoLogPdf} data-testid="user-io-log-export-pdf">PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {userIoLogsLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : userIoLogTotal === 0 ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm" data-testid="user-io-log-no-match">
                No entries match the current filters
              </div>
            ) : (
            <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Details</TableHead>
                    <TableHead className="text-xs">File</TableHead>
                    <TableHead className="text-xs text-right">Rows</TableHead>
                    <TableHead className="text-xs">Result</TableHead>
                    <TableHead className="text-xs">Undo</TableHead>
                    <TableHead className="text-xs">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userIoLogs.map(l => {
                    const detail = l.kind === 'import'
                      ? (l.source === 'wizard' ? 'Import Wizard' : 'Quick CSV Import')
                      : 'Users Export';
                    return (
                      <TableRow key={l.id} data-testid={`user-io-log-row-${l.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{l.timestamp ? formatDT(l.timestamp) : '—'}</TableCell>
                        <TableCell>
                          <Badge className={l.kind === 'import'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'}>
                            {l.kind === 'import' ? 'Import' : 'Export'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{detail}{l.file_type ? ` (.${l.file_type})` : ''}</TableCell>
                        <TableCell className="text-xs max-w-[180px] truncate" title={l.file_name || ''}>{l.file_name || '—'}</TableCell>
                        <TableCell className="text-xs text-right">{l.row_count ?? 0}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {l.kind === 'import'
                            ? `${l.created ?? 0} created / ${l.updated ?? 0} updated / ${l.skipped ?? 0} skipped / ${l.failed ?? 0} failed`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {l.undo_status === 'undone'
                            ? <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">Undone ({l.undo_removed ?? 0} removed)</Badge>
                            : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{l.actor_name || l.actor_email || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
            )}
            <PaginationFooter
              page={userIoLogPage}
              setPage={setUserIoLogPage}
              totalItems={userIoLogTotal}
              itemsPerPage={userIoLogPageSize}
              setItemsPerPage={setUserIoLogPageSize}
              testIdPrefix="user-io-log"
              scrollOnChange={false}
              disabled={userIoLogsLoading}
              alwaysShow
            />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* User Import Wizard */}
          <UserImportWizard
            open={isImportWizardOpen}
            onOpenChange={setIsImportWizardOpen}
            users={users}
            allDevices={allDevices}
            allBeacons={allBeacons}
            featureCatalog={privilegesTree.flatMap(n => [
              { id: n.id, label: n.label },
              ...(n.children || []).map(c => ({ id: c.id, label: `${n.label} — ${c.label}` })),
            ])}
            api={api}
            getDateFormat={getDateFormat}
            onCompleted={(run) => { fetchUsers(); if (run?.id) setLastImportRun(run); }}
          />
    </div>
  );
};
